import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useContext,
} from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";
import {
  Container,
  Grid,
  Stack,
  Button,
  LoadingOverlay,
  Paper,
  Title,
  Center,
  Loader,
  Text,
  Group,
  Box,
  Accordion,
  Badge,
  Alert,
  Divider,
  Modal,
  Select,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconRefresh,
  IconCircleCheck,
  IconChevronDown,
  IconAlertTriangle,
  IconArrowLeft,
  IconScan,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type StainSelectionContext } from "../../utils/controlStainSelection";
import {
  allowsGrossingSpecimenIdentifierEdits,
  preservePersistedSpecimenIdentifiers,
} from "../../utils/grossingSpecimenIdentifiers";
import { useGrossingSpecimenIdentifierSave } from "../../hooks/useGrossingSpecimenIdentifierSave";
import SpecimenIdentifierWarningModal from "../../components/accessioning/SpecimenIdentifierWarningModal";
import GrossingSpecimenIdentifierEditor from "../../components/accessioning/GrossingSpecimenIdentifierEditor";
import { useAuth } from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useMissingFields } from "../../hooks/useMissingFields";
import { useFieldCompletion } from "../../hooks/useFieldCompletion";
import {
  useCreateDraftCase,
  useUpdateAccessioningCase,
  useAssignAccessioningPrefix,
  useSynchronizeCanonicalExternalCaseNumber,
  useCompleteAccessioning,
  useSpecimenMacrosForAccessioning,
  useBatchTemplatesForAccessioning,
} from "../../hooks/useWorkflows";
import {
  useDefaultFacility,
  useIcdCodes,
  useProcedures,
  useInsuranceProviders,
  useSourcesWithSubsites,
  usePathologists,
  useBillingScenarios,
  useAccessioningGrossCodes,
  useCollectionMethods,
} from "../../hooks/useReferenceData";
import {
  accessioningService,
  type AccessioningData,
  CanonicalExternalCaseNumberError,
  type CaseIdentityResult,
} from "../../services/workflows.service";
import {
  workListService,
  type ExpectedCase,
  type WorkListStainSubmission,
} from "../../services/workList.service";
import { api } from "../../lib";
import {
  useCase,
  useCaseSpecimens,
  useCaseFiles,
  useGenerateCassetteLabels,
  useGenerateStickerLabels,
} from "../../hooks/useCases";
import { useStickerPrinterStatus } from "../../hooks/useStickerPrinterStatus";
import { useAccessioningPanelOptions } from "../../hooks/useAccessioningPanels";
import {
  getStickerTemplateUpdateMessage,
  isStickerTemplateVersionAtLeast,
  MIN_DYMO_SLIDE_TEMPLATE_VERSION,
  stickerLabelPrinterService,
  type StickerLabelData,
} from "../../services/stickerLabelPrinter.service";
import { buildRequisitionStickerLabel } from "../../services/requisitionStickerLabelBuilder";
import { workstationPreferenceService } from "../../services/workstationPreference.service";
import { bridgeService } from "../../services/bridgeService";
import { accessioningScanSessionService } from "../../services/accessioningScanSession.service";
import { assignPathologist, casesService } from "../../services";
import { validateAddress } from "../../services/addressValidation.service";
import {
  lookupPrefix,
  listPrefixes,
  getPrefixById,
  type PrefixLookupResult,
} from "../../services/prefixService";
import type {
  UploadedFile,
  ExtractionJobStatus,
  ExtractionStepState,
  PriorityTrigger,
} from "../../types";

// Components
import {
  DocumentUpload,
  ExtractionProgress,
  PatientInfoForm,
  CaseInfoForm,
  ConsultationForm,
  OriginatingFacilityForm,
  InsuranceForm,
  SpecimenList,
  CaseSummary,
  CaseFieldsCard,
  DerivedWorkflowCard,
  CompletionWarningModal,
  PrefixOverrideWarningModal,
  PassthroughPrefixWorkflowWarningModal,
  BlocksAndStainsPreview,
  ErrorsCard,
  NewCaseModal,
  AccessioningPrintActions,
  CaseNumberCorrectionModal,
  type StainInfo,
  type ManualStainsState,
  type StainRemovalTarget,
  areStainInfoOccurrencesEqual,
} from "../../components/accessioning";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import { CaseCommentsSection } from "../../components/accessioning/CaseCommentsSection";
import {
  CassettePrinterStatusPanel,
  DocumentScannerStatusPanel,
} from "../../components/workflows";
import { StickerPrinterStatusPanel } from "../../components/sticker";
import { documentScanningService } from "../../services/documentScanning.service";
import { useCassettePrint } from "../../hooks/useCassettePrint";
import { useSalesRepsAdmin } from "../../hooks/admin/useSalesReps";
import { parseDateOnly, type DateOnlyString } from "../../utils/dateOnly";
import {
  deriveCaseType,
  caseTypeLabel,
  type CaseType,
} from "../../utils/deriveCaseType";
import { resolveAccessioningBlockCount } from "../../utils/grossingOnlyPolicy";
import { normalizeRuleOuts } from "../../utils/helpers";
import {
  readExtractionSignalFlags,
  type ExtractionSignalFlags,
} from "../../utils/extractionSignals";
import { parseCaseNumber } from "../../utils/caseNumberSequence";
import { useCompletionWarnings } from "../../hooks/useCompletionWarnings";
import { getPrefixOverrideWarningDetails } from "../../utils/prefixOverrideWarning";
import { getPassthroughPrefixWorkflowWarningDetails } from "../../utils/passthroughPrefixWorkflowWarning";
import {
  MISSING_PATIENT_LABEL_NAME_ERROR,
  resolvePatientLabelName,
} from "../../utils/patientLabelName";
import {
  applyPassThroughMaterialDefault,
  applyPassThroughMaterialDefaultsToSpecimens,
  getDefaultMaterialForNewSpecimen,
} from "../../utils/passThroughMaterialDefaults";
import { uploadAccessioningDocument } from "../../utils/accessioningUpload";
import { getApiErrorStatus } from "../../utils/apiError";
import {
  createExtractionStatusPollingState,
  EXTRACTION_STATUS_UNAVAILABLE_MESSAGE,
  getFailedExtractionStepKeys,
  isCurrentExtractionStatusJob,
  recordExtractionStatusFailure,
  recordExtractionStatusSuccess,
  resumeExtractionStatusPolling,
} from "../../utils/extractionStatusPolling";
import {
  countCompletedDymoSlides,
  countPrintableDymoLabels,
  countResolvedDymoSlides,
  deriveEffectiveDymoSlideCompletions,
} from "../../utils/accessioningDymoEligibility";
import {
  buildResolvedStainSelections,
  buildStainSelectionId,
  getStainSelectionId,
} from "../../utils/resolvedStainSelections";
import { createUuid } from "../../utils/createUuid";
import type {
  AccessioningPanelOption,
  AccessioningPanelSelection,
} from "../../services/accessioningPanels.service";
import {
  deriveNoPatientBillingState,
  shouldCreateMissingInsuranceFollowUp,
} from "../../utils/noPatientBilling";
import { NoPatientBillingCheckbox } from "../../components/accessioning/NoPatientBillingCheckbox";
import {
  getAccessioningStickerDateReceived,
  getNewAccessioningDateReceived,
  hydratePersistedSpecializedCaseFields,
  normalizeAccessioningDateOnly,
} from "./accessioningCaseHydration";
import {
  getAccessioningDisplayCaseNumber,
  isDisplayedCaseNumberBackfill,
  requiresCanonicalExternalCaseNumberBeforeDraftPrinting,
  shouldRequestPrefixAssignment,
} from "../../utils/accessioningCaseNumber";
import { createSerialLatestExecutor } from "../../utils/serialLatestExecutor";
import { createLatestRequestGuard } from "../../utils/latestRequestGuard";
import { buildClientIdentityResolutionInput } from "../../utils/clientIdentityResolutionInput";
import {
  getSpecimenIdentifierErrors,
  normalizeSpecimenIdentifier,
} from "../../utils/specimenIdentifiers";
import {
  advanceSpecimenPopulationSnapshot,
  areSpecimensUnchangedSincePopulation,
  assignSpecimenUiKeys,
  createSpecimenPopulationSnapshot,
  createSpecimenUiKey,
  reindexSpecimenFieldRecord,
  reindexSpecimenRecord,
  type SpecimenIndexMap,
} from "../../utils/specimenState";
import { resetAccessioningFormForNewCase } from "../../utils/accessioningFormReset";
import { isPracticeLabRequired } from "../../utils/accessioningClientRequirements";
import {
  filterActiveUnresolvedWorkListStains,
  renameMappedWorkListStains,
} from "../../utils/workListAccessioning";
// ErrorReportModal is now imported inside ErrorsCard component

const MIN_REQUISITION_STICKER_TEMPLATE_VERSION = "1.1";

type PrefixOverrideAction =
  | "complete"
  | "saveAndGoBack"
  | "saveAndNew"
  | "saveGrossingIdentifiers";

type WorkListStainOverride = WorkListStainSubmission;

interface AccessioningAutoSaveRequest {
  caseId: string;
  serializedValues: string;
  data: AccessioningData;
}

type AccessioningAutoSaveResult = Awaited<
  ReturnType<typeof accessioningService.updateCase>
>;

function findSpecimenIndex(specimens: any[], identifier: string): number {
  const normalized = normalizeSpecimenIdentifier(identifier).toLowerCase();
  return specimens.findIndex(
    (specimen) =>
      normalizeSpecimenIdentifier(specimen.number).toLowerCase() === normalized
  );
}

function remapWorkListStains(
  stains: WorkListStainOverride[],
  previousSpecimens: any[],
  nextSpecimens: any[],
  oldIndexByNewIndex: SpecimenIndexMap
): WorkListStainOverride[] {
  return stains.flatMap((stain) => {
    const expectedIdentifier = stain.expectedSpecimenNumber;
    const oldIndex = expectedIdentifier
      ? previousSpecimens.findIndex(
          (specimen) =>
            normalizeSpecimenIdentifier(
              specimen.expectedSpecimenNumber
            ).toLowerCase() ===
            normalizeSpecimenIdentifier(expectedIdentifier).toLowerCase()
        )
      : findSpecimenIndex(previousSpecimens, stain.specimenNumber);
    const newIndex = oldIndexByNewIndex.findIndex(
      (candidate) => candidate === oldIndex
    );
    return newIndex < 0
      ? []
      : [{ ...stain, specimenNumber: nextSpecimens[newIndex].number }];
  });
}

function remapPriorityTriggers(
  triggers: PriorityTrigger[],
  previousSpecimens: any[],
  nextSpecimens: any[],
  oldIndexByNewIndex: SpecimenIndexMap
): PriorityTrigger[] {
  return triggers.flatMap((trigger) => {
    if (trigger.specimenId === "CASE") return [trigger];
    const oldIndex = trigger.specimenUiKey
      ? previousSpecimens.findIndex(
          (specimen) => specimen.uiKey === trigger.specimenUiKey
        )
      : findSpecimenIndex(previousSpecimens, trigger.specimenId);
    const newIndex = oldIndexByNewIndex.findIndex(
      (candidate) => candidate === oldIndex
    );
    return newIndex < 0
      ? []
      : [
          {
            ...trigger,
            specimenId: nextSpecimens[newIndex].number,
            specimenUiKey: nextSpecimens[newIndex].uiKey,
          },
        ];
  });
}

// Utility to filter out options without valid value property
// This prevents Mantine Select validation errors during navigation/hydration
const sanitizeOptions = <T extends { value: string }>(
  options: T[] | undefined | null
): T[] => {
  if (!Array.isArray(options)) return [];
  const valid = options.filter(
    (opt): opt is T =>
      opt != null &&
      typeof opt === "object" &&
      "value" in opt &&
      opt.value != null &&
      opt.value !== ""
  );
  // Deduplicate by value to guard against duplicate DB rows
  return Array.from(new Map(valid.map((o) => [o.value, o])).values());
};

export default function AccessioningPage() {
  // Auth
  const { user } = useAuth();
  const canPrintSticker = useHasPermission("print.sticker");

  // URL params - check if editing existing case
  const { caseId: editingCaseId } = useParams<{ caseId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!editingCaseId;

  // Fetch existing case data when editing
  const { data: existingCase, isLoading: existingCaseLoading } = useCase(
    editingCaseId || undefined
  );

  // Limited-edit mode: post-grossing edits allow only patient info, insurance,
  // rush/priority, and sales rep. Specimens, documents, and completion are locked.
  const isLimitedEdit = useMemo(() => {
    if (!isEditMode) return false;
    if (searchParams.get("limitedEdit") === "1") return true;
    if (!existingCase?.currentStatus?.name) return false;

    // Post-grossing statuses are restricted to limited edits
    const fullEditStatuses = ["Draft", "Pending Grossing", "Grossing"];
    return !fullEditStatuses.includes(existingCase.currentStatus.name);
  }, [isEditMode, searchParams, existingCase?.currentStatus?.name]);

  // Past Draft the saved specimens have blocks and slides behind them, so the backend
  // reconciles them in place instead of recreating the set. Removing or re-lettering
  // one here would orphan that material, so those actions are locked in the UI too.
  const lockPersistedSpecimens = useMemo(() => {
    if (!isEditMode) return false;
    const statusName = existingCase?.currentStatus?.name;
    return !!statusName && statusName !== "Draft";
  }, [isEditMode, existingCase?.currentStatus?.name]);

  const allowGrossingIdentifierEdits = useMemo(
    () =>
      isEditMode &&
      allowsGrossingSpecimenIdentifierEdits(existingCase?.currentStatus?.name),
    [isEditMode, existingCase?.currentStatus?.name]
  );

  const { data: existingSpecimens = [], isLoading: existingSpecimensLoading } =
    useCaseSpecimens(editingCaseId || undefined);
  const { data: existingFiles = [], isLoading: existingFilesLoading } =
    useCaseFiles(editingCaseId || undefined);

  // State
  // Case creation state - controls UI visibility
  const [caseCreationState, setCaseCreationState] = useState<
    "loading" | "error" | "ready"
  >("loading");
  const [caseCreationError, setCaseCreationError] = useState<string | null>(
    null
  );

  // Case state - created immediately on mount
  const [caseId, setCaseId] = useState<string | null>(null);
  const [currentCaseNumber, setCurrentCaseNumber] = useState<string | null>(
    null
  );
  const [backfillCaseNumber, setBackfillCaseNumber] = useState<string | null>(
    null
  );
  const applyCaseNumberContext = useCallback(
    (
      caseNumber: string | null,
      allocationSource?: CaseIdentityResult["allocationSource"]
    ) => {
      setCurrentCaseNumber(caseNumber);
      setBackfillCaseNumber(
        allocationSource === "reusable_pool" ? caseNumber : null
      );
    },
    []
  );
  const [
    caseNumberModalOpened,
    { open: openCaseNumberModal, close: closeCaseNumberModal },
  ] = useDisclosure(false);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const uploadedFilesRef = useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;

  const [scannerReady, setScannerReady] = useState(false);
  const [scanning, setScanning] = useState(false);

  const handleScanDocument = useCallback(async () => {
    const currentFiles = uploadedFilesRef.current;
    const workstationId =
      workstationPreferenceService.getSelectedWorkstationId();
    const deviceId = documentScanningService.selectedDeviceId;
    if (!scannerReady || scanning || !caseId || !workstationId || !deviceId)
      return;
    if (currentFiles.length >= 5) {
      notifications.show({
        title: "Document limit reached",
        message: "Remove a document before scanning another one.",
        color: "yellow",
      });
      return;
    }

    setScanning(true);
    let sessionId: string | null = null;
    let scannedFile: File | null = null;
    try {
      const workstation = (await bridgeService.getWorkstation(workstationId))
        .data;
      const selectedDevice = workstation.devices?.find(
        (device) => device.id === deviceId
      );
      const waitForEventOnly =
        selectedDevice?.connectionType === "folder_watch";
      const session = await accessioningScanSessionService.start({
        caseId,
        workstationId,
        deviceId,
        clientRequestId: createUuid(),
      });
      sessionId = session.id;

      const result = await documentScanningService.scanDocument({
        waitForEventOnly,
      });
      if (!result.success || !result.file) {
        await accessioningScanSessionService.cancel(session.id);
        sessionId = null;
        notifications.show({
          title: "Scan failed",
          message: "The scanner did not return a document. Please try again.",
          color: "red",
        });
        return;
      }

      scannedFile = result.file;
      const pending: UploadedFile = {
        file: scannedFile,
        s3Key: "",
        uploadProgress: 0,
      };
      setUploadedFiles((previous) => [...previous, pending]);
      const key = await uploadAccessioningDocument(
        scannedFile,
        (fileName, contentType) =>
          accessioningScanSessionService.getPresignedUrl(
            session.id,
            fileName,
            contentType
          )
      );
      await accessioningScanSessionService.attach(session.id, scannedFile);
      sessionId = null;
      setUploadedFiles((previous) =>
        previous.map((file) =>
          file.file === scannedFile
            ? { ...file, s3Key: key, uploadProgress: 100 }
            : file
        )
      );
      notifications.show({
        title: "Scanned file attached",
        message: "The scan was attached only to this case.",
        color: "green",
      });
    } catch (error) {
      if (sessionId) {
        await accessioningScanSessionService
          .cancel(sessionId)
          .catch(() => undefined);
      }
      if (scannedFile) {
        setUploadedFiles((previous) =>
          previous.filter((file) => file.file !== scannedFile)
        );
      }
      const scannerReserved = getApiErrorStatus(error) === 409;
      notifications.show({
        title: scannerReserved ? "Scanner already in use" : "Scan failed",
        message: scannerReserved
          ? "Another accessioning case is waiting for this scanner. Finish or cancel that scan first."
          : "The scan could not be attached. Check the scanner and connection, then try again.",
        color: "red",
      });
    } finally {
      setScanning(false);
    }
  }, [caseId, scannerReady, scanning]);

  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [extractionJobId, setExtractionJobId] = useState<string | null>(null);
  const [extractionSteps, setExtractionSteps] = useState<
    Record<string, ExtractionStepState>
  >({});
  const [extractionCurrentPhase, setExtractionCurrentPhase] = useState<
    1 | 2 | 3 | undefined
  >(undefined);
  const [rerunningSteps, setRerunningSteps] = useState<Set<string>>(new Set());
  const lastAppliedSteps = useRef<Set<string>>(new Set());
  const [crossDocMismatches, setCrossDocMismatches] = useState<
    Array<{
      field: string;
      requisitionValue: string | null;
      demographicsValue: string | null;
      message: string;
    }>
  >([]);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const extractionPollInFlightJobRef = useRef<string | null>(null);
  const extractionRequestGuardRef = useRef(createLatestRequestGuard());
  const extractionPollingStateRef = useRef(
    createExtractionStatusPollingState()
  );
  const [isExtractionStatusUnavailable, setIsExtractionStatusUnavailable] =
    useState(false);
  const [certainties, setCertainties] = useState<Record<string, number>>({});
  const [highPriorityTriggers, setHighPriorityTriggers] = useState<
    PriorityTrigger[]
  >([]);
  const [unmatchedExtractions, setUnmatchedExtractions] = useState<
    Record<string, string>
  >({});
  const [specimenSlideCompletions, setSpecimenSlideCompletions] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [assignmentReason, setAssignmentReason] = useState<string>("");
  // Committed findings per specimen index — updated on blur to drive stain preview
  const [committedFindings, setCommittedFindings] = useState<
    Record<number, string>
  >({});
  // Auto-resolved prefix state
  const [prefixLookupResult, setPrefixLookupResult] =
    useState<PrefixLookupResult | null>(null);
  // Manual prefix override — staff can override the auto-resolved prefix
  const [prefixOverride, setPrefixOverride] = useState<string | null>(null);
  // Manual override prefix data (fetched when override is selected)
  const [prefixOverrideResult, setPrefixOverrideResult] =
    useState<PrefixLookupResult | null>(null);

  const effectivePrefixData = useMemo(() => {
    if (
      prefixOverride &&
      prefixOverrideResult &&
      prefixOverrideResult.prefixCode === prefixOverride
    ) {
      return prefixOverrideResult;
    }
    return prefixLookupResult;
  }, [prefixOverride, prefixOverrideResult, prefixLookupResult]);
  const [isResolvingPrefix, setIsResolvingPrefix] = useState(false);
  const prefixLookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Manual billing selection for practices that require it (e.g. AG)
  const [manualBillingScenarioId, setManualBillingScenarioId] = useState<
    string | null
  >(null);
  // Prefix-driven billing scenario selection (Scenario 4/5: multiple options from prefix)
  const [prefixSelectedBillingScenarioId, setPrefixSelectedBillingScenarioId] =
    useState<string | null>(null);
  // Requisition signal flags — pre-populated by AI extraction, overridable by staff
  const [isCytology, setIsCytology] = useState(false);
  const [isResearch, setIsResearch] = useState(false);
  const [isConsultation, setIsConsultation] = useState(false);
  const [isTcOnly, setIsTcOnly] = useState(false);
  const [isGrossingOnly, setIsGrossingOnly] = useState(false);

  // Ref to prevent multiple initialization attempts
  const isInitializingRef = useRef(false);
  // Ref to track if we just created this case (to skip "editing" notification)
  const justCreatedCaseRef = useRef(false);

  // Accordion state - most sections open by default, secondary insurance collapsed
  const [openedSections, setOpenedSections] = useState<string[]>([
    "patient",
    "primaryInsurance",
    "caseInfo",
    "specimens",
    "consultation",
    "originating",
  ]);

  // Error reporting is now handled inside ErrorsCard component

  // New case modal state
  const [newCaseModalOpened, setNewCaseModalOpened] = useState(false);

  // Cassette labels
  const generateLabelsMutation = useGenerateCassetteLabels();
  const { printLabels } = useCassettePrint();

  // Sticker print state — one-click, no confirmation modal (parity with cassette)
  const [isPrintingStickers, setIsPrintingStickers] = useState(false);
  const [isPrintingRequisitionLabel, setIsPrintingRequisitionLabel] =
    useState(false);
  const { data: stickerPrinterReadiness } = useStickerPrinterStatus();
  const { data: stickerCaseData } = useCase(caseId || undefined);
  const generateStickerLabelsMutation = useGenerateStickerLabels();

  // Left sidebar scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Manual stains state - tracks added/removed stains per specimen index
  // { specimenIndex: { added: [{id, code, description}], removed: [stainId] } }
  const [manualStains, setManualStains] = useState<
    Record<number, ManualStainsState>
  >({});
  const [panelSelections, setPanelSelections] = useState<
    Record<number, AccessioningPanelSelection[]>
  >({});

  // Work list match state
  const [matchedExpectedCase, setMatchedExpectedCase] =
    useState<ExpectedCase | null>(null);
  const [workListStainOverrides, setWorkListStainOverrides] = useState<
    WorkListStainOverride[]
  >([]);

  // Refs for work list lookup debounce (effect placed after form declaration)
  const workListLookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastLookedUpExtCaseRef = useRef<string>("");
  const workListLookupGuardRef = useRef(createLatestRequestGuard());

  // Track whether to create practice mappings on completion
  const [shouldCreateMappings, setShouldCreateMappings] = useState(false);

  // Auto-save state (status is set internally but not displayed per user request)
  const [, setAutoSaveStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle"
  );
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValuesRef = useRef<string>("");
  const autoSaveExecutorRef = useRef(
    createSerialLatestExecutor<
      AccessioningAutoSaveRequest,
      AccessioningAutoSaveResult
    >()
  );

  const clearAutoSaveTimeout = useCallback(() => {
    if (!autoSaveTimeoutRef.current) return;
    clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = null;
  }, []);

  // Used by CaseInfoForm to track auto-create practice-physician link state
  const handleAutoCreatePracticePhysicianChange = useCallback(
    (value: boolean) => {
      setShouldCreateMappings(value);
    },
    []
  );

  // Handle slide completion changes in the blocks/slides preview
  const handleSlideCompletionChange = useCallback(
    (specimenIndex: number, stainId: string, isCompleted: boolean) => {
      setSpecimenSlideCompletions((prev) => ({
        ...prev,
        [specimenIndex]: { ...prev[specimenIndex], [stainId]: isCompleted },
      }));
    },
    []
  );

  // Lift resolved stains from BlocksAndStainsPreview so we can mark them all
  const [resolvedStains, setResolvedStains] = useState<
    Record<number, StainInfo[]>
  >({});

  const handleResolvedStainsChange = useCallback(
    (specimenIndex: number, stains: StainInfo[]) => {
      setResolvedStains((previous) => {
        if (areStainInfoOccurrencesEqual(previous[specimenIndex], stains)) {
          return previous;
        }
        return { ...previous, [specimenIndex]: stains };
      });
      setSpecimenSlideCompletions((previous) => {
        const specimenCompletions = previous[specimenIndex] || {};
        const migrated = { ...specimenCompletions };
        let changed = false;
        stains.forEach((stain) => {
          if (!stain.selectionId || migrated[stain.selectionId] !== undefined)
            return;
          if (migrated[stain.id] === undefined) return;
          migrated[stain.selectionId] = migrated[stain.id];
          changed = true;
        });
        return changed ? { ...previous, [specimenIndex]: migrated } : previous;
      });
    },
    []
  );

  const handleMarkAllReceivedCompleted = useCallback(() => {
    setSpecimenSlideCompletions((prev) => {
      const next = { ...prev };
      Object.entries(resolvedStains).forEach(([specimenIndexStr, stains]) => {
        const specimenIndex = parseInt(specimenIndexStr, 10);
        next[specimenIndex] = { ...next[specimenIndex] };
        stains.forEach((stain) => {
          next[specimenIndex][getStainSelectionId(stain)] = true;
        });
      });
      return next;
    });
  }, [resolvedStains]);

  const handleClearAllReceivedCompleted = useCallback(() => {
    setSpecimenSlideCompletions((prev) => {
      const next = { ...prev };
      Object.entries(resolvedStains).forEach(([specimenIndexStr, stains]) => {
        const specimenIndex = parseInt(specimenIndexStr, 10);
        next[specimenIndex] = { ...next[specimenIndex] };
        stains.forEach((stain) => {
          next[specimenIndex][getStainSelectionId(stain)] = false;
        });
      });
      return next;
    });
  }, [resolvedStains]);

  // Handle adding a stain manually to a specimen
  const handleAddStain = useCallback(
    (
      specimenIndex: number,
      stain: {
        id: string;
        selectionId?: string;
        code: string;
        description: string;
        isSystemGenerated?: boolean;
        isPersisted?: boolean;
      }
    ) => {
      const occurrence = {
        ...stain,
        selectionId: stain.selectionId ?? createUuid(),
      };
      setManualStains((prev) => {
        const current = prev[specimenIndex] || { added: [], removed: [] };
        if (current.removed.includes(stain.id)) {
          return {
            ...prev,
            [specimenIndex]: {
              ...current,
              removed: current.removed.filter((id) => id !== stain.id),
            },
          };
        }
        return {
          ...prev,
          [specimenIndex]: {
            ...current,
            added: [...current.added, occurrence],
          },
        };
      });
    },
    []
  );

  // Handle removing a stain from a specimen
  const handleRemoveStain = useCallback(
    (specimenIndex: number, target: StainRemovalTarget) => {
      setManualStains((prev) => {
        const current = prev[specimenIndex] || { added: [], removed: [] };
        const isManualOccurrence = current.added.some(
          (stain) => stain.selectionId === target.selectionId
        );
        if (isManualOccurrence) {
          return {
            ...prev,
            [specimenIndex]: {
              ...current,
              added: current.added.filter(
                (stain) => stain.selectionId !== target.selectionId
              ),
            },
          };
        }
        if (current.removed.includes(target.stainId)) return prev;
        return {
          ...prev,
          [specimenIndex]: {
            ...current,
            removed: [...current.removed, target.stainId],
          },
        };
      });
      setSpecimenSlideCompletions((previous) => {
        const specimenCompletions = previous[specimenIndex];
        if (!specimenCompletions) return previous;
        const nextSpecimenCompletions = { ...specimenCompletions };
        delete nextSpecimenCompletions[target.selectionId];
        return {
          ...previous,
          [specimenIndex]: nextSpecimenCompletions,
        };
      });
    },
    []
  );

  const handleAddPanelSelection = useCallback(
    (specimenIndex: number, option: AccessioningPanelOption) => {
      const selection: AccessioningPanelSelection = {
        selectionKey: createUuid(),
        optionType: option.optionType,
        optionId: option.id,
        panelDefinitionId: option.panelDefinitionId,
        panelDefinitionVersionId: option.panelDefinitionVersionId,
      };
      setPanelSelections((previous) => ({
        ...previous,
        [specimenIndex]: [...(previous[specimenIndex] || []), selection],
      }));
    },
    []
  );

  const handleRemovePanelSelection = useCallback(
    (specimenIndex: number, selectionKey: string) => {
      setPanelSelections((previous) => ({
        ...previous,
        [specimenIndex]: (previous[specimenIndex] || []).filter(
          (selection) => selection.selectionKey !== selectionKey
        ),
      }));
    },
    []
  );

  // Mutations - extract mutateAsync which is stable
  const createDraftCaseMutation = useCreateDraftCase();
  const { mutateAsync: createDraftCase } = createDraftCaseMutation;
  const assignPrefixMutation = useAssignAccessioningPrefix();
  const { mutateAsync: assignPrefix } = assignPrefixMutation;
  const canonicalExternalMutation = useSynchronizeCanonicalExternalCaseNumber();
  const [canonicalExternalClaimState, setCanonicalExternalClaimState] =
    useState<"idle" | "claiming" | "verified" | "conflict">("idle");
  const [isAssigningPrefix, setIsAssigningPrefix] = useState(false);
  const [isCaseIdentityUnverified, setIsCaseIdentityUnverified] =
    useState(false);
  const isCaseNavigationBlocked = useMemo(
    () =>
      isAssigningPrefix ||
      isCaseIdentityUnverified ||
      canonicalExternalMutation.isPending,
    [
      canonicalExternalMutation.isPending,
      isAssigningPrefix,
      isCaseIdentityUnverified,
    ]
  );
  const isCaseNumberIdentityBlocked = useMemo(
    () => isCaseNavigationBlocked || canonicalExternalClaimState === "conflict",
    [canonicalExternalClaimState, isCaseNavigationBlocked]
  );
  const assignedPrefixRef = useRef<string | null>(null);
  const requestedPrefixRef = useRef<string | null>(null);
  const prefixLookupGuardRef = useRef(createLatestRequestGuard());
  const prefixAssignmentExecutorRef = useRef(
    createSerialLatestExecutor<string, CaseIdentityResult>()
  );

  const handleUpdateSuccess = useCallback(
    (returned: any) => {
      if (returned?.caseNumber && returned.caseNumber !== currentCaseNumber) {
        applyCaseNumberContext(returned.caseNumber);
      }
    },
    [applyCaseNumberContext, currentCaseNumber]
  );

  const updateCaseMutation = useUpdateAccessioningCase();
  const { mutateAsync: updateAccessioningCase } = updateCaseMutation;

  const performAutoSaveRequest = useCallback(
    (request: AccessioningAutoSaveRequest) =>
      updateAccessioningCase({
        caseId: request.caseId,
        data: request.data,
      }),
    [updateAccessioningCase]
  );

  const markAutoSaveSucceeded = useCallback((serializedValues: string) => {
    lastSavedValuesRef.current = serializedValues;
    setAutoSaveStatus("saved");
    setTimeout(() => setAutoSaveStatus("idle"), 3000);
  }, []);

  const markAutoSaveFailed = useCallback(() => {
    setAutoSaveStatus("error");
    setTimeout(() => setAutoSaveStatus("idle"), 5000);
  }, []);

  const queueAutoSave = useCallback(
    (request: AccessioningAutoSaveRequest) => {
      autoSaveExecutorRef.current.execute(request, performAutoSaveRequest, {
        onLatestSuccess: () => markAutoSaveSucceeded(request.serializedValues),
        onLatestError: markAutoSaveFailed,
        onLatestSettled: () => undefined,
      });
    },
    [markAutoSaveFailed, markAutoSaveSucceeded, performAutoSaveRequest]
  );

  const prepareForExplicitSave = useCallback(async () => {
    clearAutoSaveTimeout();
    autoSaveExecutorRef.current.invalidate();
    await autoSaveExecutorRef.current.whenIdle();
    setAutoSaveStatus("idle");
  }, [clearAutoSaveTimeout]);

  const completeMutation = useCompleteAccessioning({
    onSuccess: handleUpdateSuccess,
  });

  // Data fetching hooks
  const { data: defaultFacility } = useDefaultFacility();
  const { data: icdCodes = [] } = useIcdCodes();
  const { data: procedures = [] } = useProcedures();
  const { data: insuranceProviders = [] } = useInsuranceProviders();
  const { data: sourcesData } = useSourcesWithSubsites();
  const { data: pathologists = [] } = usePathologists();
  const { data: allBillingScenarios = [] } = useBillingScenarios();
  const { data: grossCodes = [] } = useAccessioningGrossCodes();
  const { data: salesReps = [] } = useSalesRepsAdmin();
  const { data: collectionMethods = [] } = useCollectionMethods();
  const { data: specimenMacros = [] } = useSpecimenMacrosForAccessioning();
  const { data: batchTemplatesData = [] } = useBatchTemplatesForAccessioning();
  const stainSelectionContext = useMemo<StainSelectionContext>(
    () =>
      (prefixOverride || prefixLookupResult?.prefixCode) === "QC" &&
      !existingCase?.parentCaseId &&
      (!isEditMode ||
        (existingCase &&
          ["Draft", "Pending Grossing", "Grossing"].includes(
            existingCase.currentStatus?.name || ""
          )))
        ? "qc-accessioning"
        : "ordinary",
    [prefixOverride, prefixLookupResult?.prefixCode, existingCase, isEditMode]
  );
  const { data: panelOptions = [], isLoading: panelsLoading } =
    useAccessioningPanelOptions();

  // Convert sales reps to select options for CaseFieldsCard
  const salesRepOptions = useMemo(
    () =>
      salesReps.map((rep) => ({
        value: rep.id,
        label: rep.name,
      })),
    [salesReps]
  );
  // Fetch all active prefixes for the prefix override select
  const { data: allPrefixesData } = useQuery({
    queryKey: ["prefixes-active"],
    queryFn: () => listPrefixes({ all: true }),
    staleTime: 5 * 60 * 1000,
  });
  const prefixSelectOptions = useMemo(
    () =>
      (allPrefixesData?.data ?? [])
        .filter((p) => p.isActive)
        .map((p) => ({ value: p.code, label: `${p.code}`, color: p.color })),
    [allPrefixesData]
  );

  // Fetch error types for auto-creating non-blocking error reports (e.g., missing insurance)
  const { data: errorTypes = [] } = useQuery({
    queryKey: ["errors"],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          error: string;
          description: string;
          category: string;
        }>
      >("/admin/errors"),
  });

  // Bridge ref: form is defined before the value-change handler, so we use a ref
  // to call the handler from onValuesChange without a stale closure.
  const onFormValuesChangeRef = useRef<
    ((values: any, prev: any) => void) | null
  >(null);

  // Refs for unload/unmount flush - defined before form to satisfy no-use-before-define
  const formValuesRef = useRef<any>(null);
  // Separates real edits from hydration and UI identity changes before applying Step J.
  const specimenPopulationSnapshotRef = useRef("");

  // Form
  const form = useForm({
    initialValues: {
      patient: {
        firstName: "",
        lastName: "",
        dateOfBirth: null as DateOnlyString | null,
        sex: "" as "M" | "F" | "Other" | "",
        medicalRecordNumber: "",
        mobilePhone: "",
        homePhone: "",
        workPhone: "",
        email: "",
        address: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
        },
      },
      case: {
        facilityId: "",
        accountId: "",
        submittingLabId: "",
        locationId: "",
        physicianId: "",
        procedureId: "",
        procedureName: "",
        externalCaseNumber: "",
        ruleOuts: "",
        dateReceived: getNewAccessioningDateReceived(),
        dateCollected: null as DateOnlyString | null,
        clinicalHistoryCodes: [] as string[],
        clinicalHistoryNotes: "",
        indications: "",
        impressions: "",
        pathologistId: "",
        billingScenarioId: null,
        noPatientBillingAndInsuranceInfoRequired: false,
        accessioningType: "",
        consultationReason: "",
        consultationOriginalDiagnosis: "",
        consultationClinicalContext: "",
        requestingPathologist: "",
        originatingPracticeId: "",
        originatingPhysicianId: "",
        originatingSurgeryCenterId: "",
        // Rush flag - physical RUSH stamp detection (expedited processing)
        isRush: false,
        rushReason: "",
        // High Priority flag - clinical keyword detection (mass, tumor, cancer)
        isHighPriority: false,
        highPriorityReason: "",
        // Sales Rep tracking - case came through a sales representative
        isThroughSalesRep: false,
        salesRepId: "",
        // TC shipping destination - external pathologist and address
        shippingPathologist: "",
        shippingStreet: "",
        shippingCity: "",
        shippingState: "",
        shippingZipCode: "",
        primaryInsurance: {
          insuranceProvider: "",
          planType: "",
          policyNumber: "",
          authorizationNumber: "",
          groupNumber: "",
          groupName: "",
          patientRelationToInsured: "",
          address: { address: "", city: "", state: "", zipCode: "" },
        },
        secondaryInsurance: {
          insuranceProvider: "",
          planType: "",
          policyNumber: "",
          authorizationNumber: "",
          groupNumber: "",
          groupName: "",
          patientRelationToInsured: "",
          address: { address: "", city: "", state: "", zipCode: "" },
        },
        specimens: [
          {
            uiKey: createSpecimenUiKey(),
            number: "A",
            sourceId: "",
            subsiteId: null,
            collectionMethod: "",
            material: "",
            findings: "",
            comments: "",
            extractedStainCodes: "",
            matchedStainIds: [],
            extractedGrossCodePrefix: "",
            extractedBlockIdentifiers: "",
            extractedDimensions: "",
            gross: "",
            grossCodeId: null,
            grossTemplate: "",
            blockCount: 1,
          },
        ],
      },
    },
    onValuesChange: (values, prev) => {
      formValuesRef.current = values;
      onFormValuesChangeRef.current?.(values, prev);
    },
    validate: (values) => {
      const errors: any = {};
      // Patient required fields
      if (!values.patient.firstName) errors["patient.firstName"] = "Required";
      if (!values.patient.lastName) errors["patient.lastName"] = "Required";
      if (!values.patient.dateOfBirth)
        errors["patient.dateOfBirth"] = "Required";
      if (!values.patient.sex) errors["patient.sex"] = "Required";
      if (!values.patient.medicalRecordNumber)
        errors["patient.medicalRecordNumber"] = "Required";

      // Note: Insurance fields are NOT required - they are "recommended" per C-783/C-1617 requirements.
      // If missing, an error report is auto-created on completion for billing follow-up (non-blocking).

      // Case required fields. Practice/lab is validated at completion because
      // pass-through cases may omit that slot; surgical center is optional.
      if (!values.case.physicianId)
        errors["case.physicianId"] = "Physician is required";
      // Practice (accountId) is required for all case types EXCEPT pass-through
      // (a pass-through case may be referred by a lab with no practice). The
      // pass-through-aware guard lives in handleComplete, where derivedCaseType
      // is in scope — keeping it out of form.validate avoids a stale closure.
      if (!values.case.dateReceived) errors["case.dateReceived"] = "Required";
      if (!values.case.dateCollected) errors["case.dateCollected"] = "Required";
      if (!values.case.procedureId) errors["case.procedureId"] = "Required";

      // Case type is derived (not entered), and the per-type fields (external case number,
      // consultation reason, TC shipping destination) are always optional in form validation.
      // Missing-but-expected fields are surfaced as a soft warning modal at Complete time
      // (see useCompletionWarnings + CompletionWarningModal), not as hard form errors.

      // Specimen validation
      if (values.case.specimens.length === 0)
        errors["case.specimens"] = "At least one specimen required";

      const validationCaseType = deriveCaseType(
        (values.case.specimens || []).map(
          (specimen: { material?: string | null }) => specimen.material
        ),
        { isConsultation, isTcOnly, isGrossingOnly }
      );
      if (
        !lockPersistedSpecimens ||
        validationCaseType === "tc_only" ||
        (prefixOverride || prefixLookupResult?.prefixCode)
          ?.trim()
          .toUpperCase() === "GC"
      ) {
        Object.entries(
          getSpecimenIdentifierErrors(values.case.specimens || [])
        ).forEach(([index, message]) => {
          errors[`case.specimens.${index}.number`] = message;
        });
      }

      // Check each specimen has required source
      values.case.specimens.forEach((spec: any, idx: number) => {
        if (!spec.sourceId)
          errors[`case.specimens.${idx}.sourceId`] = "Required";
      });

      return errors;
    },
  });

  if (!specimenPopulationSnapshotRef.current) {
    specimenPopulationSnapshotRef.current = createSpecimenPopulationSnapshot(
      form.values.case.specimens
    );
  }

  // Sync refs for unload/unmount flush in the render body
  formValuesRef.current = form.values;

  const noPatientBillingPrefix = useMemo(
    () =>
      prefixOverride ||
      effectivePrefixData?.prefixCode ||
      existingCase?.casePrefix,
    [prefixOverride, effectivePrefixData?.prefixCode, existingCase?.casePrefix]
  );
  const noPatientBillingState = useMemo(
    () =>
      deriveNoPatientBillingState(
        noPatientBillingPrefix,
        form.values.case.noPatientBillingAndInsuranceInfoRequired
      ),
    [
      noPatientBillingPrefix,
      form.values.case.noPatientBillingAndInsuranceInfoRequired,
    ]
  );
  const handleNoPatientBillingChoiceChange = useCallback(
    (checked: boolean) => {
      if (noPatientBillingState.forcedByPrefix) return;
      form.setFieldValue(
        "case.noPatientBillingAndInsuranceInfoRequired",
        checked
      );
    },
    [form, noPatientBillingState.forcedByPrefix]
  );

  const hasSpecimensWithSource = useMemo(
    () => (form.values.case?.specimens || []).some((s: any) => s.sourceId),
    [form.values.case?.specimens]
  );

  const cassetteLabelCount = useMemo(
    () =>
      (form.values.case?.specimens || [])
        .filter((s: any) => s.sourceId)
        .reduce((sum: number, s: any) => sum + (Number(s.blockCount) || 1), 0),
    [form.values.case?.specimens]
  );
  uploadedFilesRef.current = uploadedFiles;

  // Case type is derived live from specimen materials + signals — never user-entered.
  // Mirror of the backend deriveCaseType; the backend re-derives authoritatively on save.
  const derivedCaseType: CaseType = useMemo(
    () =>
      deriveCaseType(
        (form.values.case.specimens || []).map(
          (specimen: { material?: string | null }) => specimen?.material
        ),
        { isConsultation, isTcOnly, isGrossingOnly }
      ),
    [form.values.case.specimens, isConsultation, isGrossingOnly, isTcOnly]
  );

  const handleSpecimensChange = useCallback(
    (nextSpecimens: any[], oldIndexByNewIndex?: SpecimenIndexMap) => {
      const previousSpecimens = form.values.case.specimens || [];
      const specimensWithUiKeys = assignSpecimenUiKeys(
        nextSpecimens,
        previousSpecimens,
        oldIndexByNewIndex
      );
      form.setFieldValue("case.specimens", specimensWithUiKeys);

      if (!oldIndexByNewIndex) {
        setSpecimenSlideCompletions({});
        setManualStains({});
        setPanelSelections({});
        setResolvedStains({});
        setCommittedFindings({});
        setCertainties((previous) => reindexSpecimenFieldRecord(previous, []));
        setUnmatchedExtractions((previous) =>
          reindexSpecimenFieldRecord(previous, [])
        );
        setWorkListStainOverrides([]);
        setHighPriorityTriggers([]);
        return specimensWithUiKeys;
      }

      setSpecimenSlideCompletions((previous) =>
        reindexSpecimenRecord(previous, oldIndexByNewIndex)
      );
      setManualStains((previous) =>
        reindexSpecimenRecord(previous, oldIndexByNewIndex)
      );
      setResolvedStains((previous) =>
        reindexSpecimenRecord(previous, oldIndexByNewIndex)
      );
      setCommittedFindings((previous) =>
        reindexSpecimenRecord(previous, oldIndexByNewIndex)
      );
      setCertainties((previous) =>
        reindexSpecimenFieldRecord(previous, oldIndexByNewIndex)
      );
      setUnmatchedExtractions((previous) =>
        reindexSpecimenFieldRecord(previous, oldIndexByNewIndex)
      );
      setWorkListStainOverrides((previous) =>
        remapWorkListStains(
          previous,
          previousSpecimens,
          specimensWithUiKeys,
          oldIndexByNewIndex
        )
      );
      setHighPriorityTriggers((previous) =>
        remapPriorityTriggers(
          previous,
          previousSpecimens,
          specimensWithUiKeys,
          oldIndexByNewIndex
        )
      );
      return specimensWithUiKeys;
    },
    [form]
  );

  const applyPopulatedSpecimens = useCallback(
    (nextSpecimens: any[]) => {
      const populatedSpecimens = handleSpecimensChange(nextSpecimens);
      specimenPopulationSnapshotRef.current =
        createSpecimenPopulationSnapshot(populatedSpecimens);
    },
    [handleSpecimensChange]
  );

  const handleSpecimenIdentifierChange = useCallback(
    (index: number, value: string) => {
      const specimen: any = form.values.case.specimens[index];
      const oldIdentifier = specimen?.number || "";
      const expectedIdentifier = specimen?.expectedSpecimenNumber;
      const specimenUiKey = specimen?.uiKey;
      form.setFieldValue(`case.specimens.${index}.number`, value);
      setWorkListStainOverrides((previous) =>
        renameMappedWorkListStains(previous, expectedIdentifier, value)
      );
      setHighPriorityTriggers((previous) =>
        previous.map((trigger) =>
          (specimenUiKey && trigger.specimenUiKey === specimenUiKey) ||
          (!trigger.specimenUiKey &&
            normalizeSpecimenIdentifier(trigger.specimenId).toLowerCase() ===
              normalizeSpecimenIdentifier(oldIdentifier).toLowerCase())
            ? { ...trigger, specimenId: value, specimenUiKey }
            : trigger
        )
      );
    },
    [form]
  );

  const effectiveSlideCompletions = useMemo(
    () =>
      deriveEffectiveDymoSlideCompletions({
        specimens: form.values.case.specimens || [],
        resolvedStains,
        explicitCompletions: specimenSlideCompletions,
      }),
    [form.values.case.specimens, resolvedStains, specimenSlideCompletions]
  );

  const submittedResolvedStains = useMemo(
    () =>
      buildResolvedStainSelections(
        form.values.case.specimens?.length || 0,
        resolvedStains,
        effectiveSlideCompletions
      ),
    [
      form.values.case.specimens?.length,
      resolvedStains,
      effectiveSlideCompletions,
    ]
  );

  const stickerCount = useMemo(
    () => countCompletedDymoSlides(effectiveSlideCompletions),
    [effectiveSlideCompletions]
  );

  const totalSlideCount = useMemo(
    () => countResolvedDymoSlides(effectiveSlideCompletions),
    [effectiveSlideCompletions]
  );

  const printLabelCount = useMemo(
    () =>
      countPrintableDymoLabels(
        effectiveSlideCompletions,
        form.values.case.specimens || []
      ),
    [effectiveSlideCompletions, form.values.case.specimens]
  );

  const displayCaseNumber = useMemo(() => {
    return getAccessioningDisplayCaseNumber({
      authoritativeCaseNumber: currentCaseNumber,
      caseType: derivedCaseType,
      casePrefix: noPatientBillingPrefix,
      externalCaseNumber: form.values.case.externalCaseNumber,
    });
  }, [
    currentCaseNumber,
    derivedCaseType,
    noPatientBillingPrefix,
    form.values.case.externalCaseNumber,
  ]);
  const isDisplayedNumberBackfill = useMemo(
    () => isDisplayedCaseNumberBackfill(displayCaseNumber, backfillCaseNumber),
    [backfillCaseNumber, displayCaseNumber]
  );

  const handleCaseNumberCorrected = useCallback(
    (result: CaseIdentityResult) => {
      applyCaseNumberContext(result.caseNumber);
    },
    [applyCaseNumberContext]
  );
  const canCorrectCaseNumber = useMemo(() => {
    const identity = currentCaseNumber
      ? parseCaseNumber(currentCaseNumber)
      : undefined;
    return (
      identity !== undefined &&
      identity.prefix !== "DR" &&
      derivedCaseType !== "pass_through"
    );
  }, [currentCaseNumber, derivedCaseType]);

  // Serialize current form state for comparison to track unsaved changes.
  // Includes slide completions and manual stains to protect them during navigation,
  // even though they are only persisted to DB at completion time.
  const serializeFormState = useCallback(() => {
    return JSON.stringify({
      patient: form.values.patient,
      case: form.values.case,
      files: uploadedFiles.map((f) => f.s3Key),
      completions: effectiveSlideCompletions,
      manualStains,
      panelSelections,
      signals: {
        isCytology,
        isResearch,
        isConsultation,
        isTcOnly,
        isGrossingOnly,
      },
    });
  }, [
    form.values.patient,
    form.values.case,
    uploadedFiles,
    effectiveSlideCompletions,
    manualStains,
    panelSelections,
    isCytology,
    isResearch,
    isConsultation,
    isTcOnly,
    isGrossingOnly,
  ]);

  const hasUnsavedChanges = useCallback(() => {
    if (!caseId) return false;
    return serializeFormState() !== lastSavedValuesRef.current;
  }, [caseId, serializeFormState]);

  const handleHasUnsavedChanges = useCallback(
    () => hasUnsavedChanges(),
    [hasUnsavedChanges]
  );

  // Missing-but-expected fields for the derived case type — surfaced as a soft warning modal
  // at Complete time, never as a blocking form error.
  const completionWarnings = useCompletionWarnings(
    derivedCaseType,
    form.values.case
  );
  const [completionWarningModalOpen, setCompletionWarningModalOpen] =
    useState(false);
  const [prefixOverrideWarningModalOpen, setPrefixOverrideWarningModalOpen] =
    useState(false);
  const [
    passthroughMismatchWarningModalOpen,
    setPassthroughMismatchWarningModalOpen,
  ] = useState(false);
  const [pendingPrefixOverrideAction, setPendingPrefixOverrideAction] =
    useState<PrefixOverrideAction | null>(null);
  const [confirmedPrefixOverrideKey, setConfirmedPrefixOverrideKey] = useState<
    string | null
  >(null);
  const [confirmedPassthroughMismatchKey, setConfirmedPassthroughMismatchKey] =
    useState<string | null>(null);
  const prefixOverrideWarningDetails = useMemo(
    () =>
      getPrefixOverrideWarningDetails({
        selectedPrefix: prefixOverride,
        computedPrefix: prefixLookupResult?.prefixCode,
      }),
    [prefixOverride, prefixLookupResult?.prefixCode]
  );

  const prefixOverrideWarningKey = useMemo(() => {
    if (!prefixOverrideWarningDetails.shouldWarn) return null;
    return `${prefixOverrideWarningDetails.computedPrefix}:${prefixOverrideWarningDetails.selectedPrefix}`;
  }, [prefixOverrideWarningDetails]);

  const needsPrefixOverrideWarning = useMemo(
    () =>
      !!prefixOverrideWarningKey &&
      prefixOverrideWarningKey !== confirmedPrefixOverrideKey,
    [confirmedPrefixOverrideKey, prefixOverrideWarningKey]
  );

  const selectedPrefixForWorkflowWarning = useMemo(
    () => prefixOverride || prefixLookupResult?.prefixCode || null,
    [prefixOverride, prefixLookupResult?.prefixCode]
  );

  const passthroughMismatchWarningDetails = useMemo(
    () =>
      getPassthroughPrefixWorkflowWarningDetails({
        selectedPrefix: selectedPrefixForWorkflowWarning,
        caseType: derivedCaseType,
      }),
    [derivedCaseType, selectedPrefixForWorkflowWarning]
  );

  const passthroughMismatchWarningKey = useMemo(() => {
    if (!passthroughMismatchWarningDetails.shouldWarn) return null;
    return `${passthroughMismatchWarningDetails.selectedPrefix}:${passthroughMismatchWarningDetails.caseType}`;
  }, [passthroughMismatchWarningDetails]);

  const needsPassthroughMismatchWarning = useMemo(
    () =>
      !!passthroughMismatchWarningKey &&
      passthroughMismatchWarningKey !== confirmedPassthroughMismatchKey,
    [confirmedPassthroughMismatchKey, passthroughMismatchWarningKey]
  );

  const openPrefixOverrideWarning = useCallback(
    (action: PrefixOverrideAction) => {
      setPendingPrefixOverrideAction(action);
      setPrefixOverrideWarningModalOpen(true);
    },
    []
  );

  // Value refs — updated every render so stable callbacks always read current values
  const formCaseValuesRef = useRef(form.values.case);
  formCaseValuesRef.current = form.values.case;
  const signalFlagsRef = useRef({
    isCytology,
    isResearch,
    isConsultation,
    isTcOnly,
    isGrossingOnly,
  });
  signalFlagsRef.current = {
    isCytology,
    isResearch,
    isConsultation,
    isTcOnly,
    isGrossingOnly,
  };
  const formRef = useRef(form);
  formRef.current = form;
  const prefixOverrideRef = useRef<string | null>(null);
  prefixOverrideRef.current = prefixOverride;
  const effectivePrefixRef = useRef<string | undefined>(undefined);
  effectivePrefixRef.current =
    prefixOverride || prefixLookupResult?.prefixCode || undefined;
  const derivedCaseTypeRef = useRef(derivedCaseType);
  derivedCaseTypeRef.current = derivedCaseType;

  const performPrefixAssignment = useCallback(
    (prefixCode: string) => {
      if (!caseId) return Promise.reject(new Error("Case is not ready"));
      return assignPrefix({ caseId, prefixCode });
    },
    [assignPrefix, caseId]
  );

  const handlePrefixAssignmentSuccess = useCallback(
    (result: CaseIdentityResult) => {
      requestedPrefixRef.current = null;
      setIsCaseIdentityUnverified(false);
      assignedPrefixRef.current = result.casePrefix;
      applyCaseNumberContext(result.caseNumber, result.allocationSource);
    },
    [applyCaseNumberContext]
  );

  const handlePrefixAssignmentError = useCallback(async () => {
    requestedPrefixRef.current = null;
    setIsCaseIdentityUnverified(true);
    let identityWasRechecked = false;
    if (caseId) {
      try {
        const currentCase = await casesService.getById(caseId);
        assignedPrefixRef.current = currentCase.casePrefix || null;
        applyCaseNumberContext(currentCase.caseNumber);
        setIsCaseIdentityUnverified(false);
        identityWasRechecked = true;
        if (prefixOverrideRef.current) {
          setPrefixOverride(currentCase.casePrefix || null);
        } else {
          setPrefixLookupResult(null);
        }
      } catch {
        // The user-facing message below prevents continuing with an unverified identity.
      }
    }
    notifications.show({
      title: "Case Number Assignment Failed",
      message: identityWasRechecked
        ? "The prior prefix was restored. Please select the desired prefix again."
        : "The current case number could not be verified. Reload this case before continuing.",
      color: "red",
    });
  }, [applyCaseNumberContext, caseId]);

  const handlePrefixAssignmentSettled = useCallback(() => {
    setIsAssigningPrefix(false);
  }, []);

  const prefixAssignmentObserver = useMemo(
    () => ({
      onLatestSuccess: handlePrefixAssignmentSuccess,
      onLatestError: handlePrefixAssignmentError,
      onLatestSettled: handlePrefixAssignmentSettled,
    }),
    [
      handlePrefixAssignmentError,
      handlePrefixAssignmentSettled,
      handlePrefixAssignmentSuccess,
    ]
  );

  const assignAuthoritativePrefix = useCallback(
    (prefixCode: string) => {
      if (!caseId || derivedCaseTypeRef.current === "pass_through") return;
      const normalizedPrefix = prefixCode.trim().toUpperCase();
      if (!normalizedPrefix) return;
      if (
        !shouldRequestPrefixAssignment(
          assignedPrefixRef.current,
          requestedPrefixRef.current,
          normalizedPrefix
        )
      )
        return;
      requestedPrefixRef.current = normalizedPrefix;
      setIsCaseIdentityUnverified(false);
      setIsAssigningPrefix(true);
      prefixAssignmentExecutorRef.current.execute(
        normalizedPrefix,
        performPrefixAssignment,
        prefixAssignmentObserver
      );
    },
    [caseId, performPrefixAssignment, prefixAssignmentObserver]
  );

  // Debounce refs for stable callbacks
  const pathologistDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const pathologistAssignmentGuardRef = useRef(createLatestRequestGuard());

  // Stable debounced pathologist assignment — reads current values from refs
  const debouncedPathologistAssignment = useCallback(() => {
    const assignmentRequest = pathologistAssignmentGuardRef.current.begin();
    if (pathologistDebounceRef.current)
      clearTimeout(pathologistDebounceRef.current);
    pathologistDebounceRef.current = setTimeout(async () => {
      const c = formCaseValuesRef.current;
      if (
        !c.dateReceived ||
        (!c.accountId && !c.submittingLabId && !c.locationId && !c.physicianId)
      )
        return;
      if (c.pathologistId) return;
      try {
        const result = await assignPathologist({
          sendingPracticeId: c.accountId || undefined,
          physicianId: c.physicianId || undefined,
          surgeryCenterLabId: c.submittingLabId || c.locationId || undefined,
          date: c.dateReceived,
        });
        if (!pathologistAssignmentGuardRef.current.isCurrent(assignmentRequest))
          return;
        formRef.current.setFieldValue(
          "case.pathologistId",
          result.pathologistId
        );
        setAssignmentReason(result.reason);
      } catch {
        if (!pathologistAssignmentGuardRef.current.isCurrent(assignmentRequest))
          return;
        setAssignmentReason("Auto-assignment failed");
      }
    }, 400);
  }, []); // stable — reads all state from refs

  // Stable debounced prefix lookup — reads current values from refs
  const debouncedPrefixLookup = useCallback(() => {
    const lookupGeneration = prefixLookupGuardRef.current.begin();
    if (prefixLookupTimeoutRef.current)
      clearTimeout(prefixLookupTimeoutRef.current);
    prefixLookupTimeoutRef.current = setTimeout(async () => {
      const c = formCaseValuesRef.current;
      const flags = signalFlagsRef.current;
      const { practiceId, physicianId, surgeryCenterLabId } =
        buildClientIdentityResolutionInput({
          sendingPracticeId: c.accountId,
          physicianId: c.physicianId,
          surgeryCenterLabId: c.locationId,
          submittingLabId: c.submittingLabId,
        });
      // Trigger lookup if we have a practice, a surgery center, a physician
      // (doctor-identity prefixes resolve from physician alone), or any signal
      // flag set.
      const hasAnySignalFlag =
        flags.isCytology ||
        flags.isResearch ||
        flags.isConsultation ||
        flags.isTcOnly;
      if (
        !practiceId &&
        !surgeryCenterLabId &&
        !physicianId &&
        !hasAnySignalFlag
      ) {
        setPrefixLookupResult(null);
        setIsResolvingPrefix(false);
        return;
      }
      setIsResolvingPrefix(true);
      try {
        const result = await lookupPrefix({
          practiceId,
          physicianId,
          surgeryCenterLabId,
          insuranceProviderId:
            c.primaryInsurance?.insuranceProvider || undefined,
          insurancePlanTypeId: c.primaryInsurance?.planType || undefined,
          isCytology: flags.isCytology,
          isResearch: flags.isResearch,
          isConsultation: flags.isConsultation,
          isTcOnly: flags.isTcOnly,
        });
        if (!prefixLookupGuardRef.current.isCurrent(lookupGeneration)) return;
        setPrefixLookupResult(result);
        if (!prefixOverrideRef.current) {
          assignAuthoritativePrefix(result.prefixCode);
        }
        const currentSpecimens = formRef.current.values.case?.specimens || [];
        const defaultedSpecimens = applyPassThroughMaterialDefaultsToSpecimens(
          result.prefixCode,
          currentSpecimens
        );
        if (defaultedSpecimens !== currentSpecimens) {
          specimenPopulationSnapshotRef.current =
            advanceSpecimenPopulationSnapshot(
              specimenPopulationSnapshotRef.current,
              currentSpecimens,
              defaultedSpecimens
            );
          formRef.current.setFieldValue("case.specimens", defaultedSpecimens);
        }
        setIsResolvingPrefix(false);
        setPrefixSelectedBillingScenarioId(null);
        setManualBillingScenarioId(null);
        // Prefix lookup now only drives the prefix code + billing scenario. Case type is
        // derived from materials + signals, never from the prefix.
        if (result.resolvedBillingScenarioId) {
          // Only auto-update the form field if there is no manual prefix override active.
          // If an override is active, it handles its own billing scenario defaults.
          if (!prefixOverrideRef.current) {
            formRef.current.setFieldValue(
              "case.billingScenarioId",
              result.resolvedBillingScenarioId as any
            );
          }
        } else if (!prefixOverrideRef.current) {
          // If no billing scenario was resolved, and no override is active, clear it.
          formRef.current.setFieldValue("case.billingScenarioId", null as any);
        }
      } catch {
        if (!prefixLookupGuardRef.current.isCurrent(lookupGeneration)) return;
        setPrefixLookupResult(null);
        setIsResolvingPrefix(false);
      }
    }, 600);
  }, [assignAuthoritativePrefix]);

  // Signal flag handlers: update state AND re-run prefix lookup (replaces flag-watching useEffect)
  const handleIsCytologyChange = useCallback(
    (value: boolean) => {
      setIsCytology(value);
      debouncedPrefixLookup();
    },
    [debouncedPrefixLookup]
  );
  const handleIsResearchChange = useCallback(
    (value: boolean) => {
      setIsResearch(value);
      debouncedPrefixLookup();
    },
    [debouncedPrefixLookup]
  );
  const handleIsConsultationChange = useCallback(
    (value: boolean) => {
      setIsConsultation(value);
      if (value) setIsGrossingOnly(false);
      debouncedPrefixLookup();
    },
    [debouncedPrefixLookup]
  );
  const handleIsTcOnlyChange = useCallback(
    (value: boolean) => {
      setIsTcOnly(value);
      if (value) setIsGrossingOnly(false);
      debouncedPrefixLookup();
    },
    [debouncedPrefixLookup]
  );
  const handleIsGrossingOnlyChange = useCallback((value: boolean) => {
    setIsGrossingOnly(value);
    if (!value) return;
    setIsConsultation(false);
    setIsTcOnly(false);
    setPanelSelections({});
    setManualStains({});
  }, []);

  const handlePrefixOverrideChange = useCallback(
    async (selectedPrefix: string | null) => {
      prefixOverrideRef.current = selectedPrefix;
      prefixLookupGuardRef.current.invalidate();
      setPrefixOverride(selectedPrefix);
      if (selectedPrefix) assignAuthoritativePrefix(selectedPrefix);
      const specimens = form.values.case?.specimens || [];
      const defaultedSpecimens = applyPassThroughMaterialDefaultsToSpecimens(
        selectedPrefix,
        specimens
      );
      if (defaultedSpecimens !== specimens) {
        specimenPopulationSnapshotRef.current =
          advanceSpecimenPopulationSnapshot(
            specimenPopulationSnapshotRef.current,
            specimens,
            defaultedSpecimens
          );
        form.setFieldValue("case.specimens", defaultedSpecimens);
      }

      if (selectedPrefix) {
        const prefixObj = (allPrefixesData?.data || []).find(
          (p) => p.code === selectedPrefix
        );
        if (prefixObj) {
          try {
            const details = await getPrefixById(prefixObj.id);
            const defaultScenario = details.allowedBillingScenarios.find(
              (s) => s.isDefault
            );

            setPrefixOverrideResult({
              prefixId: details.id,
              prefixCode: details.code,
              prefixColor: details.color,
              prefixType: details.prefixType
                ? {
                    name: details.prefixType.name,
                    assignmentStrategy: details.prefixType.assignmentStrategy,
                    priority: details.prefixType.priority,
                  }
                : null,
              billingPending: false,
              fallbackPrefixCode: details.fallbackPrefix?.code || null,
              resolvedBillingScenarioId:
                defaultScenario?.billingScenarioId || null,
              resolvedBillingScenarioName:
                defaultScenario?.billingScenario?.name || null,
              allowedBillingScenarios: details.allowedBillingScenarios.map(
                (s) => ({
                  id: s.billingScenarioId,
                  name: s.billingScenario?.name || "Unknown",
                  isDefault: s.isDefault,
                })
              ),
            });

            if (defaultScenario) {
              form.setFieldValue(
                "case.billingScenarioId",
                defaultScenario.billingScenarioId as any
              );
            }
          } catch (error) {
            notifications.show({
              title: "Prefix Lookup Failed",
              message:
                "Unable to load details for the selected prefix. Please try again.",
              color: "red",
            });
          }
        }
      } else {
        setPrefixOverrideResult(null);
        // Clearing override will allow debouncedPrefixLookup to restore the
        // resolved billing scenario on its next run or we can trigger it here.
        debouncedPrefixLookup();
      }
    },
    [form, allPrefixesData, assignAuthoritativePrefix, debouncedPrefixLookup]
  );

  const applyExtractionSignalFlags = useCallback(
    (signals: ExtractionSignalFlags) => {
      signalFlagsRef.current = {
        ...signals,
        isGrossingOnly: signalFlagsRef.current.isGrossingOnly,
      };
      setIsCytology(signals.isCytology);
      setIsResearch(signals.isResearch);
      setIsConsultation(signals.isConsultation);
      setIsTcOnly(signals.isTcOnly);
      debouncedPrefixLookup();
    },
    [debouncedPrefixLookup]
  );

  // Wire form value changes to debounced async operations (replaces two state-watching useEffects)
  const handleFormValuesChange = useCallback(
    (values: any, prev: any) => {
      const c = values.case;
      const p = prev.case;
      const pathologistFields = [
        "accountId",
        "locationId",
        "physicianId",
        "dateReceived",
      ] as const;
      // Specimens drive the derived case type, so re-run assignment when they change too.
      if (
        pathologistFields.some((f) => c[f] !== p[f]) ||
        c.specimens !== p.specimens
      )
        debouncedPathologistAssignment();
      const prefixFields = ["accountId", "locationId", "physicianId"] as const;
      const insuranceChanged =
        c.primaryInsurance?.insuranceProvider !==
          p.primaryInsurance?.insuranceProvider ||
        c.primaryInsurance?.planType !== p.primaryInsurance?.planType;
      if (prefixFields.some((f) => c[f] !== p[f]) || insuranceChanged)
        debouncedPrefixLookup();
    },
    [debouncedPathologistAssignment, debouncedPrefixLookup]
  );
  onFormValuesChangeRef.current = handleFormValuesChange;

  // Shared: look up an expected case by external case number and pre-fill the form
  const performWorkListLookup = useCallback(
    async (extCaseNum: string) => {
      // Set ref before the async call so re-renders during the in-flight request
      // see the guard as already satisfied and don't schedule duplicate calls.
      const lookupGeneration = workListLookupGuardRef.current.begin();
      lastLookedUpExtCaseRef.current = extCaseNum;
      const expectedCase = await workListService.lookupExpectedCase(extCaseNum);
      if (!workListLookupGuardRef.current.isCurrent(lookupGeneration)) return;
      if (!expectedCase) {
        setMatchedExpectedCase(null);
        setWorkListStainOverrides([]);
        return;
      }
      setMatchedExpectedCase(expectedCase);

      if (expectedCase.groupedSpecimens?.length) {
        const sourcesArr = sourcesData?.sources || [];
        const wlSpecimens = expectedCase.groupedSpecimens.map((specimen) => {
          const site = specimen.site.trim().toLowerCase();
          const matchedSource = site
            ? sourcesArr.find(
                (source) =>
                  source.label.toLowerCase().includes(site) ||
                  site.includes(source.label.toLowerCase())
              )
            : undefined;
          return {
            number: specimen.specimenNumber,
            expectedSpecimenNumber: specimen.specimenNumber,
            sourceId: matchedSource?.value || "",
            sourceName: matchedSource?.label || specimen.site,
            subsiteId: null,
            collectionMethod: "",
            material:
              derivedCaseTypeRef.current === "tc_only" ||
              signalFlagsRef.current.isTcOnly
                ? "blocks_and_slides"
                : getDefaultMaterialForNewSpecimen(
                    effectivePrefixRef.current,
                    ""
                  ),
            findings: "",
            comments: "",
            extractedStainCodes: specimen.requestedStains.join(", "),
            matchedStainIds: [],
            extractedGrossCodePrefix: "",
            extractedBlockIdentifiers: "",
            extractedDimensions: "",
            gross: "",
            grossCodeId: null,
            grossTemplate: "",
            blockCount: specimen.blockCount || 1,
          };
        });
        applyPopulatedSpecimens(wlSpecimens);
      }

      setWorkListStainOverrides(
        (expectedCase.resolvedStains || []).map((stain) => ({
          expectedSpecimenNumber: stain.specimenNumber,
          specimenNumber: stain.specimenNumber,
          stainId: stain.stainId,
          stainCode: stain.stainCode,
          isCompleted: false,
        }))
      );

      if (expectedCase.unresolvedStains?.length) {
        notifications.show({
          title: "Work List Stains Need Attention",
          message: expectedCase.unresolvedStains
            .map((stain) => `${stain.specimenNumber}: ${stain.requestedStain}`)
            .join(", "),
          color: "red",
          autoClose: false,
        });
      }

      notifications.show({
        title: "Work List Match Found",
        message: expectedCase.unresolvedStains?.length
          ? `Expected case ${expectedCase.externalCaseNumber} matched, but stain requests must be resolved before completion`
          : `Expected case ${expectedCase.externalCaseNumber} — specimens and stains pre-filled`,
        color: expectedCase.unresolvedStains?.length ? "orange" : "teal",
        autoClose: 8000,
      });
    },
    [applyPopulatedSpecimens, sourcesData?.sources]
  );

  // Ref to the current matchedExpectedCase for use in the stable onChange callback
  const matchedExpectedCaseRef = useRef(matchedExpectedCase);
  matchedExpectedCaseRef.current = matchedExpectedCase;

  // Wired to the external case number input's onChange — replaces the state-watching useEffect
  const handleExternalCaseNumberChange = useCallback(
    (value: string) => {
      form.setFieldValue("case.externalCaseNumber", value);
      form.clearFieldError("case.externalCaseNumber");
      setCanonicalExternalClaimState("idle");
      workListLookupGuardRef.current.invalidate();
      const trimmed = value.trim();
      const currentMatch = matchedExpectedCaseRef.current;
      if (currentMatch && currentMatch.externalCaseNumber.trim() !== trimmed) {
        matchedExpectedCaseRef.current = null;
        setMatchedExpectedCase(null);
        setWorkListStainOverrides([]);
      }
      if (workListLookupTimeoutRef.current) {
        clearTimeout(workListLookupTimeoutRef.current);
        workListLookupTimeoutRef.current = null;
      }
      if (!trimmed || trimmed.length < 3) return;
      if (trimmed === lastLookedUpExtCaseRef.current) return;
      if (currentMatch?.externalCaseNumber.trim() === trimmed) return;
      workListLookupTimeoutRef.current = setTimeout(() => {
        performWorkListLookup(trimmed).catch(() => {});
      }, 800);
    },
    [form, performWorkListLookup]
  );

  const formValuesWithDerivedType = useMemo(
    () => ({
      ...form.values,
      case: { ...form.values.case, accessioningType: derivedCaseType },
    }),
    [derivedCaseType, form.values]
  );
  const activeUnresolvedWorkListStains = useMemo(() => {
    return filterActiveUnresolvedWorkListStains(
      matchedExpectedCase?.unresolvedStains ?? [],
      form.values.case.specimens ?? []
    );
  }, [form.values.case.specimens, matchedExpectedCase]);

  // Detect missing required fields and completion using the live derived type.
  const missingFields = useMissingFields(formValuesWithDerivedType);
  const fieldCompletion = useFieldCompletion(formValuesWithDerivedType);

  // Handle block count changes in the blocks/slides preview
  const handleBlockCountChange = useCallback(
    (specimenIndex: number, count: number) => {
      const specimens = form.values.case?.specimens || [];
      if (specimens[specimenIndex]) {
        form.setFieldValue(`case.specimens.${specimenIndex}.blockCount`, count);
      }
    },
    [form]
  );

  // Initialize facility ID
  useEffect(() => {
    if (defaultFacility && !form.values.case.facilityId) {
      form.setFieldValue("case.facilityId", defaultFacility.id);
    }
  }, [defaultFacility, form]);

  // Initialize case - wrapped in useCallback with stable dependencies
  const initializeCase = useCallback(async () => {
    // For edit mode, we just set the case ID from URL and wait for data to load
    if (isEditMode && editingCaseId) {
      // If we already have this caseId (e.g. just created it and URL was updated),
      // skip the loading/refetch cycle to avoid a flash of loading state
      if (caseId === editingCaseId) {
        return;
      }

      if (existingCaseLoading || existingSpecimensLoading) {
        setCaseCreationState("loading");
        return;
      }

      if (existingCase) {
        setCaseId(editingCaseId);
        applyCaseNumberContext(existingCase.caseNumber);
        setCaseCreationState("ready");
        return;
      }
    }

    // For new case mode
    if (!defaultFacility || !user) return;

    // Don't recreate if already created or currently initializing
    if (caseId || isInitializingRef.current) {
      if (caseId) {
        setCaseCreationState("ready");
      }
      return;
    }

    // Mark as initializing to prevent duplicate calls (React StrictMode, etc.)
    isInitializingRef.current = true;
    setCaseCreationState("loading");
    setCaseCreationError(null);

    try {
      const result = await createDraftCase(defaultFacility.id);

      setCaseId(result.caseId);
      applyCaseNumberContext(result.caseNumber);
      setCaseCreationState("ready");

      // Initialize lastSavedValuesRef for the new draft
      lastSavedValuesRef.current = serializeFormState();

      // Mark that we just created this case (to skip "editing" notification)
      justCreatedCaseRef.current = true;

      // Update URL to include case ID so refresh loads the same case
      navigate(`/accessioning/${result.caseId}`, { replace: true });
    } catch (error: any) {
      setCaseCreationState("error");
      setCaseCreationError(error.message || "Failed to create new case");
      // Reset the ref so user can retry
      isInitializingRef.current = false;
    }
  }, [
    defaultFacility,
    user,
    caseId,
    createDraftCase,
    navigate,
    isEditMode,
    editingCaseId,
    existingCase,
    existingCaseLoading,
    existingSpecimensLoading,
    applyCaseNumberContext,
    serializeFormState,
  ]);

  // Create draft case on mount
  useEffect(() => {
    initializeCase();
  }, [initializeCase]);

  // Populate form from existing case data when in edit mode
  const hasPopulatedForm = useRef(false);
  useEffect(() => {
    if (
      !isEditMode ||
      !existingCase ||
      existingCaseLoading ||
      existingSpecimensLoading ||
      existingFilesLoading ||
      hasPopulatedForm.current
    ) {
      return;
    }

    hasPopulatedForm.current = true;

    // Hydrate signal flags (C-4703) from existing case data to prevent them
    // being reset to default (false) on subsequent saves/completion.
    setIsCytology(!!existingCase.isCytology);
    setIsResearch(!!existingCase.isResearch);
    setIsConsultation(!!existingCase.isConsultation);
    setIsTcOnly(!!existingCase.isTcOnly);
    setIsGrossingOnly(!!existingCase.isGrossingOnly);

    // Populate patient data from Patient record, or fall back to draftPatientData
    const patientData = existingCase.patient || existingCase.draftPatientData;
    if (patientData) {
      const p = patientData;
      form.setFieldValue("patient.firstName", p.firstName || "");
      form.setFieldValue("patient.lastName", p.lastName || "");
      form.setFieldValue(
        "patient.dateOfBirth",
        normalizeAccessioningDateOnly(p.dateOfBirth)
      );
      form.setFieldValue(
        "patient.sex",
        (p.sex as "" | "M" | "F" | "Other") || ""
      );
      form.setFieldValue(
        "patient.medicalRecordNumber",
        p.medicalRecordNumber || ""
      );
      form.setFieldValue("patient.mobilePhone", p.mobilePhone || "");
      form.setFieldValue("patient.homePhone", p.homePhone || "");
      form.setFieldValue("patient.workPhone", p.workPhone || "");
      form.setFieldValue("patient.email", p.email || "");
      if (p.address) {
        form.setFieldValue(
          "patient.address.address",
          p.address.address || p.address.street || ""
        );
        form.setFieldValue("patient.address.city", p.address.city || "");
        form.setFieldValue("patient.address.state", p.address.state || "");
        form.setFieldValue(
          "patient.address.zipCode",
          p.address.zipCode || p.address.zip || ""
        );
      }
    }

    // Populate case data
    form.setFieldValue("case.facilityId", existingCase.facilityId || "");

    // Prefer saved procedureId, then manual procedureName override, then
    // suggestion from aiExtractedData if present.
    // This allows manual overrides and final refinements (like Surgical-family
    // inference) to survive reloads.
    const savedProcedureId = existingCase.procedureId;
    const savedProcedureName = existingCase.procedureName;
    const extractedProcedureId = (existingCase.aiExtractedData as any)
      ?.matchedData?.procedureId;

    const procedureValue =
      savedProcedureId || savedProcedureName || extractedProcedureId || "";

    form.setFieldValue("case.procedureId", procedureValue);
    form.setFieldValue("case.procedureName", savedProcedureName || "");

    form.setFieldValue(
      "case.externalCaseNumber",
      existingCase.externalCaseNumber || ""
    );
    form.setFieldValue(
      "case.ruleOuts",
      Array.isArray((existingCase as any).ruleOuts)
        ? (existingCase as any).ruleOuts.join(", ")
        : (existingCase as any).ruleOuts || ""
    );
    form.setFieldValue(
      "case.dateReceived",
      normalizeAccessioningDateOnly(existingCase.dateReceived) ??
        getNewAccessioningDateReceived()
    );
    form.setFieldValue(
      "case.dateCollected",
      normalizeAccessioningDateOnly(existingCase.dateCollected)
    );
    hydratePersistedSpecializedCaseFields(existingCase, form.setFieldValue);
    form.setFieldValue(
      "case.clinicalHistoryNotes",
      existingCase.clinicalHistoryNotes || ""
    );
    form.setFieldValue("case.indications", existingCase.indications || "");
    form.setFieldValue("case.impressions", existingCase.impressions || "");
    form.setFieldValue("case.pathologistId", existingCase.pathologistId || "");
    form.setFieldValue(
      "case.billingScenarioId",
      (existingCase.billingScenarioId ?? null) as any
    );
    form.setFieldValue(
      "case.noPatientBillingAndInsuranceInfoRequired",
      existingCase.noPatientBillingAndInsuranceInfoRequired === true
    );
    form.setFieldValue("case.isRush", existingCase.isRush || false);
    form.setFieldValue("case.rushReason", existingCase.rushReason || "");
    form.setFieldValue(
      "case.isHighPriority",
      existingCase.isHighPriority || false
    );
    form.setFieldValue(
      "case.isThroughSalesRep",
      existingCase.isThroughSalesRep || false
    );
    form.setFieldValue("case.salesRepId", existingCase.salesRepId || "");
    form.setFieldValue(
      "case.shippingPathologist",
      existingCase.shippingPathologist || ""
    );
    form.setFieldValue(
      "case.shippingStreet",
      existingCase.shippingStreet || ""
    );
    form.setFieldValue("case.shippingCity", existingCase.shippingCity || "");
    form.setFieldValue("case.shippingState", existingCase.shippingState || "");
    form.setFieldValue(
      "case.shippingZipCode",
      existingCase.shippingZipCode || ""
    );
    form.setFieldValue(
      "case.highPriorityReason",
      existingCase.highPriorityReason || ""
    );

    // Handle unmatched procedure suggestion on mount if no ID was matched
    const extractedUnmatched = (existingCase.aiExtractedData as any)
      ?.matchedData?.unmatchedProcedureName;
    if (!savedProcedureId && !extractedProcedureId && extractedUnmatched) {
      setUnmatchedExtractions((prev) => ({
        ...prev,
        "case.procedureId": extractedUnmatched,
      }));
    }

    // Populate accessioning type - use case's saved value, fall back to workflow default
    // Check for both null and undefined, and ensure we set the field if a value exists
    const accessioningTypeValue =
      existingCase.accessioningType ?? existingCase.workflow?.accessioningType;
    if (accessioningTypeValue) {
      form.setFieldValue("case.accessioningType", accessioningTypeValue);
    }

    // Set sending practice/physician/surgery center IDs (v2 schema)
    // Use relation IDs if available, otherwise fall back to direct ID fields
    const loadedAccountId =
      existingCase.sendingPractice?.id || existingCase.sendingPracticeId;
    const loadedPhysicianId =
      existingCase.physician?.id || existingCase.physicianId;
    const loadedLocationId =
      existingCase.surgeryCenterLab?.id || existingCase.surgeryCenterLabId;
    const loadedSubmittingLabId =
      existingCase.submittingLab?.id || existingCase.submittingLabId;

    if (loadedAccountId) {
      form.setFieldValue("case.accountId", loadedAccountId);
    }
    if (loadedPhysicianId) {
      form.setFieldValue("case.physicianId", loadedPhysicianId);
    }
    if (loadedLocationId) {
      form.setFieldValue("case.locationId", loadedLocationId);
    }
    if (loadedSubmittingLabId) {
      form.setFieldValue("case.submittingLabId", loadedSubmittingLabId);
    }

    // Populate primary insurance
    if (existingCase.primaryInsurance) {
      const pi = existingCase.primaryInsurance;
      form.setFieldValue(
        "case.primaryInsurance.insuranceProvider",
        pi.insuranceProvider || ""
      );
      form.setFieldValue("case.primaryInsurance.planType", pi.planType || "");
      form.setFieldValue(
        "case.primaryInsurance.policyNumber",
        pi.policyNumber || ""
      );
      form.setFieldValue(
        "case.primaryInsurance.authorizationNumber",
        pi.authorizationNumber || ""
      );
      form.setFieldValue(
        "case.primaryInsurance.groupNumber",
        pi.groupNumber || ""
      );
      form.setFieldValue("case.primaryInsurance.groupName", pi.groupName || "");
      form.setFieldValue(
        "case.primaryInsurance.patientRelationToInsured",
        pi.patientRelationToInsured || ""
      );
      if (pi.address) {
        form.setFieldValue(
          "case.primaryInsurance.address.address",
          pi.address.address || ""
        );
        form.setFieldValue(
          "case.primaryInsurance.address.city",
          pi.address.city || ""
        );
        form.setFieldValue(
          "case.primaryInsurance.address.state",
          pi.address.state || ""
        );
        form.setFieldValue(
          "case.primaryInsurance.address.zipCode",
          pi.address.zipCode || ""
        );
      }
    }

    // Populate secondary insurance
    if (existingCase.secondaryInsurance) {
      const si = existingCase.secondaryInsurance;
      form.setFieldValue(
        "case.secondaryInsurance.insuranceProvider",
        si.insuranceProvider || ""
      );
      form.setFieldValue("case.secondaryInsurance.planType", si.planType || "");
      form.setFieldValue(
        "case.secondaryInsurance.policyNumber",
        si.policyNumber || ""
      );
      form.setFieldValue(
        "case.secondaryInsurance.authorizationNumber",
        si.authorizationNumber || ""
      );
      form.setFieldValue(
        "case.secondaryInsurance.groupNumber",
        si.groupNumber || ""
      );
      form.setFieldValue(
        "case.secondaryInsurance.groupName",
        si.groupName || ""
      );
      form.setFieldValue(
        "case.secondaryInsurance.patientRelationToInsured",
        si.patientRelationToInsured || ""
      );
      if (si.address) {
        form.setFieldValue(
          "case.secondaryInsurance.address.address",
          si.address.address || ""
        );
        form.setFieldValue(
          "case.secondaryInsurance.address.city",
          si.address.city || ""
        );
        form.setFieldValue(
          "case.secondaryInsurance.address.state",
          si.address.state || ""
        );
        form.setFieldValue(
          "case.secondaryInsurance.address.zipCode",
          si.address.zipCode || ""
        );
      }
    }

    // Populate specimens
    if (existingSpecimens.length > 0) {
      const specimenValues = existingSpecimens.map((spec) => ({
        uiKey: `specimen-db-${spec.id}`,
        // Persisted identity. Once the case leaves Draft the backend updates the
        // matching row in place instead of recreating it; a specimen with no id is
        // treated as newly added and gets its own block and slide.
        id: spec.id,
        number: spec.number || "A",
        sourceId: spec.sourceId || "",
        subsiteId: spec.subsiteId || null,
        collectionMethod: spec.collectionMethod || "",
        material: spec.material || "",
        findings: spec.findings || "",
        comments: spec.comments || "",
        extractedStainCodes: "",
        matchedStainIds: [],
        extractedGrossCodePrefix: "",
        extractedBlockIdentifiers: "",
        extractedDimensions: "",
        gross: spec.gross || "",
        grossCodeId: spec.accessioningGrossCodeId || null,
        fragmentCount: spec.fragmentCount ?? null,
        fragmentCountQualifier: spec.fragmentCountQualifier ?? null,
        grossTemplate: "",
        blockCount: spec.blocks?.length || 1,
      })) as typeof form.values.case.specimens;
      form.setFieldValue("case.specimens", specimenValues);
      specimenPopulationSnapshotRef.current =
        createSpecimenPopulationSnapshot(specimenValues);

      // Seed committedFindings from saved data so the stain preview works on load
      const initialCommitted: Record<number, string> = {};
      specimenValues.forEach((sv, idx) => {
        initialCommitted[idx] = sv.findings;
      });
      setCommittedFindings(initialCommitted);

      // Restore manual stains from existing slides (blocks → slides → stain)
      // When a case has already been saved/completed, slides exist in the DB with their stains.
      // We reconstruct these as "added" manual stains so the preview shows them correctly.
      // The BlocksAndStainsPreview component deduplicates against default stains automatically.
      const restoredManualStains: Record<
        number,
        {
          added: Array<{
            id: string;
            selectionId?: string;
            code: string;
            description: string;
            isSystemGenerated?: boolean;
          }>;
          removed: string[];
        }
      > = {};

      existingSpecimens.forEach((spec, index) => {
        const slides = spec.blocks?.flatMap((b) => b.slides || []) || [];
        if (slides.length === 0) return;

        // Use the first block to avoid repeating the same physical positions across blocks.
        const firstBlockSlides = spec.blocks?.[0]?.slides || [];
        const stains = firstBlockSlides
          .filter((slide) => slide.stainId && slide.stain)
          .map((slide) => ({
            id: slide.stainId!,
            selectionId: `persisted:${slide.id}`,
            code: slide.stain!.code,
            description: slide.stain!.description,
            isPersisted: true,
          }));

        if (stains.length > 0) {
          restoredManualStains[index] = {
            added: stains,
            removed: [],
          };
        }
      });

      if (Object.keys(restoredManualStains).length > 0) {
        setManualStains(restoredManualStains);
      }
    }

    // Hydrate transient draft UI state (slide completions, manual stains) from aiExtractedData.draftUiState
    // This allows in-progress work to survive reloads/navigation during the draft stage.
    const draftUiState = existingCase.aiExtractedData?.draftUiState;
    if (draftUiState) {
      if (draftUiState.slideCompletions) {
        setSpecimenSlideCompletions(draftUiState.slideCompletions);
      }
      if (draftUiState.manualStains) {
        const normalized: Record<number, any> = {};
        Object.entries(draftUiState.manualStains).forEach(
          ([key, value]: [string, any]) => {
            const idx = parseInt(key, 10);
            if (!isNaN(idx)) {
              normalized[idx] = {
                added: (value.added || []).map(
                  (s: any, stainIndex: number) => ({
                    id: s.id,
                    selectionId:
                      s.selectionId ??
                      buildStainSelectionId(
                        "legacy-manual",
                        idx,
                        stainIndex,
                        s.id
                      ),
                    code: s.code,
                    description: s.description || s.code,
                    isSystemGenerated: !!s.isSystemGenerated,
                    isPersisted: !!s.isPersisted,
                  })
                ),
                removed: value.removed || [],
              };
            }
          }
        );
        setManualStains((prev) => ({
          ...prev,
          ...normalized,
        }));
      }
      if (draftUiState.panelSelections) {
        setPanelSelections(draftUiState.panelSelections);
      }
    }

    // Populate existing files
    if (existingFiles.length > 0) {
      const mappedFiles: UploadedFile[] = existingFiles.map((f) => ({
        fileName: f.name,
        mimeType: f.mimeType,
        s3Key: f.url || f.id, // Use URL or ID as the key
      }));
      setUploadedFiles(mappedFiles);
    }

    // Initialize lastSavedValuesRef to prevent immediate auto-save after loading
    // We need to use setTimeout to ensure form values are set before serializing
    setTimeout(() => {
      lastSavedValuesRef.current = serializeFormState();
    }, 100);

    // Only show "editing" notification if we're loading an existing case (not one we just created)
    if (!justCreatedCaseRef.current) {
      notifications.show({
        title: "Editing Case",
        message: `Loaded case ${existingCase.caseNumber} for editing`,
        color: "blue",
      });
    }
    // Reset the ref so future navigations to this case show the notification
    justCreatedCaseRef.current = false;

    // Restore the persisted prefix as the manual override so the user's last
    // confirmed choice survives re-edit. The lookup below still fires; if it
    // resolves to the same code, the override matches it (no visible badge).
    // If it differs (e.g. inputs changed since last save), the "Auto-resolved"
    // hint surfaces it without silently replacing the user's saved value.
    if (existingCase.casePrefix && existingCase.casePrefix !== "DR") {
      setPrefixOverride(existingCase.casePrefix);
      assignedPrefixRef.current = existingCase.casePrefix;
    } else {
      setPrefixOverride(null);
      assignedPrefixRef.current = null;
    }

    // Trigger prefix lookup after form is populated with existing case data
    debouncedPrefixLookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    existingCase,
    existingSpecimens,
    existingFiles,
    existingCaseLoading,
    existingSpecimensLoading,
    existingFilesLoading,
    debouncedPrefixLookup,
  ]);

  // Helper to sanitize form data before sending to backend
  const sanitizeFormData = useCallback(
    (values: any, includeGrossingIdentifiers = false) => {
      // Sanitize case data - ensure arrays are arrays, not empty strings
      const sanitizedCase = {
        ...values.case,
        dateReceived: normalizeAccessioningDateOnly(values.case.dateReceived),
        dateCollected: normalizeAccessioningDateOnly(values.case.dateCollected),
        // Ensure clinicalHistoryCodes is an array (not empty string)
        clinicalHistoryCodes: Array.isArray(values.case.clinicalHistoryCodes)
          ? values.case.clinicalHistoryCodes
          : [],
        // Send empty strings so the backend can clear values with toNullableString
        externalCaseNumber: values.case.externalCaseNumber || null,
        ruleOuts: normalizeRuleOuts(values.case.ruleOuts),
        clinicalHistoryNotes: values.case.clinicalHistoryNotes ?? null,
        indications: values.case.indications ?? null,
        impressions: values.case.impressions ?? null,
        pathologistId: values.case.pathologistId || undefined,
        billingScenarioId: values.case.billingScenarioId || undefined,
        // Case type is derived (and re-derived authoritatively on the backend); send the
        // derived value for transparency rather than any user-entered field.
        accessioningType: deriveCaseType(
          (values.case.specimens || []).map(
            (specimen: { material?: string | null }) => specimen?.material
          ),
          {
            isConsultation: signalFlagsRef.current.isConsultation,
            isTcOnly: signalFlagsRef.current.isTcOnly,
            isGrossingOnly: signalFlagsRef.current.isGrossingOnly,
          }
        ),
        // Signal flags (C-4703): include page-level signal states in the case payload
        isTcOnly: signalFlagsRef.current.isTcOnly,
        isGrossingOnly: signalFlagsRef.current.isGrossingOnly,
        isConsultation: signalFlagsRef.current.isConsultation,
        isResearch: signalFlagsRef.current.isResearch,
        isCytology: signalFlagsRef.current.isCytology,
        rushReason: values.case.rushReason || undefined,
        highPriorityReason: values.case.highPriorityReason || undefined,
        salesRepId: values.case.salesRepId || undefined,
        // C-5044: Split procedure input into ID (UUID) and Name (custom string)
        procedureId:
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            values.case.procedureId
          )
            ? values.case.procedureId
            : null,
        procedureName:
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            values.case.procedureId
          )
            ? null
            : values.case.procedureId || null,
      };

      // Sanitize specimens - ensure array fields are arrays, not empty strings
      const sanitizedSpecimens = values.case?.specimens?.map(
        (specimen: any) => ({
          ...specimen,
          // UI-only identity keeps child component state attached through reordering.
          uiKey: undefined,
          // Convert empty strings to undefined for optional fields
          sourceId: specimen.sourceId || undefined,
          subsiteId: specimen.subsiteId || undefined,
          collectionMethod: specimen.collectionMethod || undefined,
          material: specimen.material || undefined,
          findings: specimen.findings || undefined,
          comments: specimen.comments || undefined,
          gross: specimen.gross || undefined,
          grossCodeId: specimen.grossCodeId || undefined,
        })
      );

      // Sanitize patient data - always include it, backend handles partial data
      const sanitizedPatient = {
        ...values.patient,
        dateOfBirth: normalizeAccessioningDateOnly(values.patient.dateOfBirth),
        // Convert empty string to undefined for enum field
        sex: values.patient.sex || undefined,
        // Convert empty strings to undefined for optional fields
        medicalRecordNumber: values.patient.medicalRecordNumber || undefined,
        mobilePhone: values.patient.mobilePhone || undefined,
        homePhone: values.patient.homePhone || undefined,
        workPhone: values.patient.workPhone || undefined,
        email: values.patient.email || undefined,
      };

      return {
        patient: sanitizedPatient,
        case: sanitizedCase,
        specimens:
          allowGrossingIdentifierEdits && !includeGrossingIdentifiers
            ? preservePersistedSpecimenIdentifiers(
                sanitizedSpecimens || [],
                existingSpecimens
              )
            : sanitizedSpecimens,
        slideCompletions: effectiveSlideCompletions,
        manualStains,
        panelSelections,
      };
    },
    [
      effectiveSlideCompletions,
      manualStains,
      panelSelections,
      allowGrossingIdentifierEdits,
      existingSpecimens,
    ]
  );

  const persistCurrentDraftForIdentityClaim = useCallback(async () => {
    if (!caseId) return;
    await prepareForExplicitSave();
    const sanitizedData = sanitizeFormData(formRef.current.values);
    const documents = uploadedFilesRef.current
      .filter((file) => file.s3Key)
      .map((file) => ({
        s3Key: file.s3Key,
        fileName: file.file?.name || file.fileName,
        mimeType: file.file?.type || file.mimeType,
      }));
    await updateCaseMutation.mutateAsync({
      caseId,
      data: {
        patient: sanitizedData.patient,
        case: sanitizedData.case,
        specimens: sanitizedData.specimens,
        documents,
        slideCompletions: sanitizedData.slideCompletions,
        manualStains: sanitizedData.manualStains,
        panelSelections: sanitizedData.panelSelections,
      },
    });
    lastSavedValuesRef.current = serializeFormState();
  }, [
    caseId,
    prepareForExplicitSave,
    sanitizeFormData,
    serializeFormState,
    updateCaseMutation,
  ]);

  const synchronizeCanonicalExternalIdentity = useCallback(
    async (purpose: "completion" | "field-blur" | "printing") => {
      if (!caseId) return undefined;
      const externalCaseNumber =
        formRef.current.values.case.externalCaseNumber?.trim() || null;
      const prefixCode = effectivePrefixRef.current;
      const needsExternalBeforePrinting =
        requiresCanonicalExternalCaseNumberBeforeDraftPrinting(
          derivedCaseTypeRef.current,
          prefixCode
        );
      if (
        purpose === "printing" &&
        needsExternalBeforePrinting &&
        !externalCaseNumber
      ) {
        const message =
          "Enter and verify the External Case Number before printing labels for this case.";
        formRef.current.setFieldError("case.externalCaseNumber", message);
        setCanonicalExternalClaimState("conflict");
        notifications.show({
          title: "External Case Number Required",
          message,
          color: "red",
        });
        return undefined;
      }
      setCanonicalExternalClaimState("claiming");
      try {
        await persistCurrentDraftForIdentityClaim();
        const result = await canonicalExternalMutation.mutateAsync({
          caseId,
          externalCaseNumber,
          prefixCode,
        });
        applyCaseNumberContext(result.caseNumber);
        formRef.current.setFieldValue(
          "case.externalCaseNumber",
          result.externalCaseNumber || ""
        );
        formRef.current.clearFieldError("case.externalCaseNumber");
        setCanonicalExternalClaimState(
          result.status === "claimed" ? "verified" : "idle"
        );
        if (result.staleMaterialTypes.length > 0) {
          notifications.show({
            title: "Reprint Labels",
            message: `The case number changed. Reprint ${result.staleMaterialTypes.join(
              " and "
            )} with ${result.caseNumber}.`,
            color: "orange",
            autoClose: false,
          });
        }
        if (result.displacedDraft?.staleMaterialTypes.length) {
          notifications.show({
            title: "Another Draft Was Renumbered",
            message: `The draft previously numbered ${
              result.displacedDraft.previousCaseNumber
            } is now ${
              result.displacedDraft.newCaseNumber
            }. Reprint its ${result.displacedDraft.staleMaterialTypes.join(
              " and "
            )} using the new number.`,
            color: "orange",
            autoClose: false,
          });
        }
        return result;
      } catch (error) {
        const message =
          error instanceof CanonicalExternalCaseNumberError
            ? error.message
            : "The External Case Number could not be verified. Try again.";
        formRef.current.setFieldError("case.externalCaseNumber", message);
        setCanonicalExternalClaimState("conflict");
        notifications.show({
          title: "External Case Number Not Verified",
          message,
          color: "red",
          autoClose: false,
        });
        return undefined;
      }
    },
    [
      applyCaseNumberContext,
      canonicalExternalMutation,
      caseId,
      persistCurrentDraftForIdentityClaim,
    ]
  );

  const handleExternalCaseNumberBlur = useCallback(() => {
    void synchronizeCanonicalExternalIdentity("field-blur");
  }, [synchronizeCanonicalExternalIdentity]);

  // Emergency flush for debounced save on unmount or tab close
  const flushAutoSave = useCallback(() => {
    // Only flush if there is a pending save
    if (!autoSaveTimeoutRef.current || !caseId) return;

    // Clear the timeout to prevent duplicate saves
    clearAutoSaveTimeout();

    // Use fetch with keepalive=true to ensure the save completes even if the tab is closing.
    // We use the refs to ensure we have the most recent data regardless of when the listener was registered.
    const values = formValuesRef.current;
    const files = uploadedFilesRef.current;
    const sanitizedData = sanitizeFormData(values);

    const allDocs = files
      .filter((f) => f.s3Key)
      .map((f) => ({
        s3Key: f.s3Key,
        fileName: f.file?.name || f.fileName,
        mimeType: f.file?.type || f.mimeType,
      }));

    const token = localStorage.getItem("token");
    const apiBase = `${process.env.REACT_APP_API_BASE_URL || ""}/lis`;

    fetch(`${apiBase}/workflows/accessioning/${caseId}`, {
      method: "PUT",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        patient: sanitizedData.patient,
        case: sanitizedData.case,
        specimens: sanitizedData.specimens,
        documents: allDocs,
        slideCompletions: sanitizedData.slideCompletions,
        manualStains: sanitizedData.manualStains,
        panelSelections: sanitizedData.panelSelections,
      }),
    });
  }, [caseId, clearAutoSaveTimeout, sanitizeFormData]);

  // Update committed findings for a specimen on blur — drives stain preview refresh
  const handleSpecimenFindingsBlur = useCallback(
    (index: number) => {
      const findings = form.values.case?.specimens?.[index]?.findings || "";
      setCommittedFindings((prev) => ({ ...prev, [index]: findings }));
    },
    [form]
  );

  // Keep a stable ref to the latest flushAutoSave to avoid tearing down the unload effect on every keystroke
  const flushAutoSaveRef = useRef(flushAutoSave);
  flushAutoSaveRef.current = flushAutoSave;

  // Emergency flush on tab close or navigation away
  useEffect(() => {
    if (!caseId) return;

    const handleUnload = () => {
      if (autoSaveTimeoutRef.current) {
        flushAutoSaveRef.current();
      }
    };

    window.addEventListener("pagehide", handleUnload);
    return () => {
      window.removeEventListener("pagehide", handleUnload);
      // Also flush on component unmount
      handleUnload();
    };
  }, [caseId]);

  // Auto-save effect - debounced save when form values change
  useEffect(() => {
    // Don't auto-save if no case or still loading
    if (!caseId || caseCreationState !== "ready") return;

    // Don't auto-save during extraction or manual save
    if (isExtracting || isSaving) return;

    // Serialize current values for comparison (include uploaded files, completions, stains)
    const currentValues = serializeFormState();

    // Skip if values haven't changed
    if (currentValues === lastSavedValuesRef.current) return;

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce: save after 2 seconds of no changes
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      setAutoSaveStatus("saving");
      const sanitizedData = sanitizeFormData(form.values);
      const allDocs = uploadedFiles
        .filter((f) => f.s3Key)
        .map((f) => ({
          s3Key: f.s3Key,
          fileName: f.file?.name || f.fileName,
          mimeType: f.file?.type || f.mimeType,
        }));
      queueAutoSave({
        caseId,
        serializedValues: currentValues,
        data: {
          patient: sanitizedData.patient,
          case: sanitizedData.case,
          specimens: sanitizedData.specimens,
          documents: allDocs,
          slideCompletions: sanitizedData.slideCompletions,
          manualStains: sanitizedData.manualStains,
          panelSelections: sanitizedData.panelSelections,
        },
      });
    }, 2000);

    return clearAutoSaveTimeout;
  }, [
    form.values,
    caseId,
    caseCreationState,
    isExtracting,
    isSaving,
    uploadedFiles,
    clearAutoSaveTimeout,
    queueAutoSave,
    sanitizeFormData,
    serializeFormState,
  ]);

  // Check if left sidebar needs scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      const scrollContainer = document.querySelector(".left-sidebar-scroll");
      if (scrollContainer) {
        const hasScroll =
          scrollContainer.scrollHeight > scrollContainer.clientHeight;
        const isAtBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - 10;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      }
    };

    // Check initially and on scroll/resize
    checkScrollable();
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollable);
    }
    window.addEventListener("resize", checkScrollable);

    // Re-check periodically for content changes (e.g., collapsing cards)
    const interval = setInterval(checkScrollable, 500);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
      clearInterval(interval);
    };
  }, []);

  // Apply a single step's result to the form, skipping user-touched fields.
  const applyStepResult = useCallback(
    (stepKey: string, result: unknown) => {
      const form = formRef.current;
      const r = result as any;
      const newCertainties: Record<string, number> = {};
      const newUnmatched: Record<string, string> = {};
      const applyField = (
        path: string,
        data: { value: any; certainty?: number } | undefined | null
      ) => {
        if (!data?.value || form.isDirty(path)) return;
        form.setFieldValue(path, data.value);
        if (data.certainty != null) newCertainties[path] = data.certainty;
      };
      switch (stepKey) {
        case "A": {
          const p = r?.patientInformation;
          if (!p) return;
          applyField("patient.firstName", p.firstName);
          applyField("patient.lastName", p.lastName);
          if (p.dateOfBirth?.value && !form.isDirty("patient.dateOfBirth")) {
            const dateOfBirth = parseDateOnly(p.dateOfBirth.value);
            if (dateOfBirth) {
              form.setFieldValue("patient.dateOfBirth", dateOfBirth);
              if (p.dateOfBirth.certainty != null)
                newCertainties["patient.dateOfBirth"] = p.dateOfBirth.certainty;
            }
          }
          applyField("patient.sex", p.sex);
          applyField("patient.medicalRecordNumber", p.medicalRecordNumber);
          applyField("patient.mobilePhone", p.mobilePhone);
          applyField("patient.homePhone", p.homePhone);
          applyField("patient.workPhone", p.workPhone);
          applyField("patient.email", p.email);
          applyField("patient.address.address", p.address?.address);
          applyField("patient.address.zipCode", p.address?.zipCode);
          // Apply matched address state/city (dropdown fields — only from matching)
          const mA = r?.matched?.patientAddress;
          if (mA?.stateCode && !form.isDirty("patient.address.state")) {
            form.setFieldValue("patient.address.state", mA.stateCode);
            if (mA.stateConfidence != null)
              newCertainties["patient.address.state"] = mA.stateConfidence;
          }
          if (mA?.cityName && !form.isDirty("patient.address.city")) {
            form.setFieldValue("patient.address.city", mA.cityName);
            if (mA.cityConfidence != null)
              newCertainties["patient.address.city"] = mA.cityConfidence;
          }
          break;
        }
        case "D": {
          const cf = r?.consultationFields;
          if (!cf) return;
          applyField("case.consultationReason", cf.consultationReason);
          applyField(
            "case.consultationOriginalDiagnosis",
            cf.consultationOriginalDiagnosis
          );
          applyField("case.requestingPathologist", cf.requestingPathologist);
          break;
        }
        case "F": {
          applyField("case.externalCaseNumber", r?.externalCaseNumber);
          if (r?.dateCollected?.value && !form.isDirty("case.dateCollected")) {
            const dateCollected = parseDateOnly(r.dateCollected.value);
            if (dateCollected) {
              form.setFieldValue("case.dateCollected", dateCollected);
              if (r.dateCollected.certainty != null)
                newCertainties["case.dateCollected"] =
                  r.dateCollected.certainty;
            }
          }
          applyField("case.clinicalHistoryNotes", r?.clinicalHistoryNotes);
          applyField("case.indications", r?.indications);
          applyField("case.impressions", r?.impressions);
          // Normalize ruleOuts: join if array, otherwise use as-is (applyField handles null/empty)
          const ruleOuts = r?.ruleOuts;
          if (Array.isArray(ruleOuts?.value)) {
            ruleOuts.value = ruleOuts.value.join(", ");
          }
          applyField("case.ruleOuts", ruleOuts);
          // Apply matched ICD codes
          const mF = r?.matched;
          if (
            mF?.clinicalHistoryCodeIds?.length > 0 &&
            !form.isDirty("case.clinicalHistoryCodes")
          ) {
            form.setFieldValue(
              "case.clinicalHistoryCodes",
              mF.clinicalHistoryCodeIds
            );
            if (r?.clinicalHistoryCodes?.certainty != null) {
              newCertainties["case.clinicalHistoryCodes"] =
                r.clinicalHistoryCodes.certainty;
            }
          } else if (
            mF?.unmatchedIcdCodes?.length > 0 &&
            !form.isDirty("case.clinicalHistoryCodes")
          ) {
            newUnmatched["case.unmatchedIcdCodes"] =
              mF.unmatchedIcdCodes.join(", ");
          }
          break;
        }
        case "I": {
          applyField("case.shippingPathologist", r?.externalPathologistName);
          const addr = r?.shippingAddress;
          if (addr) {
            applyField("case.shippingStreet", addr.street);
            applyField("case.shippingCity", addr.city);
            applyField("case.shippingState", addr.state);
            applyField("case.shippingZipCode", addr.zipCode);
          }
          break;
        }
        case "K": {
          const signals = readExtractionSignalFlags(r);
          if (!signals) return;
          applyExtractionSignalFlags(signals);
          break;
        }
        case "J": {
          const specimens = r?.specimenInformation;
          if (!Array.isArray(specimens) || specimens.length === 0) return;
          const currentSpecimens = form.getValues().case.specimens;
          if (
            !areSpecimensUnchangedSincePopulation(
              currentSpecimens,
              specimenPopulationSnapshotRef.current
            )
          )
            return;
          const matchedSpecimens: any[] = r?.matched?.specimens ?? [];
          const rows = specimens.map(
            (
              spec: {
                findings?: { value?: string; certainty?: number };
                material?: { value?: string; certainty?: number };
                grossDescription?: { value?: string; certainty?: number };
              },
              idx: number
            ) => {
              const m = matchedSpecimens[idx] ?? {};
              // Fallback collection method: if backend didn't match, use source default
              let collectionMethod = m.collectionMethodName || "";
              if (!collectionMethod && m.sourceId && sourcesData?.sources) {
                const matchedSource = sourcesData.sources.find(
                  (s: {
                    value: string;
                    defaultCollectionMethodName?: string;
                  }) => s.value === m.sourceId
                );
                if (matchedSource?.defaultCollectionMethodName) {
                  collectionMethod = matchedSource.defaultCollectionMethodName;
                }
              }
              return {
                number: String.fromCharCode(65 + idx),
                findings: spec.findings?.value || "",
                gross: m.grossDescription || spec.grossDescription?.value || "",
                sourceId: m.sourceId || "",
                subsiteId: m.subsiteId || null,
                collectionMethod,
                comments: "",
                extractedStainCodes: "",
                matchedStainIds: m.matchedStainIds || [],
                extractedGrossCodePrefix: "",
                extractedBlockIdentifiers: "",
                extractedDimensions: "",
                // Material is detected per specimen by AI extraction (Step J) and drives the
                // derived case type — no longer inferred from a (now-removed) case-type field.
                material: applyPassThroughMaterialDefault(
                  effectivePrefixRef.current,
                  spec.material?.value
                ),
                grossDescriptionConfidence:
                  m.grossDescriptionConfidence ??
                  spec.grossDescription?.certainty ??
                  null,
                grossDescriptionSource:
                  m.grossDescription || spec.grossDescription?.value
                    ? "ai_extracted"
                    : null,
                grossCodeId: m.grossCodeId || null,
                grossTemplate: "",
                blockCount: 1,
              };
            }
          );
          applyPopulatedSpecimens(rows);
          const extractedCommitted: Record<number, string> = {};
          rows.forEach((row, idx) => {
            extractedCommitted[idx] = row.findings;
            if (specimens[idx]?.findings?.certainty != null) {
              newCertainties[`case.specimens.${idx}.findings`] =
                specimens[idx].findings.certainty;
            }
            if (specimens[idx]?.material?.certainty != null) {
              newCertainties[`case.specimens.${idx}.material`] =
                specimens[idx].material.certainty;
            }
            if (matchedSpecimens[idx]?.sourceConfidence != null) {
              newCertainties[`case.specimens.${idx}.sourceId`] =
                matchedSpecimens[idx].sourceConfidence;
            }
            if (matchedSpecimens[idx]?.subsiteConfidence != null) {
              newCertainties[`case.specimens.${idx}.subsiteId`] =
                matchedSpecimens[idx].subsiteConfidence;
            }
            if (
              !rows[idx].collectionMethod &&
              specimens[idx]?.collectionMethod?.value
            ) {
              newUnmatched[`case.specimens.${idx}.collectionMethod`] =
                specimens[idx].collectionMethod.value;
            }
          });
          setCommittedFindings(extractedCommitted);
          break;
        }
        case "C": {
          const primaryIns = r?.insuranceInformation?.primaryInsurance;
          const secondaryIns = r?.insuranceInformation?.secondaryInsurance;
          const m = r?.matched;

          // Apply raw extraction fields for primary insurance
          if (
            primaryIns?.phoneNumber?.value &&
            !form.isDirty("case.primaryInsurance.phoneNumber")
          ) {
            form.setFieldValue(
              "case.primaryInsurance.phoneNumber",
              primaryIns.phoneNumber.value
            );
            if (primaryIns.phoneNumber.certainty != null) {
              newCertainties["case.primaryInsurance.phoneNumber"] =
                primaryIns.phoneNumber.certainty;
            }
          }
          applyField(
            "case.primaryInsurance.policyNumber",
            primaryIns?.policyNumber
          );
          applyField(
            "case.primaryInsurance.groupNumber",
            primaryIns?.groupNumber
          );
          applyField("case.primaryInsurance.groupName", primaryIns?.groupName);
          applyField(
            "case.primaryInsurance.authorizationNumber",
            primaryIns?.authorizationNumber
          );
          applyField(
            "case.primaryInsurance.patientRelationToInsured",
            primaryIns?.patientRelationToInsured
          );

          // Apply address fields for primary insurance
          if (primaryIns?.address) {
            applyField(
              "case.primaryInsurance.address.address",
              primaryIns.address.address
            );
            applyField(
              "case.primaryInsurance.address.zipCode",
              primaryIns.address.zipCode
            );
          }

          // Match and fallback for primary insurance provider
          const primaryProviderId = m?.primaryInsurance?.providerId;
          if (
            primaryProviderId &&
            !form.isDirty("case.primaryInsurance.insuranceProvider")
          ) {
            form.setFieldValue(
              "case.primaryInsurance.insuranceProvider",
              primaryProviderId
            );
            newCertainties["case.primaryInsurance.insuranceProvider"] =
              m.primaryInsurance.confidence ?? 0;

            // Show notification based on match method
            if (m.primaryInsurance.requiresManualSelection) {
              notifications.show({
                title: "Manual Selection Required",
                message:
                  "Insurance provider could not be matched automatically. Please select manually.",
                color: "yellow",
              });
            } else {
              notifications.show({
                title: "Insurance Provider Matched",
                message: `${
                  m.primaryInsurance.message || "Fuzzy match found"
                } (${m.primaryInsurance.matchMethod || "fuzzy-name"})`,
                color: "green",
              });
            }
          } else if (primaryIns?.insuranceProvider?.value) {
            newUnmatched["case.primaryInsurance.insuranceProvider"] =
              primaryIns.insuranceProvider.value;
          }

          // Match and fallback for primary insurance plan type
          const primaryPlanTypeId = m?.primaryPlanType?.planTypeId;
          if (
            primaryPlanTypeId &&
            !form.isDirty("case.primaryInsurance.planType")
          ) {
            form.setFieldValue(
              "case.primaryInsurance.planType",
              primaryPlanTypeId
            );
            newCertainties["case.primaryInsurance.planType"] =
              m.primaryPlanType.confidence ?? 0;

            // Show notification based on match result
            if (m.primaryPlanType.requiresManualSelection) {
              notifications.show({
                title: "Plan Type: Manual Review Required",
                message:
                  m.primaryPlanType.message ||
                  "Low confidence match - please review",
                color: "yellow",
              });
            } else {
              notifications.show({
                title: "Plan Type Matched",
                message: `${m.primaryPlanType.planTypeName || "Plan type"} (${
                  m.primaryPlanType.confidence || 0
                }% confidence)`,
                color: "green",
              });
            }
          } else if (primaryIns?.planType?.value) {
            newUnmatched["case.primaryInsurance.planType"] =
              primaryIns.planType.value;
          }

          // Insurance address state/city (matched only)
          if (
            m?.primaryInsuranceAddress?.stateCode &&
            !form.isDirty("case.primaryInsurance.address.state")
          ) {
            form.setFieldValue(
              "case.primaryInsurance.address.state",
              m.primaryInsuranceAddress.stateCode
            );
            if (m.primaryInsuranceAddress.stateConfidence != null) {
              newCertainties["case.primaryInsurance.address.state"] =
                m.primaryInsuranceAddress.stateConfidence;
            }
          }
          if (
            m?.primaryInsuranceAddress?.cityName &&
            !form.isDirty("case.primaryInsurance.address.city")
          ) {
            form.setFieldValue(
              "case.primaryInsurance.address.city",
              m.primaryInsuranceAddress.cityName
            );
            if (m.primaryInsuranceAddress.cityConfidence != null) {
              newCertainties["case.primaryInsurance.address.city"] =
                m.primaryInsuranceAddress.cityConfidence;
            }
          }

          // Apply raw extraction fields for secondary insurance
          if (
            secondaryIns &&
            typeof secondaryIns === "object" &&
            "insuranceProvider" in secondaryIns
          ) {
            if (
              secondaryIns.phoneNumber?.value &&
              !form.isDirty("case.secondaryInsurance.phoneNumber")
            ) {
              form.setFieldValue(
                "case.secondaryInsurance.phoneNumber",
                secondaryIns.phoneNumber.value
              );
              if (secondaryIns.phoneNumber?.certainty != null) {
                newCertainties["case.secondaryInsurance.phoneNumber"] =
                  secondaryIns.phoneNumber.certainty;
              }
            }
            applyField(
              "case.secondaryInsurance.policyNumber",
              secondaryIns.policyNumber
            );
            applyField(
              "case.secondaryInsurance.groupNumber",
              secondaryIns.groupNumber
            );
            applyField(
              "case.secondaryInsurance.groupName",
              secondaryIns.groupName
            );
            applyField(
              "case.secondaryInsurance.authorizationNumber",
              secondaryIns.authorizationNumber
            );
            applyField(
              "case.secondaryInsurance.patientRelationToInsured",
              secondaryIns.patientRelationToInsured
            );

            // Apply address fields for secondary insurance
            if (secondaryIns.address) {
              applyField(
                "case.secondaryInsurance.address.address",
                secondaryIns.address.address
              );
              applyField(
                "case.secondaryInsurance.address.zipCode",
                secondaryIns.address.zipCode
              );
            }
          }

          // Match and fallback for secondary insurance provider
          const secondaryProviderId = m?.secondaryInsurance?.providerId;
          if (
            secondaryProviderId &&
            !form.isDirty("case.secondaryInsurance.insuranceProvider")
          ) {
            form.setFieldValue(
              "case.secondaryInsurance.insuranceProvider",
              secondaryProviderId
            );
            newCertainties["case.secondaryInsurance.insuranceProvider"] =
              m.secondaryInsurance.confidence ?? 0;

            // Show notification based on match method
            if (m.secondaryInsurance.requiresManualSelection) {
              notifications.show({
                title: "Secondary Insurance: Manual Selection Required",
                message:
                  "Secondary insurance provider could not be matched automatically.",
                color: "yellow",
              });
            } else {
              notifications.show({
                title: "Secondary Insurance Matched",
                message: `${
                  m.secondaryInsurance.message || "Fuzzy match found"
                }`,
                color: "green",
              });
            }
          } else if (secondaryIns?.insuranceProvider?.value) {
            newUnmatched["case.secondaryInsurance.insuranceProvider"] =
              secondaryIns.insuranceProvider.value;
          }

          // Match and fallback for secondary insurance plan type
          const secondaryPlanTypeId = m?.secondaryPlanType?.planTypeId;
          if (
            secondaryPlanTypeId &&
            !form.isDirty("case.secondaryInsurance.planType")
          ) {
            form.setFieldValue(
              "case.secondaryInsurance.planType",
              secondaryPlanTypeId
            );
            newCertainties["case.secondaryInsurance.planType"] =
              m.secondaryPlanType.confidence ?? 0;

            // Show notification based on match result
            if (m.secondaryPlanType.requiresManualSelection) {
              notifications.show({
                title: "Secondary Plan Type: Manual Review Required",
                message:
                  m.secondaryPlanType.message ||
                  "Low confidence match - please review",
                color: "yellow",
              });
            } else {
              notifications.show({
                title: "Secondary Plan Type Matched",
                message: `${m.secondaryPlanType.planTypeName || "Plan type"} (${
                  m.secondaryPlanType.confidence || 0
                }% confidence)`,
                color: "green",
              });
            }
          } else if (secondaryIns?.planType?.value) {
            newUnmatched["case.secondaryInsurance.planType"] =
              secondaryIns.planType.value;
          }

          // Insurance address state/city (matched only)
          if (
            m?.secondaryInsuranceAddress?.stateCode &&
            !form.isDirty("case.secondaryInsurance.address.state")
          ) {
            form.setFieldValue(
              "case.secondaryInsurance.address.state",
              m.secondaryInsuranceAddress.stateCode
            );
            if (m.secondaryInsuranceAddress.stateConfidence != null) {
              newCertainties["case.secondaryInsurance.address.state"] =
                m.secondaryInsuranceAddress.stateConfidence;
            }
          }
          if (
            m?.secondaryInsuranceAddress?.cityName &&
            !form.isDirty("case.secondaryInsurance.address.city")
          ) {
            form.setFieldValue(
              "case.secondaryInsurance.address.city",
              m.secondaryInsuranceAddress.cityName
            );
            if (m.secondaryInsuranceAddress.cityConfidence != null) {
              newCertainties["case.secondaryInsurance.address.city"] =
                m.secondaryInsuranceAddress.cityConfidence;
            }
          }
          break;
        }
        case "E": {
          const m = r?.matched;
          if (!m) break;
          const isPracticeLabDirty =
            form.isDirty("case.accountId") ||
            form.isDirty("case.submittingLabId");
          if (m.submittingLabId && !isPracticeLabDirty) {
            form.setFieldValue("case.submittingLabId", m.submittingLabId);
            form.setFieldValue("case.accountId", "");
            if (r?.submittingLab?.certainty != null) {
              newCertainties["case.accountId"] = r.submittingLab.certainty;
            }
          } else if (
            r?.submittingLab?.value &&
            !isPracticeLabDirty &&
            !form.values.case?.submittingLabId
          ) {
            newUnmatched["case.submittingLabId"] = r.submittingLab.value;
          } else if (m.practiceId && !isPracticeLabDirty) {
            form.setFieldValue("case.accountId", m.practiceId);
            form.setFieldValue("case.submittingLabId", "");
            if (r?.practice?.certainty != null) {
              newCertainties["case.accountId"] = r.practice.certainty;
            }
          } else if (
            !m.practiceId &&
            r?.practice?.value &&
            !isPracticeLabDirty &&
            !form.values.case?.accountId
          ) {
            newUnmatched["case.accountId"] = r.practice.value;
          }
          if (m.physicianId && !form.isDirty("case.physicianId")) {
            form.setFieldValue("case.physicianId", m.physicianId);
            if (r?.clientPhysician?.certainty != null) {
              newCertainties["case.physicianId"] = r.clientPhysician.certainty;
            }
          } else if (
            !m.physicianId &&
            r?.clientPhysician?.value &&
            !form.isDirty("case.physicianId") &&
            !form.values.case?.physicianId
          ) {
            newUnmatched["case.physicianId"] = r.clientPhysician.value;
          }
          if (m.surgicalCenterId && !form.isDirty("case.locationId")) {
            form.setFieldValue("case.locationId", m.surgicalCenterId);
            if (r?.clientLocation?.certainty != null) {
              newCertainties["case.locationId"] = r.clientLocation.certainty;
            }
          } else if (
            !m.surgicalCenterId &&
            r?.clientLocation?.value &&
            !form.isDirty("case.locationId") &&
            !form.values.case?.locationId
          ) {
            newUnmatched["case.locationId"] = r.clientLocation.value;
          }
          if (m.procedureId && !form.isDirty("case.procedureId")) {
            form.setFieldValue("case.procedureId", m.procedureId);
            if (r?.procedureName?.certainty != null) {
              newCertainties["case.procedureId"] = r.procedureName.certainty;
            }
          } else if (
            m.unmatchedProcedureName &&
            !form.isDirty("case.procedureId") &&
            !form.values.case?.procedureId
          ) {
            newUnmatched["case.procedureId"] = m.unmatchedProcedureName;
          }
          break;
        }
        case "H": {
          const m = r?.matched;
          if (!m) break;
          if (
            m.originatingPracticeId &&
            !form.isDirty("case.originatingPractice")
          ) {
            form.setFieldValue(
              "case.originatingPractice",
              m.originatingPracticeId
            );
            if (r?.originatingPractice?.certainty != null) {
              newCertainties["case.originatingPractice"] =
                r.originatingPractice.certainty;
            }
          }
          if (
            m.originatingPhysicianId &&
            !form.isDirty("case.originatingPhysician")
          ) {
            form.setFieldValue(
              "case.originatingPhysician",
              m.originatingPhysicianId
            );
            if (r?.originatingPhysician?.certainty != null) {
              newCertainties["case.originatingPhysician"] =
                r.originatingPhysician.certainty;
            }
          }
          if (
            m.originatingSurgicalCenterId &&
            !form.isDirty("case.originatingLocation")
          ) {
            form.setFieldValue(
              "case.originatingLocation",
              m.originatingSurgicalCenterId
            );
            if (r?.originatingLocation?.certainty != null) {
              newCertainties["case.originatingLocation"] =
                r.originatingLocation.certainty;
            }
          }
          break;
        }
        default:
          break;
      }
      if (Object.keys(newCertainties).length > 0) {
        setCertainties((prev) => ({ ...prev, ...newCertainties }));
      }
      if (Object.keys(newUnmatched).length > 0) {
        setUnmatchedExtractions((prev) => ({ ...prev, ...newUnmatched }));
      }
      // Re-run prefix lookup since extraction may have populated practice/physician/insurance
      debouncedPrefixLookup();
    },
    [
      sourcesData,
      setCertainties,
      setCommittedFindings,
      setUnmatchedExtractions,
      debouncedPrefixLookup,
      applyExtractionSignalFlags,
      applyPopulatedSpecimens,
    ]
  );

  // Poll for extraction job status
  const pollExtractionStatus = useCallback(
    async (jobId: string) => {
      if (extractionPollInFlightJobRef.current === jobId) return;
      extractionPollInFlightJobRef.current = jobId;
      const form = formRef.current;
      try {
        const job = await accessioningService.getExtractionStatus(jobId);
        if (
          !isCurrentExtractionStatusJob(
            extractionPollingStateRef.current,
            jobId
          )
        )
          return;
        extractionPollingStateRef.current = recordExtractionStatusSuccess(
          extractionPollingStateRef.current
        );

        setExtractionProgress(job.progress);

        // Sync per-step state for the progress UI
        if (job.steps && Object.keys(job.steps).length > 0) {
          setExtractionSteps(job.steps);
        }
        if (job.currentPhase) {
          setExtractionCurrentPhase(job.currentPhase);
        }

        // Apply newly completed steps progressively (before full-job completion)
        if (job.steps) {
          Object.entries(job.steps).forEach(([stepKey, stepState]) => {
            if (
              stepState.status === "completed" &&
              !lastAppliedSteps.current.has(stepKey)
            ) {
              lastAppliedSteps.current.add(stepKey);
              applyStepResult(stepKey, stepState.result);
            }
          });
        }

        if (job.status === ("completed" as ExtractionJobStatus)) {
          const failedStepKeys = getFailedExtractionStepKeys(job.steps);

          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          setIsExtracting(false);
          setIsExtractionStatusUnavailable(false);

          if (job.result) {
            // Form is already populated progressively by applyStepResult as steps complete.
            // Apply final matchedData here to capture end-of-job refinements (like specimen-site inference)
            // that may have changed after Step E completed with null/fallback.
            const finalSignals = readExtractionSignalFlags(job.result);
            if (finalSignals) {
              applyExtractionSignalFlags(finalSignals);
            }

            const m = (job.result as any).matchedData;
            if (m) {
              const isPracticeLabDirty =
                form.isDirty("case.accountId") ||
                form.isDirty("case.submittingLabId");
              if (m.submittingLabId && !isPracticeLabDirty) {
                form.setFieldValue("case.accountId", "");
                form.setFieldValue("case.submittingLabId", m.submittingLabId);
              } else if (m.practiceId && !isPracticeLabDirty) {
                form.setFieldValue("case.accountId", m.practiceId);
                form.setFieldValue("case.submittingLabId", "");
              }
              if (m.surgicalCenterId && !form.isDirty("case.locationId")) {
                form.setFieldValue("case.locationId", m.surgicalCenterId);
              }
              if (m.physicianId && !form.isDirty("case.physicianId")) {
                form.setFieldValue("case.physicianId", m.physicianId);
              }
              if (m.procedureId && !form.isDirty("case.procedureId")) {
                form.setFieldValue("case.procedureId", m.procedureId);
                if (
                  job.result.caseInformation?.procedureName?.certainty != null
                ) {
                  setCertainties((prev) => ({
                    ...prev,
                    "case.procedureId":
                      job.result!.caseInformation.procedureName.certainty,
                  }));
                }
              } else if (
                m.unmatchedProcedureName &&
                !form.isDirty("case.procedureId") &&
                !form.values.case?.procedureId
              ) {
                setUnmatchedExtractions((prev) => ({
                  ...prev,
                  "case.procedureId": m.unmatchedProcedureName!,
                }));
              }
            }

            // Surface cross-document validation mismatches
            const crossDoc = (job.result as any).crossDocumentValidation;
            if (crossDoc?.mismatches?.length > 0) {
              setCrossDocMismatches(crossDoc.mismatches);
            } else {
              setCrossDocMismatches([]);
            }

            // Validate patient address via SmartyStreets after extraction
            const patientAddress = job.result.patientInformation?.address;
            if (
              patientAddress?.address?.value &&
              patientAddress?.city?.value &&
              patientAddress?.state?.value
            ) {
              try {
                const validatedAddr = await validateAddress({
                  address: patientAddress.address.value,
                  city: patientAddress.city.value,
                  state: patientAddress.state.value,
                  zipCode: patientAddress.zipCode?.value || "",
                });
                if (
                  !isCurrentExtractionStatusJob(
                    extractionPollingStateRef.current,
                    jobId
                  )
                )
                  return;

                if (validatedAddr.isValid) {
                  // Update form with validated/standardized address
                  form.setFieldValue(
                    "patient.address.address",
                    validatedAddr.address
                  );
                  form.setFieldValue(
                    "patient.address.city",
                    validatedAddr.city
                  );
                  form.setFieldValue(
                    "patient.address.state",
                    validatedAddr.state
                  );
                  form.setFieldValue(
                    "patient.address.zipCode",
                    validatedAddr.zipCode || ""
                  );
                  // Set certainty to 100% for validated address
                  setCertainties((prev) => ({
                    ...prev,
                    "patient.address.address": 100,
                    "patient.address.city": 100,
                    "patient.address.state": 100,
                    "patient.address.zipCode": 100,
                  }));
                  notifications.show({
                    title: "Address Validated",
                    message:
                      validatedAddr.validationMessage ||
                      "Address has been standardized via USPS validation",
                    color: "green",
                  });
                } else {
                  // Show warning for invalid address
                  notifications.show({
                    title: "Address Validation Warning",
                    message:
                      validatedAddr.validationMessage ||
                      "Could not validate patient address",
                    color: "yellow",
                  });
                }
              } catch {
                if (
                  !isCurrentExtractionStatusJob(
                    extractionPollingStateRef.current,
                    jobId
                  )
                )
                  return;
                // Address validation is optional, don't block extraction
              }
            }

            // Validate shipping address via SmartyStreets after extraction (Full TC)
            const shippingAddr = job.result.caseInformation?.shippingAddress;
            if (
              shippingAddr?.street?.value &&
              shippingAddr?.city?.value &&
              shippingAddr?.state?.value
            ) {
              try {
                const validatedShipping = await validateAddress({
                  address: shippingAddr.street.value,
                  city: shippingAddr.city.value,
                  state: shippingAddr.state.value,
                  zipCode: shippingAddr.zipCode?.value || "",
                });
                if (
                  !isCurrentExtractionStatusJob(
                    extractionPollingStateRef.current,
                    jobId
                  )
                )
                  return;

                if (validatedShipping.isValid) {
                  form.setFieldValue(
                    "case.shippingStreet",
                    validatedShipping.address
                  );
                  form.setFieldValue(
                    "case.shippingCity",
                    validatedShipping.city
                  );
                  form.setFieldValue(
                    "case.shippingState",
                    validatedShipping.state
                  );
                  form.setFieldValue(
                    "case.shippingZipCode",
                    validatedShipping.zipCode || ""
                  );
                  setCertainties((prev) => ({
                    ...prev,
                    "case.shippingStreet": 100,
                    "case.shippingCity": 100,
                    "case.shippingState": 100,
                    "case.shippingZipCode": 100,
                  }));
                  notifications.show({
                    title: "Shipping Address Validated",
                    message:
                      validatedShipping.validationMessage ||
                      "Shipping address standardized via USPS validation",
                    color: "green",
                  });
                } else {
                  notifications.show({
                    title: "Shipping Address Warning",
                    message:
                      validatedShipping.validationMessage ||
                      "Could not validate shipping address",
                    color: "yellow",
                  });
                }
              } catch {
                if (
                  !isCurrentExtractionStatusJob(
                    extractionPollingStateRef.current,
                    jobId
                  )
                )
                  return;
                // Shipping address validation is optional
              }
            }
          }

          if (job.priorityResult) {
            const priorityResult = job.priorityResult;
            // Rush - physical stamp detection
            form.setFieldValue("case.isRush", priorityResult.isRush);
            form.setFieldValue(
              "case.rushReason",
              priorityResult.rushReason || ""
            );
            // High Priority - clinical keyword detection with specimen attribution
            form.setFieldValue(
              "case.isHighPriority",
              priorityResult.isHighPriority
            );
            form.setFieldValue(
              "case.highPriorityReason",
              priorityResult.highPriorityReason || ""
            );
            setHighPriorityTriggers(priorityResult.highPriorityTriggers || []);
            setCertainties((prev) => ({
              ...prev,
              "case.isRush": priorityResult.rushCertainty,
              "case.isHighPriority": priorityResult.highPriorityCertainty,
            }));
          }

          // Z-prefix ICD-10 safety net: warn if high priority was flagged
          // but all extracted ICD-10 codes are screening (Z-prefix) codes
          if (
            job.priorityResult?.isHighPriority &&
            job.result?.caseInformation?.clinicalHistoryCodes?.value
          ) {
            const rawCodes =
              job.result.caseInformation.clinicalHistoryCodes.value;
            const codeList: string[] = Array.isArray(rawCodes)
              ? rawCodes
              : typeof rawCodes === "string"
              ? rawCodes
                  .split(/[,;\s]+/)
                  .filter((c: string) => c.trim().length > 0)
              : [];
            if (
              codeList.length > 0 &&
              codeList.every((code: string) =>
                code.trim().toUpperCase().startsWith("Z")
              )
            ) {
              notifications.show({
                title: "Priority Review Needed",
                message:
                  "High priority was flagged but all ICD-10 codes are Z-prefix (screening). " +
                  "Z-codes indicate screening for healthy patients and typically do not warrant rush processing. " +
                  "Please verify the priority flag is correct.",
                color: "yellow",
                autoClose: false,
              });
            }
          }

          // Work list expected case lookup after extraction
          const extractedExtCaseNum =
            job.result?.caseInformation?.externalCaseNumber?.value;
          if (extractedExtCaseNum) {
            if (
              !isCurrentExtractionStatusJob(
                extractionPollingStateRef.current,
                jobId
              )
            )
              return;
            try {
              await performWorkListLookup(extractedExtCaseNum);
            } catch {
              // Work list lookup is non-blocking
            }
            if (
              !isCurrentExtractionStatusJob(
                extractionPollingStateRef.current,
                jobId
              )
            )
              return;
          }

          if (job.result || job.priorityResult) {
            if (failedStepKeys.length > 0) {
              notifications.show({
                title: "Extraction Incomplete",
                message:
                  "Some sections could not be extracted. Retry the failed sections or enter those details manually.",
                color: "orange",
                autoClose: false,
              });
            } else {
              notifications.show({
                title: "Extraction Complete",
                message: "Data has been extracted and populated",
                color: "green",
              });
            }
          }
        } else if (job.status === ("failed" as ExtractionJobStatus)) {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          setIsExtracting(false);
          setIsExtractionStatusUnavailable(false);
          setExtractionError(job.error || "Extraction failed");
          notifications.show({
            title: "Extraction Failed",
            message: job.error || "Failed to extract data from documents",
            color: "red",
          });
        }
      } catch {
        if (
          !isCurrentExtractionStatusJob(
            extractionPollingStateRef.current,
            jobId
          )
        )
          return;
        const pollState = recordExtractionStatusFailure(
          extractionPollingStateRef.current
        );
        extractionPollingStateRef.current = pollState;
        if (!pollState.statusUnavailable) return;

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsExtracting(false);
        setIsExtractionStatusUnavailable(true);
        setExtractionError(EXTRACTION_STATUS_UNAVAILABLE_MESSAGE);
        notifications.show({
          title: "Extraction status unavailable",
          message: EXTRACTION_STATUS_UNAVAILABLE_MESSAGE,
          color: "red",
        });
      } finally {
        if (extractionPollInFlightJobRef.current === jobId) {
          extractionPollInFlightJobRef.current = null;
        }
      }
    },
    [performWorkListLookup, applyStepResult, applyExtractionSignalFlags]
  );

  // Cleanup polling and auto-save on unmount
  useEffect(() => {
    const extractionRequestGuard = extractionRequestGuardRef.current;
    const pathologistAssignmentGuard = pathologistAssignmentGuardRef.current;
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      extractionRequestGuard.invalidate();
      pathologistAssignmentGuard.invalidate();
      extractionPollingStateRef.current = createExtractionStatusPollingState();
      extractionPollInFlightJobRef.current = null;
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveExecutorRef.current.invalidate();
      if (pathologistDebounceRef.current) {
        clearTimeout(pathologistDebounceRef.current);
      }
    };
  }, []);

  const handleRerunSingleStep = useCallback(
    async (stepKey: string) => {
      if (!extractionJobId) return;
      const s3Keys = uploadedFiles.filter((f) => f.s3Key).map((f) => f.s3Key);
      setRerunningSteps((prev) => new Set(prev).add(stepKey));
      setExtractionSteps((prev) => ({
        ...prev,
        [stepKey]: { ...prev[stepKey], status: "running" },
      }));
      lastAppliedSteps.current.delete(stepKey);
      try {
        await accessioningService.rerunExtractionSteps(
          extractionJobId,
          [stepKey],
          s3Keys
        );
        if (
          !isCurrentExtractionStatusJob(
            extractionPollingStateRef.current,
            extractionJobId
          )
        )
          return;
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(() => {
            pollExtractionStatus(extractionJobId);
          }, 2000);
        }
      } catch {
        if (
          !isCurrentExtractionStatusJob(
            extractionPollingStateRef.current,
            extractionJobId
          )
        )
          return;
        setExtractionSteps((prev) => ({
          ...prev,
          [stepKey]: { ...prev[stepKey], status: "failed" },
        }));
        notifications.show({
          title: "Re-extraction Failed",
          message: "Could not queue step re-extraction. Please try again.",
          color: "red",
        });
      } finally {
        setRerunningSteps((prev) => {
          const next = new Set(prev);
          next.delete(stepKey);
          return next;
        });
      }
    },
    [extractionJobId, uploadedFiles, pollExtractionStatus]
  );

  // Handle AI extraction with intelligent matching
  const handleExtractData = useCallback(async () => {
    // Get s3Keys from uploaded files
    const s3Keys = uploadedFiles.filter((f) => f.s3Key).map((f) => f.s3Key);

    if (s3Keys.length === 0) {
      notifications.show({
        title: "No Files",
        message: "Please upload documents first",
        color: "yellow",
      });
      return;
    }

    const extractionRequest = extractionRequestGuardRef.current.begin();
    setIsExtracting(true);
    setIsExtractionStatusUnavailable(false);
    setExtractionError(null);
    setExtractionProgress(0);
    setExtractionSteps({});
    setExtractionCurrentPhase(undefined);
    lastAppliedSteps.current = new Set();
    extractionPollingStateRef.current = createExtractionStatusPollingState();
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    try {
      // Start extraction job - backend fetches matching data from DB internally
      // Pass case number for prompt logging purposes
      const { jobId } = await accessioningService.startExtraction(
        s3Keys,
        currentCaseNumber || undefined
      );
      if (!extractionRequestGuardRef.current.isCurrent(extractionRequest))
        return;

      setExtractionJobId(jobId);
      extractionPollingStateRef.current =
        createExtractionStatusPollingState(jobId);

      // Start polling for status
      pollingIntervalRef.current = setInterval(() => {
        pollExtractionStatus(jobId);
      }, 2000); // Poll every 2 seconds

      // Initial poll
      await pollExtractionStatus(jobId);
    } catch (error: any) {
      if (!extractionRequestGuardRef.current.isCurrent(extractionRequest))
        return;
      setIsExtracting(false);
      setExtractionError(error.message || "Failed to start extraction");
      notifications.show({
        title: "Extraction Failed",
        message: error.message || "Failed to start extraction",
        color: "red",
      });
    }
  }, [uploadedFiles, currentCaseNumber, pollExtractionStatus]);

  const handleRetryExtraction = useCallback(() => {
    if (!isExtractionStatusUnavailable || !extractionJobId) {
      handleExtractData();
      return;
    }

    extractionPollingStateRef.current = resumeExtractionStatusPolling(
      extractionPollingStateRef.current
    );
    setIsExtractionStatusUnavailable(false);
    setExtractionError(null);
    setIsExtracting(true);
    pollingIntervalRef.current = setInterval(() => {
      pollExtractionStatus(extractionJobId);
    }, 2000);
    pollExtractionStatus(extractionJobId);
  }, [
    extractionJobId,
    handleExtractData,
    isExtractionStatusUnavailable,
    pollExtractionStatus,
  ]);

  // Handle complete accessioning (with validation)

  // Check if current case has any data worth saving
  const hasDataToSave = useCallback(() => {
    const patient = form.values.patient;
    const caseData = form.values.case;

    // Check if any meaningful data has been entered
    return (
      patient?.firstName?.trim() ||
      patient?.lastName?.trim() ||
      caseData?.accountId ||
      caseData?.physicianId ||
      caseData?.specimens?.some((s: any) => s.sourceId) ||
      uploadedFiles.length > 0
    );
  }, [form.values.patient, form.values.case, uploadedFiles.length]);

  // ── Navigate-away discard prompt ──────────────────────────────────────
  // When leaving a NEW case (not edit mode) with an empty form, give the user
  // the option to discard the empty draft so it doesn't accumulate.
  // Also protects unsaved data in any mode by prompting to save or discard.
  // Uses UNSAFE_NavigationContext since the app uses BrowserRouter (not a data router).
  const caseCompletedRef = useRef(false);
  const [discardPromptPending, setDiscardPromptPending] = useState(false);
  const [unsavedChangesPromptPending, setUnsavedChangesPromptPending] =
    useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  const { navigator } = useContext(NavigationContext);

  // Ref-stabilised hasDataToSave: keeps the blocker registered once (not on every keystroke)
  const hasDataToSaveRef = useRef(hasDataToSave);
  hasDataToSaveRef.current = hasDataToSave;

  useEffect(() => {
    if (!caseId || caseCompletedRef.current) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Only try navigator.block if it exists (safe alternative for React Router 6 non-data routers)
    let unblock: (() => void) | undefined;
    if (typeof (navigator as any).block === "function") {
      unblock = (navigator as any).block((tx: { retry: () => void }) => {
        // 1. If there are unsaved changes (any mode), prompt to save/discard
        if (hasUnsavedChanges()) {
          pendingNavigationRef.current = () => {
            if (unblock) unblock();
            tx.retry();
          };
          setUnsavedChangesPromptPending(true);
          return;
        }

        // 2. If no data at all in a NEW case, prompt to discard the empty shell
        if (!isEditMode && !hasDataToSaveRef.current()) {
          pendingNavigationRef.current = () => {
            if (unblock) unblock();
            tx.retry();
          };
          setDiscardPromptPending(true);
          return;
        }

        // 3. Otherwise, just leave (already saved or empty-ish existing draft)
        if (unblock) unblock();
        tx.retry();
      });
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (unblock) unblock();
    };
  }, [isEditMode, caseId, navigator, hasUnsavedChanges, serializeFormState]);

  const handleDiscardAndLeave = useCallback(async () => {
    if (caseId) {
      try {
        await casesService.discardDraft(caseId);
      } catch {
        // If discard fails (e.g. data was added in between), still proceed
      }
    }
    setDiscardPromptPending(false);
    setUnsavedChangesPromptPending(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  }, [caseId]);

  const handleSaveAndLeave = useCallback(async () => {
    if (caseId) {
      setIsSaving(true);
      try {
        await prepareForExplicitSave();
        const sanitizedData = sanitizeFormData(form.values);
        await updateCaseMutation.mutateAsync({
          caseId,
          data: {
            patient: sanitizedData.patient,
            case: sanitizedData.case,
            specimens: sanitizedData.specimens,
            documents: uploadedFiles
              .filter((f) => f.s3Key)
              .map((f) => ({
                s3Key: f.s3Key,
                fileName: f.file?.name || f.fileName,
                mimeType: f.file?.type || f.mimeType,
              })),
            slideCompletions: sanitizedData.slideCompletions,
            manualStains: sanitizedData.manualStains,
            panelSelections: sanitizedData.panelSelections,
          },
        });
        lastSavedValuesRef.current = serializeFormState();
      } catch {
        // Even if save fails, we should probably stay here to prevent data loss.
        setIsSaving(false);
        return;
      } finally {
        setIsSaving(false);
      }
    }
    setUnsavedChangesPromptPending(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  }, [
    caseId,
    form.values,
    uploadedFiles,
    sanitizeFormData,
    updateCaseMutation,
    serializeFormState,
    prepareForExplicitSave,
  ]);

  const handleKeepAndLeave = useCallback(() => {
    setDiscardPromptPending(false);
    setUnsavedChangesPromptPending(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  }, []);

  const handleCancelLeave = useCallback(() => {
    setDiscardPromptPending(false);
    setUnsavedChangesPromptPending(false);
    pendingNavigationRef.current = null;
  }, []);

  // Reset form and create new case (used after completion or when no save needed)
  const resetAndCreateNewCase = useCallback(async () => {
    caseCompletedRef.current = false;
    specimenPopulationSnapshotRef.current = resetAccessioningFormForNewCase(
      formRef.current,
      createSpecimenUiKey()
    );
    formRef.current.setFieldValue(
      "case.dateReceived",
      getNewAccessioningDateReceived()
    );
    formRef.current.resetDirty(formRef.current.getValues());

    // Reset all UI and extraction states
    setUploadedFiles([]);
    setCertainties({});
    setHighPriorityTriggers([]);
    setSpecimenSlideCompletions({});
    setManualStains({});
    setPanelSelections({});
    workListLookupGuardRef.current.invalidate();
    matchedExpectedCaseRef.current = null;
    setMatchedExpectedCase(null);
    setWorkListStainOverrides([]);
    setAssignmentReason("");
    setShouldCreateMappings(false);

    // Reset extraction states
    setIsExtracting(false);
    setExtractionProgress(0);
    setExtractionError(null);
    setExtractionJobId(null);
    setIsExtractionStatusUnavailable(false);
    extractionRequestGuardRef.current.invalidate();
    extractionPollingStateRef.current = createExtractionStatusPollingState();
    extractionPollInFlightJobRef.current = null;
    setRerunningSteps(new Set());
    setExtractionSteps({});
    setExtractionCurrentPhase(undefined);

    // Reset cross-doc mismatches and unmatched extractions
    setCrossDocMismatches([]);
    setUnmatchedExtractions({});

    // Reset prefix resolution state
    setPrefixLookupResult(null);
    setIsResolvingPrefix(false);
    setManualBillingScenarioId(null);
    setPrefixSelectedBillingScenarioId(null);
    setPrefixOverride(null);
    assignedPrefixRef.current = null;
    requestedPrefixRef.current = null;
    prefixLookupGuardRef.current.invalidate();
    prefixAssignmentExecutorRef.current.invalidate();
    prefixAssignmentExecutorRef.current = createSerialLatestExecutor<
      string,
      CaseIdentityResult
    >();
    setIsAssigningPrefix(false);
    setIsCaseIdentityUnverified(false);
    setCanonicalExternalClaimState("idle");
    setConfirmedPrefixOverrideKey(null);
    setConfirmedPassthroughMismatchKey(null);
    setPrefixOverrideWarningModalOpen(false);
    setPassthroughMismatchWarningModalOpen(false);

    // Reset AI-detected signals
    setIsCytology(false);
    setIsResearch(false);
    setIsConsultation(false);
    setIsTcOnly(false);
    setIsGrossingOnly(false);

    // Reset accordion and UI states
    setOpenedSections([
      "patient",
      "primaryInsurance",
      "caseInfo",
      "specimens",
      "consultation",
      "originating",
    ]);
    setShowScrollIndicator(false);

    // Reset committed findings and resolved stains
    setCommittedFindings({});
    setResolvedStains({});

    // Reset case ID states
    setCaseId(null);
    applyCaseNumberContext(null);
    setCaseCreationState("loading");

    // Reset auto-save state
    setAutoSaveStatus("idle");
    lastSavedValuesRef.current = "";
    clearAutoSaveTimeout();
    autoSaveExecutorRef.current.invalidate();
    autoSaveExecutorRef.current = createSerialLatestExecutor<
      AccessioningAutoSaveRequest,
      AccessioningAutoSaveResult
    >();

    // Clear any polling intervals
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Clear timeout refs
    if (prefixLookupTimeoutRef.current) {
      clearTimeout(prefixLookupTimeoutRef.current);
      prefixLookupTimeoutRef.current = null;
    }
    if (workListLookupTimeoutRef.current) {
      clearTimeout(workListLookupTimeoutRef.current);
      workListLookupTimeoutRef.current = null;
    }
    pathologistAssignmentGuardRef.current.invalidate();
    if (pathologistDebounceRef.current) {
      clearTimeout(pathologistDebounceRef.current);
      pathologistDebounceRef.current = null;
    }

    // Clear extraction ref
    lastAppliedSteps.current.clear();
    lastLookedUpExtCaseRef.current = "";

    // Reset the initialization ref to allow new case creation
    isInitializingRef.current = false;

    // Create new draft case
    if (defaultFacility && user) {
      isInitializingRef.current = true;
      try {
        const result = await createDraftCaseMutation.mutateAsync(
          defaultFacility.id
        );

        setCaseId(result.caseId);
        applyCaseNumberContext(result.caseNumber);
        setCaseCreationState("ready");

        // Mark that we just created this case (to skip "editing" notification)
        justCreatedCaseRef.current = true;

        // Update URL to include case ID so refresh loads the same case
        navigate(`/accessioning/${result.caseId}`, { replace: true });
      } catch (error: any) {
        setCaseCreationState("error");
        setCaseCreationError(error.message || "Failed to create new case");
        isInitializingRef.current = false;
        notifications.show({
          title: "Error",
          message: "Failed to create new case. Please refresh the page.",
          color: "red",
        });
      }
    }
  }, [
    applyCaseNumberContext,
    defaultFacility,
    user,
    createDraftCaseMutation,
    navigate,
    clearAutoSaveTimeout,
  ]);

  const getGrossingSaveData = useCallback(
    () => ({
      ...sanitizeFormData(formRef.current.values, true),
      documents: uploadedFiles
        .filter((file) => file.s3Key)
        .map((file) => ({
          s3Key: file.s3Key,
          fileName: file.file?.name || file.fileName,
          mimeType: file.file?.type || file.mimeType,
        })),
    }),
    [sanitizeFormData, uploadedFiles]
  );
  const saveGrossingIdentifiers = useCallback(
    (data: AccessioningData) =>
      updateCaseMutation.mutateAsync({ caseId: caseId!, data }),
    [updateCaseMutation, caseId]
  );
  const handleGrossingIdentifiersSaved = useCallback(
    (reprintRequired: boolean) => {
      notifications.show({
        title: "Case Saved",
        message: reprintRequired
          ? "Specimen identifiers saved. Reprint all affected slides, stickers and labels."
          : "Specimen identifiers saved.",
        color: reprintRequired ? "orange" : "green",
        autoClose: reprintRequired ? false : 4000,
      });
      navigate(-1);
    },
    [navigate]
  );
  const grossingIdentifierSave = useGrossingSpecimenIdentifierSave({
    caseId,
    persistedSpecimens: existingSpecimens,
    prepare: prepareForExplicitSave,
    getData: getGrossingSaveData,
    save: saveGrossingIdentifiers,
    onSaved: handleGrossingIdentifiersSaved,
  });

  const handleGrossingIdentifierSave = useCallback(async () => {
    if (needsPrefixOverrideWarning) {
      openPrefixOverrideWarning("saveGrossingIdentifiers");
      return;
    }
    await grossingIdentifierSave.handleSave();
  }, [
    needsPrefixOverrideWarning,
    openPrefixOverrideWarning,
    grossingIdentifierSave,
  ]);

  // Handle save and go back
  const proceedWithSaveAndGoBack = useCallback(async () => {
    if (caseId) {
      setIsSaving(true);
      try {
        await prepareForExplicitSave();
        const sanitizedData = sanitizeFormData(formRef.current.values);
        await updateCaseMutation.mutateAsync({
          caseId,
          data: {
            patient: sanitizedData.patient,
            case: sanitizedData.case,
            specimens: sanitizedData.specimens,
            documents: uploadedFiles
              .filter((f) => f.s3Key)
              .map((f) => ({
                s3Key: f.s3Key,
                fileName: f.file?.name || f.fileName,
                mimeType: f.file?.type || f.mimeType,
              })),
            slideCompletions: sanitizedData.slideCompletions,
            manualStains: sanitizedData.manualStains,
            panelSelections: sanitizedData.panelSelections,
          },
        });
      } catch {
        // navigate back regardless — auto-save will have covered recent changes
      } finally {
        setIsSaving(false);
      }
    }
    navigate(-1);
  }, [
    caseId,
    sanitizeFormData,
    updateCaseMutation,
    uploadedFiles,
    navigate,
    prepareForExplicitSave,
  ]);

  const handleSaveAndGoBack = useCallback(async () => {
    if (needsPrefixOverrideWarning) {
      openPrefixOverrideWarning("saveAndGoBack");
      return;
    }
    await proceedWithSaveAndGoBack();
  }, [
    openPrefixOverrideWarning,
    needsPrefixOverrideWarning,
    proceedWithSaveAndGoBack,
  ]);

  // Handle save and new case - saves current case before creating new one
  const proceedWithSaveAndNewCase = useCallback(async () => {
    // Save current case if there's data
    if (caseId && hasDataToSave()) {
      setIsSaving(true);
      try {
        await prepareForExplicitSave();
        const sanitizedData = sanitizeFormData(formRef.current.values);
        // Always include patient data - backend handles partial data storage
        await updateCaseMutation.mutateAsync({
          caseId,
          data: {
            patient: sanitizedData.patient,
            case: sanitizedData.case,
            specimens: sanitizedData.specimens,
            documents: uploadedFiles
              .filter((f) => f.s3Key)
              .map((f) => ({
                s3Key: f.s3Key,
                fileName: f.file?.name || f.fileName,
                mimeType: f.file?.type || f.mimeType,
              })),
            slideCompletions: sanitizedData.slideCompletions,
            manualStains: sanitizedData.manualStains,
            panelSelections: sanitizedData.panelSelections,
          },
        });

        notifications.show({
          title: "Case Saved",
          message: `Case ${displayCaseNumber} saved. Creating new case...`,
          color: "blue",
          autoClose: 2000,
        });
      } catch (error: any) {
        notifications.show({
          title: "Save Failed",
          message: error.message || "Failed to save current case",
          color: "red",
        });
        setIsSaving(false);
        return; // Don't create new case if save failed
      }
      setIsSaving(false);
    }

    // Now reset and create new case
    await resetAndCreateNewCase();
  }, [
    caseId,
    hasDataToSave,
    sanitizeFormData,
    updateCaseMutation,
    uploadedFiles,
    displayCaseNumber,
    resetAndCreateNewCase,
    prepareForExplicitSave,
  ]);

  const handleSaveAndNewCase = useCallback(async () => {
    if (needsPrefixOverrideWarning) {
      openPrefixOverrideWarning("saveAndNew");
      return;
    }
    await proceedWithSaveAndNewCase();
  }, [
    openPrefixOverrideWarning,
    needsPrefixOverrideWarning,
    proceedWithSaveAndNewCase,
  ]);

  // Core completion logic - validation is done in handleComplete before this is called
  const proceedWithCompletion = useCallback(
    async (options?: { confirmEmptyGross?: boolean }) => {
      if (!caseId) return;
      if (!(await synchronizeCanonicalExternalIdentity("completion"))) return;

      setIsSaving(true);

      await prepareForExplicitSave();

      // AI-extracted stains: when extraction matched stain codes for a specimen, send them so
      // the backend uses them instead of source default stains. Pre-processed is only honoured
      // for slides_only / blocks_and_slides materials (mirror of the backend rule).
      const PROCESSED_MATERIALS = new Set(["slides_only", "blocks_and_slides"]);
      const extractedStains = (form.values.case.specimens || []).flatMap(
        (spec: any, idx: number) => {
          const matched: string[] = spec.matchedStainIds || [];
          if (matched.length === 0) return [];
          const canBePreProcessed = PROCESSED_MATERIALS.has(spec.material);
          return matched.map((stainId) => {
            return {
              specimenNumber: spec.number,
              stainId,
              stainCode: "",
              isPreProcessed:
                canBePreProcessed &&
                !!effectiveSlideCompletions[idx]?.[stainId],
            };
          });
        }
      );

      try {
        const result = await completeMutation.mutateAsync({
          caseId,
          data: {
            patient: form.values.patient,
            case: {
              ...form.values.case,
              // Map frontend field names to backend schema names
              sendingPracticeId: form.values.case.accountId,
              submittingLabId: form.values.case.submittingLabId,
              surgeryCenterLabId: form.values.case.locationId,
              // Include the final prefix as a completion-time safety check.
              // The server remains authoritative for the sequence number.
              prefixId: effectivePrefixData?.prefixId ?? undefined,
              casePrefix:
                prefixOverride || prefixLookupResult?.prefixCode || undefined,
              // Pass billing pending flag from prefix resolution
              billingPending: effectivePrefixData?.billingPending ?? false,
              // Override billingScenarioId with prefix-driven selection when available
              ...(prefixSelectedBillingScenarioId
                ? { billingScenarioId: prefixSelectedBillingScenarioId }
                : {}),
              // Override billingScenarioId with manual selection when provided
              ...(manualBillingScenarioId
                ? { billingScenarioId: manualBillingScenarioId }
                : {}),
              // Signal flags (C-4703): include page-level signal states in the case payload
              isTcOnly,
              isGrossingOnly,
              isConsultation,
              isResearch,
              isCytology,
              confirmEmptyGross: options?.confirmEmptyGross,
            } as any,
            specimens: form.values.case.specimens.map((specimen: any) => ({
              ...specimen,
              blockCount: resolveAccessioningBlockCount(
                derivedCaseTypeRef.current,
                specimen.blockCount
              ),
            })) as any,
            createMappings: shouldCreateMappings,
            // Pass slide completion status for external cases (pass-through, consultation)
            slideCompletions:
              derivedCaseTypeRef.current === "grossing_only"
                ? {}
                : effectiveSlideCompletions,
            // Pass manually added/removed stains per specimen
            manualStains:
              derivedCaseTypeRef.current === "grossing_only"
                ? {}
                : manualStains,
            panelSelections:
              derivedCaseTypeRef.current === "grossing_only"
                ? {}
                : panelSelections,
            resolvedStains:
              derivedCaseTypeRef.current === "grossing_only"
                ? Object.fromEntries(
                    form.values.case.specimens.map((_: any, index: number) => [
                      index,
                      [],
                    ])
                  )
                : submittedResolvedStains,
            // Work list stain overrides (replaces source defaults when present)
            workListStains:
              derivedCaseTypeRef.current === "grossing_only"
                ? undefined
                : matchedExpectedCase
                ? workListStainOverrides
                : undefined,
            // AI-extracted stains (replace source defaults per specimen when present)
            extractedStains:
              derivedCaseTypeRef.current !== "grossing_only" &&
              extractedStains.length > 0
                ? extractedStains
                : undefined,
            // Expected case to match on completion
            expectedCaseId: matchedExpectedCase?.id,
            // Pass uploaded documents to link to the case (stored in case_files table).
            // Include ALL files with an s3Key — both newly uploaded (have File blob) and
            // previously persisted ones rehydrated from the DB. Backend sync treats the
            // payload as the complete desired state, so omitting rehydrated files would
            // cause them to be deleted on re-completion.
            documents: uploadedFiles
              .filter((f) => !!f.s3Key)
              .map((f) => ({
                s3Key: f.s3Key,
                fileName: f.file?.name || f.fileName,
                mimeType: f.file?.type || f.mimeType,
                size: f.file?.size,
              })),
          },
        });

        // Auto-create non-blocking error report for missing insurance (per C-783/C-1617 requirements)
        // Insurance is "recommended" not "required" - case proceeds but missing info is flagged for billing follow-up
        const primaryInsurance = form.values.case.primaryInsurance;
        const hasMissingInsurance = shouldCreateMissingInsuranceFollowUp(
          primaryInsurance,
          result.effectiveNoPatientBillingAndInsuranceInfoRequired
        );

        if (hasMissingInsurance) {
          const patDataError = errorTypes.find(
            (e: { error: string }) => e.error === "PatData"
          );
          if (patDataError) {
            try {
              // Check for existing pending PatData error before creating (backend also deduplicates)
              const existingErrors = await api.get<
                Array<{
                  discrepancy?: { error: string };
                  status: string;
                }>
              >(`/workflows/errors/case/${caseId}`);
              const alreadyReported = existingErrors.some(
                (e) =>
                  e.discrepancy?.error === "PatData" && e.status === "pending"
              );

              if (!alreadyReported) {
                await api.post("/workflows/errors", {
                  caseId,
                  errorId: patDataError.id,
                  description:
                    "Missing insurance information - flagged for billing follow-up",
                  blockCase: false,
                  preventSignOff: false,
                });
              }
            } catch (errorReportErr) {
              // eslint-disable-next-line no-console
              console.warn(
                "Failed to create error report for missing insurance:",
                errorReportErr
              );
            }
          }
        }

        // Show success message, including mapping creation info if applicable
        const mappingInfo =
          result.mappingsCreated?.physicianMappingCreated ||
          result.mappingsCreated?.surgeryCenterMappingCreated
            ? " Practice mappings created."
            : "";

        // Include insurance flag info in notification if applicable
        const insuranceInfo = hasMissingInsurance
          ? " Missing insurance flagged for billing follow-up."
          : "";

        notifications.show({
          title: "Accessioning Complete",
          message: `Case ${result.caseNumber} moved to next workflow stage.${mappingInfo}${insuranceInfo}`,
          color: "green",
        });

        // Mark as completed so blocker doesn't fire on the navigate/reset below
        caseCompletedRef.current = true;

        // If in edit mode, navigate to case details; otherwise reset for new case
        if (isEditMode) {
          navigate(`/cases/${caseId}`);
        } else {
          resetAndCreateNewCase();
        }
      } catch (error: any) {
        let message = error.message || "Failed to complete accessioning";

        // Map backend internal error codes to user-friendly messages
        if (message === "EMPTY_GROSS_DESCRIPTION") {
          message =
            "Gross description is missing for one or more specimens. Please enter them or confirm completion anyway.";
        }

        notifications.show({
          title: "Complete Failed",
          message,
          color: "red",
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      caseId,
      form,
      prefixLookupResult,
      prefixOverride,
      prefixSelectedBillingScenarioId,
      manualBillingScenarioId,
      shouldCreateMappings,
      effectiveSlideCompletions,
      manualStains,
      panelSelections,
      submittedResolvedStains,
      workListStainOverrides,
      matchedExpectedCase,
      uploadedFiles,
      isEditMode,
      navigate,
      completeMutation,
      errorTypes,
      resetAndCreateNewCase,
      effectivePrefixData,
      isTcOnly,
      isGrossingOnly,
      isConsultation,
      isResearch,
      isCytology,
      prepareForExplicitSave,
      synchronizeCanonicalExternalIdentity,
    ]
  );

  const handleComplete = useCallback(async () => {
    if (!caseId) return;

    // Validate form first before any other checks
    const validation = form.validate();
    if (validation.hasErrors) {
      const errorEntries = Object.entries(validation.errors);
      const errorCount = errorEntries.length;
      const firstErrorField = errorEntries[0][0];

      // Map field path prefixes to accordion section IDs so we can auto-expand
      // the collapsed section before attempting to scroll into view.
      const sectionMap: Record<string, string> = {
        "case.primaryInsurance": "primaryInsurance",
        "case.secondaryInsurance": "secondaryInsurance",
        "case.": "caseInfo",
        "patient.": "patient",
      };

      const targetSection: string | null =
        Object.entries(sectionMap).find(([prefix]) =>
          firstErrorField.startsWith(prefix)
        )?.[1] ?? null;

      // Expand the section if it is currently collapsed
      if (targetSection && !openedSections.includes(targetSection)) {
        setOpenedSections((prev) => [...prev, targetSection]);
      }

      // Scroll to (and focus) the field after React re-renders the expanded section
      setTimeout(() => {
        const fieldSelector = `[name="${firstErrorField}"], [data-field="${firstErrorField}"]`;
        const errorElement = document.querySelector(fieldSelector);
        if (errorElement instanceof HTMLElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }, 300);

      // Show error count summary – up to 3 messages then "..."
      const errorMessages = errorEntries
        .slice(0, 3)
        .map(([, msg]) => msg)
        .join(", ");
      const suffix = errorCount > 3 ? "..." : "";

      notifications.show({
        title: `${errorCount} Field${
          errorCount !== 1 ? "s" : ""
        } Need Attention`,
        message: `${errorMessages}${suffix}`,
        color: "red",
        autoClose: 5000,
      });
      return;
    }

    if (activeUnresolvedWorkListStains.length) {
      notifications.show({
        title: "Work List Stains Need Attention",
        message: activeUnresolvedWorkListStains
          .map((stain) => `${stain.specimenNumber}: ${stain.requestedStain}`)
          .join(", "),
        color: "red",
        autoClose: false,
      });
      return;
    }

    // Guard: practice (accountId) is required for every case type except
    // pass-through and TC-only. Checked here so the live derived case type is used.
    if (
      isPracticeLabRequired(derivedCaseType) &&
      !form.values.case.accountId &&
      !form.values.case.submittingLabId
    ) {
      if (!openedSections.includes("caseInfo")) {
        setOpenedSections((prev) => [...prev, "caseInfo"]);
      }
      notifications.show({
        title: "Practice or Lab Required",
        message: "Select a practice or lab before completing accessioning.",
        color: "red",
        autoClose: 5000,
      });
      setTimeout(() => {
        const el = document.querySelector(
          '[name="case.accountId"], [data-field="case.accountId"]'
        );
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
        }
      }, 300);
      return;
    }

    // Guard: billing pending and no scenario selected yet
    if (
      prefixLookupResult?.billingPending &&
      !manualBillingScenarioId &&
      !prefixSelectedBillingScenarioId
    ) {
      // Expand the caseInfo accordion section so the Select is visible
      if (!openedSections.includes("caseInfo")) {
        setOpenedSections((prev) => [...prev, "caseInfo"]);
      }
      notifications.show({
        title: "Billing Scenario Required",
        message:
          "Please select a billing scenario for this practice before completing accessioning.",
        color: "red",
        autoClose: 5000,
      });
      return;
    }

    if (needsPrefixOverrideWarning) {
      openPrefixOverrideWarning("complete");
      return;
    }

    if (needsPassthroughMismatchWarning) {
      setPassthroughMismatchWarningModalOpen(true);
      return;
    }

    // Soft check: fields expected for the derived case type (shipping, consultation, etc.).
    // These are optional — warn and let the operator complete anyway or jump to the field.
    if (completionWarnings.length > 0) {
      setCompletionWarningModalOpen(true);
      return;
    }

    await proceedWithCompletion();
  }, [
    caseId,
    form,
    openedSections,
    prefixLookupResult,
    manualBillingScenarioId,
    prefixSelectedBillingScenarioId,
    completionWarnings,
    openPrefixOverrideWarning,
    needsPrefixOverrideWarning,
    needsPassthroughMismatchWarning,
    proceedWithCompletion,
    derivedCaseType,
    activeUnresolvedWorkListStains,
  ]);

  const confirmPassthroughMismatchWarning = useCallback(() => {
    if (passthroughMismatchWarningKey) {
      setConfirmedPassthroughMismatchKey(passthroughMismatchWarningKey);
    }
  }, [passthroughMismatchWarningKey]);

  const handleClosePassthroughMismatchWarning = useCallback(() => {
    setPassthroughMismatchWarningModalOpen(false);
  }, []);

  const handleReviewPassthroughMismatchWarning = useCallback(() => {
    setPassthroughMismatchWarningModalOpen(false);
    setOpenedSections((prev) =>
      Array.from(new Set([...prev, "caseInfo", "specimens"]))
    );
  }, []);

  const handleCompletePassthroughMismatchAnyway = useCallback(() => {
    setPassthroughMismatchWarningModalOpen(false);
    confirmPassthroughMismatchWarning();

    if (completionWarnings.length > 0) {
      setCompletionWarningModalOpen(true);
      return;
    }

    void proceedWithCompletion();
  }, [
    completionWarnings,
    confirmPassthroughMismatchWarning,
    proceedWithCompletion,
  ]);

  const handleCloseCompletionWarning = useCallback(() => {
    setCompletionWarningModalOpen(false);
  }, []);

  // "Complete anyway" — bypass the missing-field warnings and finish.
  const handleCompleteAnyway = useCallback(() => {
    setCompletionWarningModalOpen(false);
    const hasEmptyGrossWarning = completionWarnings.some((w) =>
      w.field.endsWith(".gross")
    );
    void proceedWithCompletion({ confirmEmptyGross: hasEmptyGrossWarning });
  }, [proceedWithCompletion, completionWarnings]);

  // "Enter details" — close the modal, expand the section, and scroll to the first missing field.
  const handleEnterCompletionDetails = useCallback(() => {
    setCompletionWarningModalOpen(false);
    const first = completionWarnings[0];
    if (!first) return;
    if (!openedSections.includes(first.section)) {
      setOpenedSections((prev) => [...prev, first.section]);
    }
    setTimeout(() => {
      const fieldSelector = `[name="${first.field}"], [data-field="${first.field}"]`;
      const fieldElement = document.querySelector(fieldSelector);
      if (fieldElement instanceof HTMLElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldElement.focus();
      }
    }, 300);
  }, [completionWarnings, openedSections]);

  const handleClosePrefixOverrideWarning = useCallback(() => {
    setPrefixOverrideWarningModalOpen(false);
    setPendingPrefixOverrideAction(null);
  }, []);

  const handleReviewPrefixOverride = useCallback(() => {
    setPrefixOverrideWarningModalOpen(false);
    setPendingPrefixOverrideAction(null);
    if (!openedSections.includes("caseInfo")) {
      setOpenedSections((prev) => [...prev, "caseInfo"]);
    }
  }, [openedSections]);

  const handleProceedWithPrefixOverride = useCallback(() => {
    const action = pendingPrefixOverrideAction;
    setPrefixOverrideWarningModalOpen(false);
    setPendingPrefixOverrideAction(null);
    if (prefixOverrideWarningDetails.shouldWarn) {
      setConfirmedPrefixOverrideKey(
        `${prefixOverrideWarningDetails.computedPrefix}:${prefixOverrideWarningDetails.selectedPrefix}`
      );
    }

    if (action === "saveGrossingIdentifiers") {
      void grossingIdentifierSave.handleSave();
      return;
    }

    if (action === "saveAndGoBack") {
      void proceedWithSaveAndGoBack();
      return;
    }

    if (action === "saveAndNew") {
      void proceedWithSaveAndNewCase();
      return;
    }

    if (action !== "complete") {
      return;
    }

    if (needsPassthroughMismatchWarning) {
      setPassthroughMismatchWarningModalOpen(true);
      return;
    }

    if (completionWarnings.length > 0) {
      setCompletionWarningModalOpen(true);
      return;
    }

    void proceedWithCompletion();
  }, [
    completionWarnings,
    pendingPrefixOverrideAction,
    grossingIdentifierSave,
    prefixOverrideWarningDetails,
    needsPassthroughMismatchWarning,
    proceedWithCompletion,
    proceedWithSaveAndGoBack,
    proceedWithSaveAndNewCase,
  ]);

  const prefixOverrideConfirmLabel = useMemo(() => {
    if (pendingPrefixOverrideAction === "complete") {
      return "Complete with override";
    }
    return "Save with override";
  }, [pendingPrefixOverrideAction]);

  // ── Phase 4: Named handlers to replace inline JSX arrow functions ─────────

  const handleCertaintyChange = useCallback((field: string, cert: number) => {
    setCertainties((prev) => ({ ...prev, [field]: cert }));
  }, []);

  const handleClearUnmatched = useCallback((field: string) => {
    setUnmatchedExtractions((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleInsuranceProviderLabel = useCallback(
    (id: string) => insuranceProviders.find((p) => p.value === id)?.name || id,
    [insuranceProviders]
  );

  const handleInsuranceToggle = useCallback(() => {}, []);

  const handlePathologistChange = useCallback(
    (id: string | null) => {
      form.setFieldValue("case.pathologistId", id || "");
      setAssignmentReason(id ? "Manually selected" : "");
    },
    [form]
  );

  const handleRushChange = useCallback(
    (value: boolean) => {
      form.setFieldValue("case.isRush", value);
    },
    [form]
  );

  const handleRushReasonChange = useCallback(
    (value: string) => {
      form.setFieldValue("case.rushReason", value);
    },
    [form]
  );

  const handleRushCertaintyChange = useCallback((cert: number) => {
    setCertainties((prev) => ({ ...prev, "case.isRush": cert }));
  }, []);

  const handleHighPriorityChange = useCallback(
    (value: boolean) => {
      form.setFieldValue("case.isHighPriority", value);
    },
    [form]
  );

  const handleHighPriorityReasonChange = useCallback(
    (value: string) => {
      form.setFieldValue("case.highPriorityReason", value);
    },
    [form]
  );

  const handleHighPriorityCertaintyChange = useCallback((cert: number) => {
    setCertainties((prev) => ({ ...prev, "case.isHighPriority": cert }));
  }, []);

  const handleThroughSalesRepChange = useCallback(
    (value: boolean) => {
      form.setFieldValue("case.isThroughSalesRep", value);
      if (!value) {
        form.setFieldValue("case.salesRepId", "");
      }
    },
    [form]
  );

  const handleSalesRepChange = useCallback(
    (id: string | null) => {
      form.setFieldValue("case.salesRepId", id || "");
    },
    [form]
  );

  const handleBillingScenarioChange = useCallback(
    (id: string | null) => {
      form.setFieldValue("case.billingScenarioId", (id || null) as any);
    },
    [form]
  );

  const handleManualBillingScenarioChange = useCallback(
    (val: string | null) => {
      setManualBillingScenarioId(val);
    },
    []
  );

  const handleNewCaseButtonClick = useCallback(() => {
    setNewCaseModalOpened(true);
  }, []);

  const handleScrollIndicatorClick = useCallback(() => {
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    if (scrollContainer) {
      scrollContainer.scrollBy({ top: 150, behavior: "smooth" });
    }
  }, []);

  const handleRetryCase = useCallback(() => {
    setCaseId(null);
    setCaseCreationState("loading");
  }, []);

  const handleNewCaseModalClose = useCallback(() => {
    setNewCaseModalOpened(false);
  }, []);

  const handleNewCaseModalDiscard = useCallback(async () => {
    setNewCaseModalOpened(false);
    await resetAndCreateNewCase();
  }, [resetAndCreateNewCase]);

  const handleNewCaseModalSaveAndNew = useCallback(async () => {
    setNewCaseModalOpened(false);
    await handleSaveAndNewCase();
  }, [handleSaveAndNewCase]);

  const validateSpecimenIdentifiersForPrint = useCallback(() => {
    if (derivedCaseType !== "tc_only") return true;
    const firstError = Object.entries(
      getSpecimenIdentifierErrors(form.values.case?.specimens || [])
    )[0];
    if (!firstError) return true;
    notifications.show({
      title: "Cannot Print Labels",
      message: `Specimen ${Number(firstError[0]) + 1}: ${firstError[1]}`,
      color: "red",
    });
    return false;
  }, [derivedCaseType, form.values.case?.specimens]);

  const handlePrintCassettes = useCallback(async () => {
    if (!caseId || !canPrintSticker) return;
    if (!validateSpecimenIdentifiersForPrint()) return;
    if (!(await synchronizeCanonicalExternalIdentity("printing"))) return;
    const specimensWithSource = (form.values.case?.specimens || []).filter(
      (s: any) => s.sourceId
    );
    const effectivePrefix =
      prefixOverride || prefixLookupResult?.prefixCode || undefined;
    const request = {
      specimens: specimensWithSource.map((spec: any) => ({
        specimenLetter: spec.number,
        blockCount: spec.blockCount || 1,
      })),
      casePrefix: effectivePrefix,
    };
    try {
      const labels = await generateLabelsMutation.mutateAsync({
        caseId,
        request,
      });
      await printLabels(labels);
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Failed to generate cassette labels",
        color: "red",
      });
    }
  }, [
    form,
    caseId,
    canPrintSticker,
    generateLabelsMutation,
    printLabels,
    prefixOverride,
    prefixLookupResult?.prefixCode,
    synchronizeCanonicalExternalIdentity,
    validateSpecimenIdentifiersForPrint,
  ]);

  const handlePrintRequisitionLabel = useCallback(async () => {
    const workstationId =
      workstationPreferenceService.getSelectedWorkstationId();

    if (
      !caseId ||
      !canPrintSticker ||
      !workstationId ||
      !stickerPrinterReadiness?.ready
    ) {
      notifications.show({
        title: "Print Failed",
        message: "Case data or sticker printer not ready.",
        color: "red",
      });
      return;
    }

    const identity = await synchronizeCanonicalExternalIdentity("printing");
    if (!identity) return;
    const caseNumber = identity.caseNumber;

    if (
      !isStickerTemplateVersionAtLeast(
        stickerPrinterReadiness.stickerTemplateVersion,
        MIN_REQUISITION_STICKER_TEMPLATE_VERSION
      )
    ) {
      notifications.show({
        title: "Bridge Update Required",
        message: getStickerTemplateUpdateMessage(
          MIN_REQUISITION_STICKER_TEMPLATE_VERSION,
          stickerPrinterReadiness.stickerTemplateVersion
        ),
        color: "red",
      });
      return;
    }

    setIsPrintingRequisitionLabel(true);
    try {
      const label = buildRequisitionStickerLabel({
        caseId,
        caseNumber,
        patientFirstName: form.values.patient?.firstName,
        patientLastName: form.values.patient?.lastName,
        dateOfService: form.values.case?.dateCollected,
        specimenCount: (form.values.case?.specimens || []).length,
      });

      const result =
        await stickerLabelPrinterService.printAccessioningStickerLabels(
          [label],
          stickerPrinterReadiness.deviceId,
          workstationId,
          stickerPrinterReadiness.stickerTemplateVersion
        );

      if (result.success) {
        notifications.show({
          title: "Requisition Label Sent",
          message: "Requisition label sent to the label printer.",
          color: "green",
        });
      } else {
        notifications.show({
          title: "Print Failed",
          message: result.error || "Requisition label print failed",
          color: "red",
        });
      }
    } catch {
      notifications.show({
        title: "Print Failed",
        message: "Could not dispatch the requisition label.",
        color: "red",
      });
    } finally {
      setIsPrintingRequisitionLabel(false);
    }
  }, [
    caseId,
    canPrintSticker,
    form.values.case,
    form.values.patient?.firstName,
    form.values.patient?.lastName,
    stickerPrinterReadiness,
    synchronizeCanonicalExternalIdentity,
  ]);

  // One-click sticker print for the slides marked completed during accessioning.
  // Slides/blocks are not persisted in structured tables until completion, but
  // draft changes are flushed to the database before planning to ensure an
  // accurate label plan matching the current UI state.
  const handlePrintStickers = useCallback(async () => {
    if (!validateSpecimenIdentifiersForPrint()) return;
    const workstationId =
      workstationPreferenceService.getSelectedWorkstationId();
    if (
      !stickerCaseData ||
      !caseId ||
      !workstationId ||
      !stickerPrinterReadiness?.ready
    ) {
      notifications.show({
        title: "Print Failed",
        message: "Case data or sticker printer not ready.",
        color: "red",
      });
      return;
    }

    const identity = await synchronizeCanonicalExternalIdentity("printing");
    if (!identity) return;

    if (
      !isStickerTemplateVersionAtLeast(
        stickerPrinterReadiness.stickerTemplateVersion,
        MIN_DYMO_SLIDE_TEMPLATE_VERSION
      )
    ) {
      notifications.show({
        title: "Bridge Update Required",
        message: getStickerTemplateUpdateMessage(
          MIN_DYMO_SLIDE_TEMPLATE_VERSION,
          stickerPrinterReadiness.stickerTemplateVersion
        ),
        color: "red",
      });
      return;
    }

    setIsPrintingStickers(true);
    try {
      // Ensure current draft changes are persisted before generating labels
      // to avoid 'No completed slides' blocker from stale backend state.
      if (caseId) {
        await prepareForExplicitSave();
        const currentValues = serializeFormState();

        if (currentValues !== lastSavedValuesRef.current) {
          const sanitizedData = sanitizeFormData(form.values);
          const allDocs = uploadedFiles
            .filter((f) => f.s3Key)
            .map((f) => ({
              s3Key: f.s3Key,
              fileName: f.file?.name || f.fileName,
              mimeType: f.file?.type || f.mimeType,
            }));

          await updateCaseMutation.mutateAsync({
            caseId,
            data: {
              patient: sanitizedData.patient,
              case: sanitizedData.case,
              specimens: sanitizedData.specimens,
              documents: allDocs,
              slideCompletions: sanitizedData.slideCompletions,
              manualStains: sanitizedData.manualStains,
              panelSelections: sanitizedData.panelSelections,
            },
          });
          lastSavedValuesRef.current = currentValues;
        }
      }

      const plan = await generateStickerLabelsMutation.mutateAsync({
        caseId,
        request: {
          specimens: (form.values.case?.specimens || []).map((s: any) => ({
            number: s.number,
            expectedSpecimenNumber: s.expectedSpecimenNumber,
            blockCount: Number(s.blockCount) || 1,
            material: s.material,
          })),
          slideCompletions: effectiveSlideCompletions,
          manualStains,
          panelSelections,
          resolvedStains: submittedResolvedStains,
          workListStains: matchedExpectedCase
            ? workListStainOverrides
            : undefined,
          expectedCaseId: matchedExpectedCase?.id,
        },
      });

      if (!plan || plan.length === 0) {
        notifications.show({
          title: "Nothing to print",
          message: "No completed slides to print labels for.",
          color: "orange",
        });
        return;
      }

      // The form is the freshest source. The case query may still contain the
      // pre-save patient relation immediately after mutateAsync completes.
      const patientName = resolvePatientLabelName(
        form.values.patient,
        stickerCaseData.patient,
        stickerCaseData.draftPatientData
      );
      if (!patientName) {
        notifications.show({
          title: "Print Failed",
          message: MISSING_PATIENT_LABEL_NAME_ERROR,
          color: "red",
        });
        return;
      }
      const dateReceived = getAccessioningStickerDateReceived(
        form.values.case.dateReceived
      );
      // Use the authoritative identity returned by the claim endpoint so a
      // just-claimed external number is never printed under the prior draft ID.
      const caseNumber = identity.caseNumber;

      const labels: StickerLabelData[] = plan.map((p, index) => ({
        itemSlotId: `${Date.now()}-s-${index}`,
        itemType: "slide" as const,
        caseId,
        // Slide is not persisted yet — no DB id to link.
        itemId: "",
        caseNumber,
        patientName,
        specimen: p.specimenNumber,
        blockId: p.blockNumber,
        slideId: p.slideNumber,
        stain: p.stainCode,
        dateReceived,
        barcodeValue: `${caseNumber};${p.specimenNumber};${p.blockNumber};${p.slideNumber}`,
      }));

      const result =
        await stickerLabelPrinterService.printAccessioningStickerLabels(
          labels,
          stickerPrinterReadiness.deviceId,
          workstationId,
          stickerPrinterReadiness.stickerTemplateVersion
        );
      if (result.success) {
        notifications.show({
          title: "Stickers Sent",
          message: `${result.count} sticker${
            result.count === 1 ? "" : "s"
          } sent to the label printer`,
          color: "green",
        });
      } else {
        notifications.show({
          title: "Print Failed",
          message: result.error || "Sticker print failed",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Print Failed",
        message:
          error?.response?.data?.message ||
          "Could not generate sticker labels.",
        color: "red",
      });
    } finally {
      setIsPrintingStickers(false);
    }
  }, [
    caseId,
    stickerCaseData,
    stickerPrinterReadiness,
    generateStickerLabelsMutation,
    form.values,
    effectiveSlideCompletions,
    manualStains,
    panelSelections,
    submittedResolvedStains,
    workListStainOverrides,
    matchedExpectedCase,
    sanitizeFormData,
    serializeFormState,
    updateCaseMutation,
    uploadedFiles,
    validateSpecimenIdentifiersForPrint,
    prepareForExplicitSave,
    synchronizeCanonicalExternalIdentity,
  ]);

  // Loading state - show while creating case
  if (caseCreationState === "loading") {
    return (
      <Container size="fluid" py="md">
        <Center style={{ minHeight: "50vh" }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg">Creating new case...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state - show if case creation failed
  if (caseCreationState === "error") {
    return (
      <Container size="fluid" py="md">
        <Center style={{ minHeight: "50vh" }}>
          <Stack align="center" gap="md">
            <Text size="xl" c="red" fw={600}>
              Failed to Create Case
            </Text>
            <Text size="md" c="dimmed">
              {caseCreationError}
            </Text>
            <Button size="lg" onClick={handleRetryCase}>
              Retry
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Ready state - show full accessioning form
  return (
    <Container size="fluid" p={0} style={{ margin: 0, maxWidth: "100%" }}>
      <LoadingOverlay visible={isSaving} />

      <Grid
        gutter={0}
        style={{ margin: "0 auto", maxWidth: "2200px", flexWrap: "nowrap" }}
      >
        {/* LEFT SIDEBAR - Scrollable */}
        <Grid.Col
          span="content"
          style={{ width: 300, minWidth: 300, flexShrink: 0 }}
        >
          <Box
            style={{
              height: "calc(100vh - 76px)",
              position: "relative",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box
              className="hide-scrollbar left-sidebar-scroll"
              style={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              <style>{`
                .hide-scrollbar {
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                }
                .hide-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              <Stack gap="md" p="md">
                {/* Case Summary - simplified to just facility and case number */}
                <CaseSummary
                  caseNumber={displayCaseNumber || undefined}
                  facilityName={defaultFacility?.name}
                  isBackfillCaseNumber={isDisplayedNumberBackfill}
                  isAssigningCaseNumber={isAssigningPrefix}
                  isCaseNumberUnverified={isCaseIdentityUnverified}
                  onEditCaseNumber={
                    isEditMode &&
                    !isLimitedEdit &&
                    !isAssigningPrefix &&
                    !isCaseIdentityUnverified &&
                    canCorrectCaseNumber
                      ? openCaseNumberModal
                      : undefined
                  }
                />

                {/* Workflow - derived case type, shown facility-style with info card */}
                <DerivedWorkflowCard caseType={derivedCaseType} />

                {/* Document Upload */}
                <Paper
                  p="sm"
                  withBorder
                  style={
                    isLimitedEdit
                      ? { opacity: 0.5, pointerEvents: "none" }
                      : undefined
                  }
                >
                  <Stack gap="xs">
                    <Title order={5} size="sm">
                      Documents
                    </Title>

                    <DocumentUpload
                      files={uploadedFiles}
                      onFilesChange={setUploadedFiles}
                      maxFiles={5}
                      showDropZone
                      dropZonePlaceholder="Drag and drop requisition files here"
                      minHeight={120}
                      disabled={isExtracting || isLimitedEdit}
                    />

                    {scannerReady && (
                      <Button
                        leftSection={<IconScan size={16} />}
                        onClick={handleScanDocument}
                        loading={scanning}
                        fullWidth
                        variant="light"
                      >
                        {scanning ? "Scanning\u2026" : "Scan Document"}
                      </Button>
                    )}

                    {uploadedFiles.length > 0 && !isExtracting && (
                      <Button
                        onClick={handleExtractData}
                        size="sm"
                        fullWidth
                        disabled={uploadedFiles.some((f) => !f.s3Key)}
                      >
                        Extract Data with AI
                      </Button>
                    )}

                    <ExtractionProgress
                      isExtracting={isExtracting}
                      error={extractionError}
                      onRetry={handleRetryExtraction}
                      retryLabel={
                        isExtractionStatusUnavailable
                          ? "Retry status check"
                          : "Retry Extraction"
                      }
                      onRetryStep={handleRerunSingleStep}
                      rerunningSteps={rerunningSteps}
                      progress={extractionProgress}
                      steps={
                        Object.keys(extractionSteps).length > 0
                          ? extractionSteps
                          : undefined
                      }
                      currentPhase={extractionCurrentPhase}
                    />
                  </Stack>
                </Paper>

                {/* Cross-Document Validation Warnings */}
                {crossDocMismatches.length > 0 && (
                  <Alert
                    color="red"
                    variant="light"
                    icon={<IconAlertTriangle size={18} />}
                    title="Cross-Document Mismatch Detected"
                    p="md"
                    style={{ wordBreak: "break-word" }}
                  >
                    <Stack gap="sm" mt={4}>
                      {crossDocMismatches.map((m, i) => (
                        <Box key={i}>
                          {i > 0 && <Divider mb="sm" color="red.2" />}
                          <Text fw={600} size="sm" mb={4}>
                            {m.field}
                          </Text>
                          {(m.requisitionValue != null ||
                            m.demographicsValue != null) && (
                            <Group gap="xs" wrap="wrap" mb={4}>
                              <Text size="xs" c="dimmed" span>
                                Requisition:
                              </Text>
                              <Text size="xs" fw={500} c="red.8" span>
                                {m.requisitionValue ?? "—"}
                              </Text>
                              <Text size="xs" c="dimmed" span>
                                ·
                              </Text>
                              <Text size="xs" c="dimmed" span>
                                Demographics:
                              </Text>
                              <Text size="xs" fw={500} c="red.8" span>
                                {m.demographicsValue ?? "—"}
                              </Text>
                            </Group>
                          )}
                          <Text size="xs" c="red.7">
                            {m.message}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {/* Printer & Scanner Status */}
                {!isConsultation && (
                  <>
                    <CassettePrinterStatusPanel />
                    <StickerPrinterStatusPanel />
                  </>
                )}
                <DocumentScannerStatusPanel
                  onScannerReadyChange={setScannerReady}
                />
              </Stack>
            </Box>
            {/* Scroll indicator - only show when there's content to scroll */}
            {showScrollIndicator && (
              <Box
                onClick={handleScrollIndicatorClick}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 36,
                  background:
                    "linear-gradient(to bottom, rgba(66, 133, 244, 0), rgba(66, 133, 244, 0.15))",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: 6,
                }}
              >
                <Box
                  style={{
                    backgroundColor: "rgba(66, 133, 244, 0.2)",
                    borderRadius: 12,
                    padding: "2px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <IconChevronDown
                    size={14}
                    color="var(--mantine-color-blue-6)"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid.Col>

        {/* CENTER COLUMN - Scrollable with fixed bottom */}
        <Grid.Col span="auto" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Work List Match Banner */}
            {matchedExpectedCase && (
              <Paper
                p="sm"
                mx="md"
                mt="md"
                withBorder
                style={{
                  borderColor: "var(--mantine-color-teal-4)",
                  backgroundColor: "var(--mantine-color-teal-0)",
                }}
              >
                <Group gap="sm">
                  <IconCircleCheck
                    size={20}
                    color="var(--mantine-color-teal-6)"
                  />
                  <Text size="sm" fw={600} c="teal.8">
                    Work List Match Found
                  </Text>
                  <Badge variant="light" color="teal" size="sm">
                    {matchedExpectedCase.externalCaseNumber}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {matchedExpectedCase.patientLastName}
                    {matchedExpectedCase.patientFirstName
                      ? `, ${matchedExpectedCase.patientFirstName}`
                      : ""}{" "}
                    — {matchedExpectedCase.groupedSpecimens?.length || 0}{" "}
                    specimens from work list
                  </Text>
                </Group>
                {activeUnresolvedWorkListStains.length > 0 && (
                  <Text size="xs" c="red.7" mt="xs">
                    Resolve these stain requests before completing:{" "}
                    {activeUnresolvedWorkListStains
                      .map(
                        (stain) =>
                          `${stain.specimenNumber}: ${stain.requestedStain}`
                      )
                      .join(", ")}
                  </Text>
                )}
              </Paper>
            )}

            {/* Scrollable form area */}
            <Box style={{ flex: 1, overflowY: "auto" }} p="md">
              {isLimitedEdit && (
                <Alert
                  color="blue"
                  variant="light"
                  icon={<IconAlertTriangle size={18} />}
                  mb="md"
                >
                  <Text size="sm">
                    <strong>Limited Edit Mode</strong> — Only patient
                    information, insurance, rush/priority, and sales rep fields
                    can be updated. Specimens and documents are locked.
                  </Text>
                </Alert>
              )}
              <Accordion
                multiple
                value={openedSections}
                onChange={setOpenedSections}
                variant="separated"
              >
                {/* Patient Information */}
                <Accordion.Item value="patient" id="section-patient">
                  <Accordion.Control>
                    <Group justify="space-between" style={{ flex: 1 }} pr="md">
                      <Title order={4}>Patient Information</Title>
                      <Badge
                        color={
                          fieldCompletion.patient.requiredComplete
                            ? "green"
                            : "gray"
                        }
                        variant="light"
                      >
                        {fieldCompletion.patient.completed}/
                        {fieldCompletion.patient.total} fields
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <PatientInfoForm
                      form={form}
                      certainties={certainties}
                      onCertaintyChange={handleCertaintyChange}
                      missingFields={missingFields.patient}
                    />
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Primary Insurance */}
                <Accordion.Item
                  value="primaryInsurance"
                  id="section-primaryInsurance"
                >
                  <Accordion.Control>
                    <Group justify="space-between" style={{ flex: 1 }} pr="md">
                      <Title order={4}>Primary Insurance</Title>
                      <Badge
                        color={
                          fieldCompletion.primaryInsurance.recommendedComplete
                            ? "green"
                            : "yellow"
                        }
                        variant="light"
                      >
                        {fieldCompletion.primaryInsurance.recommendedComplete
                          ? `${fieldCompletion.primaryInsurance.completed}/${fieldCompletion.primaryInsurance.total} fields`
                          : "Recommended"}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <NoPatientBillingCheckbox
                        checked={noPatientBillingState.effective}
                        disabled={noPatientBillingState.forcedByPrefix}
                        onChange={handleNoPatientBillingChoiceChange}
                      />
                      <InsuranceForm
                        form={form}
                        type="primary"
                        certainties={certainties}
                        onCertaintyChange={handleCertaintyChange}
                        insuranceProviders={insuranceProviders.map((p) => ({
                          value: p.value,
                          label: p.label,
                        }))}
                        opened
                        onToggle={handleInsuranceToggle}
                        getProviderLabel={handleInsuranceProviderLabel}
                        missingFields={missingFields.insurance}
                        unmatchedExtractions={unmatchedExtractions}
                        onClearUnmatched={handleClearUnmatched}
                      />
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Secondary Insurance */}
                <Accordion.Item
                  value="secondaryInsurance"
                  id="section-secondaryInsurance"
                >
                  <Accordion.Control>
                    <Group justify="space-between" style={{ flex: 1 }} pr="md">
                      <Title order={4}>Secondary Insurance</Title>
                      <Badge
                        color={
                          fieldCompletion.secondaryInsurance.requiredComplete
                            ? "green"
                            : "gray"
                        }
                        variant="light"
                      >
                        {fieldCompletion.secondaryInsurance.completed}/
                        {fieldCompletion.secondaryInsurance.total} fields
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <InsuranceForm
                      form={form}
                      type="secondary"
                      certainties={certainties}
                      onCertaintyChange={handleCertaintyChange}
                      insuranceProviders={insuranceProviders.map((p) => ({
                        value: p.value,
                        label: p.label,
                      }))}
                      opened
                      onToggle={handleInsuranceToggle}
                      getProviderLabel={handleInsuranceProviderLabel}
                      unmatchedExtractions={unmatchedExtractions}
                      onClearUnmatched={handleClearUnmatched}
                    />
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Case Information */}
                <Accordion.Item value="caseInfo" id="section-caseInfo">
                  <Accordion.Control>
                    <Group justify="space-between" style={{ flex: 1 }} pr="md">
                      <Title order={4}>Case Information</Title>
                      <Badge
                        color={
                          fieldCompletion.caseInfo.requiredComplete
                            ? "green"
                            : "gray"
                        }
                        variant="light"
                      >
                        {fieldCompletion.caseInfo.completed}/
                        {fieldCompletion.caseInfo.total} fields
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <CaseInfoForm
                      form={form}
                      certainties={certainties}
                      onCertaintyChange={handleCertaintyChange}
                      onAutoCreatePracticePhysicianChange={
                        handleAutoCreatePracticePhysicianChange
                      }
                      icdCodes={icdCodes}
                      procedures={procedures}
                      missingFields={missingFields.case}
                      unmatchedExtractions={unmatchedExtractions}
                      onClearUnmatched={handleClearUnmatched}
                      onExternalCaseNumberChange={
                        handleExternalCaseNumberChange
                      }
                      onExternalCaseNumberBlur={handleExternalCaseNumberBlur}
                      externalCaseNumberStatus={canonicalExternalClaimState}
                      accessioningType={derivedCaseType}
                    />
                    {effectivePrefixData?.billingPending && (
                      <Alert
                        color="yellow"
                        variant="light"
                        icon={<IconAlertTriangle size={14} />}
                        mt="xs"
                        py="xs"
                      >
                        <Text size="xs">
                          Insurance not identified — billing will be confirmed
                          later
                        </Text>
                      </Alert>
                    )}
                    {effectivePrefixData?.billingPending && (
                      <Select
                        mt="sm"
                        label="Billing Scenario *"
                        placeholder="Select billing scenario"
                        data={sanitizeOptions(
                          allBillingScenarios.filter((s) => {
                            const name = s.label?.toLowerCase() ?? "";
                            return (
                              name === "global" ||
                              name.includes("tcpc") ||
                              name.includes("tc-pc") ||
                              name === "tc/pc split"
                            );
                          })
                        )}
                        value={manualBillingScenarioId}
                        onChange={handleManualBillingScenarioChange}
                      />
                    )}
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Specimens */}
                <Accordion.Item value="specimens" id="section-specimens">
                  <Accordion.Control>
                    <Group justify="space-between" style={{ flex: 1 }} pr="md">
                      <Title order={4}>Specimens</Title>
                      <Badge
                        color={
                          fieldCompletion.specimens.requiredComplete
                            ? "green"
                            : "gray"
                        }
                        variant="light"
                      >
                        {fieldCompletion.specimens.completed}/
                        {fieldCompletion.specimens.total} specimens
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {isLimitedEdit && allowGrossingIdentifierEdits && (
                      <GrossingSpecimenIdentifierEditor
                        form={form}
                        onIdentifierChange={handleSpecimenIdentifierChange}
                      />
                    )}
                    {/* Specimen content locks in limited-edit mode; header stays toggleable */}
                    <Box
                      style={
                        isLimitedEdit
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : undefined
                      }
                    >
                      <SpecimenList
                        form={form}
                        certainties={certainties}
                        onCertaintyChange={handleCertaintyChange}
                        sources={sourcesData?.sources || []}
                        subsites={sourcesData?.subsitesBySource || {}}
                        grossCodes={grossCodes}
                        collectionMethods={collectionMethods}
                        accessioningType={derivedCaseType}
                        isConsultation={isConsultation}
                        unmatchedExtractions={unmatchedExtractions}
                        onClearUnmatched={handleClearUnmatched}
                        onFindingsBlur={handleSpecimenFindingsBlur}
                        specimenMacros={specimenMacros}
                        batchTemplates={batchTemplatesData}
                        assignedPrefix={
                          prefixOverride ||
                          prefixLookupResult?.prefixCode ||
                          undefined
                        }
                        onSpecimensChange={handleSpecimensChange}
                        lockPersistedSpecimens={lockPersistedSpecimens}
                        allowGrossingIdentifierEdits={
                          allowGrossingIdentifierEdits
                        }
                        onSpecimenIdentifierChange={
                          handleSpecimenIdentifierChange
                        }
                      />
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Consultation — shown only when the consultation signal is on */}
                {isConsultation && (
                  <Accordion.Item
                    value="consultation"
                    id="section-consultation"
                  >
                    <Accordion.Control>
                      <Title order={4}>Consultation</Title>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <ConsultationForm
                        form={form}
                        certainties={certainties}
                        onCertaintyChange={handleCertaintyChange}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {/* Originating Facility — shown for externally-originated cases */}
                {(derivedCaseType === "pass_through" || isConsultation) && (
                  <Accordion.Item value="originating" id="section-originating">
                    <Accordion.Control>
                      <Title order={4}>Originating Facility</Title>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <OriginatingFacilityForm
                        form={form}
                        certainties={certainties}
                        onCertaintyChange={handleCertaintyChange}
                        unmatchedExtractions={unmatchedExtractions}
                        onClearUnmatched={handleClearUnmatched}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
              </Accordion>
            </Box>

            {/* FIXED bottom actions - always visible */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              {grossingIdentifierSave.error &&
                !grossingIdentifierSave.preview && (
                  <Alert
                    color="red"
                    title="Could not save specimen identifiers"
                    role="alert"
                    mb="sm"
                  >
                    {grossingIdentifierSave.error}
                  </Alert>
                )}
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={handleSaveAndGoBack}
                  loading={isSaving}
                  disabled={isCaseNavigationBlocked}
                >
                  Back
                </Button>
                <Group>
                  {!isLimitedEdit && (
                    <Button
                      variant="outline"
                      leftSection={<IconRefresh size={16} />}
                      onClick={handleNewCaseButtonClick}
                      disabled={isCaseNavigationBlocked}
                    >
                      New Case
                    </Button>
                  )}
                  {isLimitedEdit || allowGrossingIdentifierEdits ? (
                    <Button
                      leftSection={<IconCircleCheck size={16} />}
                      onClick={
                        allowGrossingIdentifierEdits
                          ? handleGrossingIdentifierSave
                          : handleSaveAndGoBack
                      }
                      loading={grossingIdentifierSave.isSaving}
                      disabled={isSaving || isCaseNavigationBlocked || !caseId}
                      color="blue"
                    >
                      Save Changes
                    </Button>
                  ) : (
                    <Button
                      leftSection={<IconCircleCheck size={16} />}
                      onClick={handleComplete}
                      disabled={
                        isSaving ||
                        isCaseNumberIdentityBlocked ||
                        completeMutation.isPending ||
                        !caseId
                      }
                      loading={completeMutation.isPending}
                      color="green"
                    >
                      Complete Accessioning
                    </Button>
                  )}
                </Group>
              </Group>
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT SIDEBAR - Fixed top button + scrollable content */}
        <Grid.Col
          span="content"
          style={{ width: 350, minWidth: 350, flexShrink: 0 }}
        >
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {/* Scrollable content area */}
            <Box style={{ flex: 1, overflowY: "auto" }}>
              <Stack gap="md" p="md">
                {/* Case Fields - Pathologist, Type, Billing, Rush, Priority */}
                <CaseFieldsCard
                  isCytology={isCytology}
                  isResearch={isResearch}
                  isConsultation={isConsultation}
                  isTcOnly={isTcOnly}
                  isGrossingOnly={isGrossingOnly}
                  onCytologyChange={handleIsCytologyChange}
                  onResearchChange={handleIsResearchChange}
                  onConsultationChange={handleIsConsultationChange}
                  onTcOnlyChange={handleIsTcOnlyChange}
                  onGrossingOnlyChange={handleIsGrossingOnlyChange}
                  prefixCode={prefixLookupResult?.prefixCode}
                  prefixColor={prefixLookupResult?.prefixColor}
                  isResolvingPrefix={isResolvingPrefix}
                  prefixOverride={prefixOverride}
                  onPrefixOverrideChange={handlePrefixOverrideChange}
                  availablePrefixes={prefixSelectOptions}
                  isPrefixReadOnly={
                    isEditMode && existingCase?.currentStatus?.name !== "Draft"
                  }
                  pathologists={sanitizeOptions(
                    pathologists.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))
                  )}
                  selectedPathologistId={form.values.case.pathologistId}
                  assignmentReason={assignmentReason}
                  onPathologistChange={handlePathologistChange}
                  isRush={form.values.case.isRush}
                  rushReason={form.values.case.rushReason || null}
                  onRushChange={handleRushChange}
                  onRushReasonChange={handleRushReasonChange}
                  rushCertainty={certainties["case.isRush"]}
                  onRushCertaintyChange={handleRushCertaintyChange}
                  isHighPriority={form.values.case.isHighPriority}
                  highPriorityReason={
                    form.values.case.highPriorityReason || null
                  }
                  onHighPriorityChange={handleHighPriorityChange}
                  onHighPriorityReasonChange={handleHighPriorityReasonChange}
                  highPriorityCertainty={certainties["case.isHighPriority"]}
                  onHighPriorityCertaintyChange={
                    handleHighPriorityCertaintyChange
                  }
                  highPriorityTriggers={highPriorityTriggers}
                  isThroughSalesRep={form.values.case.isThroughSalesRep}
                  salesRepId={form.values.case.salesRepId || null}
                  salesReps={salesRepOptions}
                  onThroughSalesRepChange={handleThroughSalesRepChange}
                  onSalesRepChange={handleSalesRepChange}
                  isLimitedEdit={isLimitedEdit}
                  prefixPossibleBillingScenarios={
                    effectivePrefixData?.allowedBillingScenarios?.length
                      ? effectivePrefixData.allowedBillingScenarios
                      : undefined
                  }
                  selectedBillingScenarioId={
                    prefixSelectedBillingScenarioId ||
                    form.values.case.billingScenarioId
                  }
                  onBillingScenarioChange={handleBillingScenarioChange}
                />

                {/* Blocks & Slides Preview (read-only in limited-edit mode) */}
                <Box
                  style={
                    isLimitedEdit
                      ? { opacity: 0.5, pointerEvents: "none" }
                      : undefined
                  }
                >
                  <BlocksAndStainsPreview
                    specimens={(form.values.case?.specimens || []).map(
                      (spec: any, idx: number) => {
                        const sourceSubsites =
                          sourcesData?.subsitesBySource?.[spec.sourceId] || [];
                        return {
                          number: spec.number,
                          sourceId: spec.sourceId,
                          subsiteId: spec.subsiteId || undefined,
                          blockCount: resolveAccessioningBlockCount(
                            derivedCaseType,
                            spec.blockCount
                          ),
                          sourceName: sourcesData?.sources?.find(
                            (s: any) => s.value === spec.sourceId
                          )?.label,
                          subsiteName: spec.subsiteId
                            ? sourceSubsites.find(
                                (ss: any) => ss.value === spec.subsiteId
                              )?.label
                            : undefined,
                          collectionMethod: spec.collectionMethod || undefined,
                          material: spec.material || undefined,
                          committedFindings: committedFindings[idx] ?? "",
                          matchedStainIds: spec.matchedStainIds || [],
                        };
                      }
                    )}
                    slideCompletions={effectiveSlideCompletions}
                    onCompletionChange={handleSlideCompletionChange}
                    onBlockCountChange={handleBlockCountChange}
                    caseId={caseId || undefined}
                    manualStains={manualStains}
                    onAddStain={handleAddStain}
                    onRemoveStain={handleRemoveStain}
                    hidePrintButton
                    accessioningType={derivedCaseType}
                    practiceId={form.values.case?.accountId || undefined}
                    billingScenarioId={
                      form.values.case?.billingScenarioId || undefined
                    }
                    onResolvedStainsChange={handleResolvedStainsChange}
                    assignedPrefix={
                      prefixOverride ||
                      prefixLookupResult?.prefixCode ||
                      undefined
                    }
                    workListStains={
                      matchedExpectedCase ? workListStainOverrides : undefined
                    }
                    onMarkAllReceivedCompleted={handleMarkAllReceivedCompleted}
                    onClearAllReceivedCompleted={
                      handleClearAllReceivedCompleted
                    }
                    stainSelectionContext={stainSelectionContext}
                    panelOptions={panelOptions}
                    panelSelections={panelSelections}
                    panelsLoading={panelsLoading}
                    onAddPanelSelection={handleAddPanelSelection}
                    onRemovePanelSelection={handleRemovePanelSelection}
                  />
                </Box>

                {/* Case Flags */}
                <CaseFlagsSection caseId={caseId} />

                {/* Case Notes */}
                <CaseNotesSection caseId={caseId} />

                {/* Case Comments */}
                <CaseCommentsSection caseId={caseId} />

                {/* Errors */}
                <ErrorsCard
                  caseId={caseId}
                  suggestedErrorTypes={missingFields.suggestedErrorTypes}
                />
              </Stack>
            </Box>

            {/* Fixed bottom - Print Requisition Label + existing print buttons */}
            {caseId && (
              <AccessioningPrintActions
                canPrintSticker={canPrintSticker}
                cassetteCount={cassetteLabelCount}
                completedStickerCount={stickerCount}
                isGeneratingCassettes={generateLabelsMutation.isPending}
                isPrintingRequisitionLabel={isPrintingRequisitionLabel}
                isPrintingStickers={isPrintingStickers}
                isCaseNumberAssignmentPending={isCaseNumberIdentityBlocked}
                onPrintCassettes={handlePrintCassettes}
                onPrintRequisitionLabel={handlePrintRequisitionLabel}
                onPrintStickers={handlePrintStickers}
                printLabelCount={printLabelCount}
                showPrintCassettes={!isConsultation && hasSpecimensWithSource}
                showPrintLabels={!isConsultation && stickerCount > 0}
                totalSlideCount={totalSlideCount}
              />
            )}
          </Box>
        </Grid.Col>
      </Grid>

      {/* New Case Modal */}
      <NewCaseModal
        opened={newCaseModalOpened}
        onClose={handleNewCaseModalClose}
        onDiscard={handleNewCaseModalDiscard}
        onSaveAndNew={handleNewCaseModalSaveAndNew}
        hasUnsavedChanges={handleHasUnsavedChanges()}
        isSaving={isSaving}
      />

      <SpecimenIdentifierWarningModal
        preview={grossingIdentifierSave.preview}
        isSaving={grossingIdentifierSave.isSaving}
        error={grossingIdentifierSave.error}
        onCancel={grossingIdentifierSave.handleCancel}
        onConfirm={grossingIdentifierSave.handleConfirm}
      />
      <PrefixOverrideWarningModal
        opened={prefixOverrideWarningModalOpen}
        selectedPrefix={prefixOverrideWarningDetails.selectedPrefix}
        computedPrefix={prefixOverrideWarningDetails.computedPrefix}
        isSaving={isSaving}
        confirmLabel={prefixOverrideConfirmLabel}
        onClose={handleClosePrefixOverrideWarning}
        onProceedWithOverride={handleProceedWithPrefixOverride}
        onReviewPrefix={handleReviewPrefixOverride}
      />

      <PassthroughPrefixWorkflowWarningModal
        opened={passthroughMismatchWarningModalOpen}
        selectedPrefix={passthroughMismatchWarningDetails.selectedPrefix}
        workflowLabel={passthroughMismatchWarningDetails.workflowLabel}
        isCompleting={isSaving}
        onClose={handleClosePassthroughMismatchWarning}
        onCompleteAnyway={handleCompletePassthroughMismatchAnyway}
        onReview={handleReviewPassthroughMismatchWarning}
      />

      <CompletionWarningModal
        opened={completionWarningModalOpen}
        warnings={completionWarnings}
        caseTypeLabel={caseTypeLabel(derivedCaseType)}
        isCompleting={isSaving}
        onClose={handleCloseCompletionWarning}
        onCompleteAnyway={handleCompleteAnyway}
        onEnterDetails={handleEnterCompletionDetails}
      />

      {editingCaseId && currentCaseNumber && (
        <CaseNumberCorrectionModal
          opened={caseNumberModalOpened}
          onClose={closeCaseNumberModal}
          caseId={editingCaseId}
          currentCaseNumber={currentCaseNumber}
          onSuccess={handleCaseNumberCorrected}
        />
      )}

      {/* Navigate-away discard prompt for empty draft cases */}
      <Modal
        opened={discardPromptPending}
        onClose={handleCancelLeave}
        title="Discard this case?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Case <strong>{displayCaseNumber}</strong> has no patient or specimen
            information. Would you like to discard it, or keep it to resume
            later?
          </Text>
          <Text size="xs" c="dimmed">
            Kept cases appear in the Missed Cases tab on the Cases page.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleCancelLeave}>
              Stay Here
            </Button>
            <Button variant="light" onClick={handleKeepAndLeave}>
              Keep as Missed Case
            </Button>
            <Button color="red" onClick={handleDiscardAndLeave}>
              Discard Case
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Unsaved changes prompt */}
      <Modal
        opened={unsavedChangesPromptPending}
        onClose={handleCancelLeave}
        title="Unsaved Changes"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            You have unsaved changes for case{" "}
            <strong>{displayCaseNumber}</strong>. Would you like to save them
            before leaving?
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleCancelLeave}>
              Stay Here
            </Button>
            <Button color="red" variant="subtle" onClick={handleKeepAndLeave}>
              Discard and Leave
            </Button>
            <Button
              color="blue"
              onClick={handleSaveAndLeave}
              loading={isSaving}
            >
              Save and Leave
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
